// /opt/projects/saas_odonto/frontend/src/pages/CRM_v3.tsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import ContactAvatar from '../components/ContactAvatar.tsx';
import { useLocation, useNavigate } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import clsx from 'clsx';
import { CrmActionsDropdown as DropdownMenu } from '../components/crm/CrmActionsDropdown.tsx';
import {
  Calendar,
  Clock,
  Phone,
  User,
  X,
  CalendarDays,
  Target,
  Search,
  Info,
  DollarSign,
  MoreVertical,
  MessageCircle,
  PlusCircle,
  Filter,
  FileText,
  CheckCircle2,
  XCircle,
  UserX,
  RefreshCw,
  Download,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Hash,
  HeartHandshake,
  Loader2,
  LayoutGrid,
  List
} from 'lucide-react';

import {
  listarCrmInicial,
  listarCrmPagina,
  criarComparecimento,
  obterComparecimento,
  atualizarAgendamento,
  criarVenda,
  atualizarComparecimento,
  deletarComparecimento,
  atualizarVenda,
  deletarVenda,
  marcarNoShowAgendamento,
  listarNoShows,
  atualizarNoShowEvent,
  deletarNoShowEvent,
  criarLead,
  exportLeadsAsCsv,
  exportLeadsToExcel,
  atualizarLead,
  criarAgendamento,
  deletarAgendamento,
  deletarLead,
  getContacts,
  getAgentTreatmentTitles,
  formatBrazilianPhone,
  formatBrazilianPhoneInput,
  parseApiError,
  validateBrazilianWhatsAppPhone,
  type CrmListParams,
  type ParsedApiError,
  // Tipos
  AgendamentoResponse,
  AgendamentoUpdate,
  AgendamentoCreate,
  Lead,
  Comparecimento,
  ComparecimentoCreate,
  ComparecimentoUpdate,
  Venda,
  VendaCreate,
  VendaUpdate,
  NoShowUpdatePayload
} from '../services/api.ts';
import { CrmLossProvider, useCrmLossActions, CRM_STAGE_LABELS } from '../components/crm/CrmLosses.tsx';
import { CrmPhoneCorrectionProvider, CrmSaleErrorDialog, useCrmPhoneCorrection } from '../components/crm/CrmPhoneCorrection.tsx';
import { getCrmPhoneLeadId, getCrmSaleFailure, getPhoneCorrectionFeedback, reconcileCrmPhone, type CrmSaleFailure } from '../components/crm/crmPhoneFeedback.ts';
import { type CrmLoss, type CrmStage } from '../services/api.ts';
import { TagsService, type LeadTagsSummary, type TagResponse } from '../services/apiTags.ts';
import CalendarFilter, { DateRange } from '../components/CalendarFilter.tsx';
import CalendarFilterDropdown from '../components/CalendarFilterDropdown.tsx';
import useCalendarFilter, { PresetFilterType } from '../hooks/useCalendarFilter.ts';
import { useTheme } from '../contexts/ThemeContext.tsx';
import { format, startOfDay, endOfDay } from 'date-fns';
import { CRM_SOURCE_OPTIONS } from '../constants/sourceOptions.ts';

// Import para componentes de tags
import TagSelector from '../components/TagSelector.tsx';
import WhatsAppIdentityErrorAlert from '../components/WhatsAppIdentityErrorAlert.tsx';

const CRM_VISIBLE_BATCH_SIZE = 60;
const CRM_TAGS_BATCH_SIZE = 250;
const CRM_CURRENT_STAGE_ONLY = true;
const CRM_VIEW_MODE_STORAGE_KEY = 'sminy.crm.viewMode';

const isProtectedCrmMediaSource = (sourceId?: string | null): boolean => {
  const normalized = sourceId?.trim();
  return Boolean(normalized && /^\d{10,}$/.test(normalized));
};

const CRM_TAB_KEYS = ['leads', 'appointments', 'noShows', 'attendances', 'sales', 'lost'] as const;
type CrmTabKey = typeof CRM_TAB_KEYS[number];
type CrmViewMode = 'cards' | 'list';
type PatientFilterValue = 'all' | 'yes' | 'no';

const DEFAULT_VISIBLE_COUNTS: Record<CrmTabKey, number> = {
  leads: CRM_VISIBLE_BATCH_SIZE,
  appointments: CRM_VISIBLE_BATCH_SIZE,
  noShows: CRM_VISIBLE_BATCH_SIZE,
  attendances: CRM_VISIBLE_BATCH_SIZE,
  sales: CRM_VISIBLE_BATCH_SIZE,
  lost: CRM_VISIBLE_BATCH_SIZE,
};

const DEFAULT_CRM_TOTALS: Record<CrmTabKey, number> = {
  leads: 0,
  appointments: 0,
  noShows: 0,
  attendances: 0,
  sales: 0,
  lost: 0,
};

const DEFAULT_EXHAUSTED_TABS: Record<CrmTabKey, boolean> = {
  leads: false,
  appointments: false,
  noShows: false,
  attendances: false,
  sales: false,
  lost: false,
};

const formatCrmDateParam = (date: Date | null): string | undefined => (
  date ? format(date, 'yyyy-MM-dd') : undefined
);

function mergeById<T extends { id: number }>(currentItems: T[], nextItems: T[]): T[] {
  const existingIds = new Set(currentItems.map((item) => item.id));
  const uniqueNextItems = nextItems.filter((item) => !existingIds.has(item.id));
  return [...currentItems, ...uniqueNextItems];
}

export function appendSaleTransaction(currentSales: Venda[], newSale: Venda): Venda[] {
  return mergeById(currentSales, [newSale]);
}

function countNewItemsById<T extends { id: number }>(currentItems: T[], nextItems: T[]): number {
  const existingIds = new Set(currentItems.map((item) => item.id));
  return nextItems.filter((item) => !existingIds.has(item.id)).length;
}

const CrmContactAvatar: React.FC<{
  name?: string | null;
  phone?: string | null;
  photo?: string | null;
  size?: 'sm' | 'md';
}> = ({ name, phone, photo, size = 'md' }) => (
  <ContactAvatar
    photo={photo}
    name={name || phone || undefined}
    className={`${size === 'sm' ? 'h-9 w-9' : 'h-11 w-11'} rounded-full shadow-sm`}
    iconClassName="h-5 w-5"
  />
);

// =========================
//      HELPER COMPONENTS
// =========================

// ---------- Tags Display Component ----------
interface TagsDisplayProps {
  tags?: LeadTagsSummary;
  compact?: boolean;
}

const TagsDisplay: React.FC<TagsDisplayProps> = ({ tags, compact = false }) => {
  if (!tags || tags.total_tags === 0) return null;

  // Função para calcular cor de contraste
  const getContrastColor = (hexColor: string): string => {
    const color = hexColor.replace('#', '');
    const r = parseInt(color.substring(0, 2), 16);
    const g = parseInt(color.substring(2, 4), 16);
    const b = parseInt(color.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  };

  // Preparar tags para exibição (priorizar tags manuais, depois herdadas)
  const getVisibleTags = () => {
    if (!tags || tags.total_tags === 0) return [];

    // Combinar tags manuais e herdadas, dando prioridade às manuais
    const allTags = [
      ...(tags.manual_tags || []),
      ...(tags.inherited_tags || [])
    ];

    // Remover duplicatas e limitar a 4 tags
    const uniqueTags = allTags.filter((tag, index, self) =>
      index === self.findIndex(t => t.id === tag.id)
    );

    return uniqueTags.slice(0, 4);
  };

  const visibleTags = getVisibleTags();

  return (
    <div className={clsx("flex min-w-0 flex-wrap gap-1", compact ? "mb-0 max-w-full overflow-hidden" : "mb-2")}>
      {visibleTags.map(tag => (
        <div
          key={tag.id}
          className={clsx(
            "inline-flex max-w-full min-w-0 items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium",
            compact && "max-w-[8rem]"
          )}
          style={{
            backgroundColor: tag.color,
            color: getContrastColor(tag.color)
          }}
          title={`${tag.name} - ${tag.category}${tag.description ? ': ' + tag.description : ''}`}
        >
          <span className="min-w-0 truncate">{tag.name}</span>
        </div>
      ))}
      {tags.total_tags > 4 && (
        <span className="inline-flex shrink-0 items-center px-2 py-0.5 rounded-full text-xs text-gray-500 bg-gray-100">
          +{tags.total_tags - 4}
        </span>
      )}
    </div>
  );
};

export const PatientStatusBadge: React.FC<{
  isPatient?: boolean;
  compact?: boolean;
}> = ({ isPatient, compact = false }) => {
  const { isDark } = useTheme();

  if (!isPatient) return null;

  return (
    <span
      className={clsx(
        "inline-flex max-w-full shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
        isDark
          ? "bg-purple-900/50 text-purple-300"
          : "bg-purple-100 text-purple-700",
        compact && "px-1.5 text-[11px]"
      )}
      aria-label="Já é paciente"
      title="Contato já cadastrado como paciente"
    >
      <HeartHandshake className="h-3 w-3 shrink-0" />
      <span className="truncate">Já é paciente</span>
    </span>
  );
};

export const getSelectedTagsFilterLabel = (
  tags: TagResponse[],
  selectedTagIds: number[],
): string => {
  if (selectedTagIds.length === 0) return 'Todas as tags';

  if (selectedTagIds.length === 1) {
    return tags.find((tag) => tag.id === selectedTagIds[0])?.name || '1 tag selecionada';
  }

  return `${selectedTagIds.length} tags selecionadas`;
};

interface CrmAdditionalFiltersPopoverProps {
  availableTags: TagResponse[];
  isTagsLoading: boolean;
  tagsError: string | null;
  selectedTagIds: number[];
  patientFilter: PatientFilterValue;
  onTagIdsChange: (tagIds: number[]) => void;
  onPatientFilterChange: (value: PatientFilterValue) => void;
  onClear: () => void;
  onClose: () => void;
  onRetryTags: () => void;
}

export const CrmAdditionalFiltersPopover: React.FC<CrmAdditionalFiltersPopoverProps> = ({
  availableTags,
  isTagsLoading,
  tagsError,
  selectedTagIds,
  patientFilter,
  onTagIdsChange,
  onPatientFilterChange,
  onClear,
  onClose,
  onRetryTags,
}) => {
  const { isDark } = useTheme();
  const [showTagOptions, setShowTagOptions] = useState(false);
  const selectedTagsLabel = getSelectedTagsFilterLabel(availableTags, selectedTagIds);
  const hasActiveFilters = selectedTagIds.length > 0 || patientFilter !== 'all';

  const toggleTag = (tagId: number) => {
    onTagIdsChange(
      selectedTagIds.includes(tagId)
        ? selectedTagIds.filter((selectedId) => selectedId !== tagId)
        : [...selectedTagIds, tagId],
    );
  };

  const inputClassName = clsx(
    "flex h-10 w-full items-center justify-between gap-3 rounded-xl border px-3 text-left text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-sminy-primary/30",
    isDark
      ? "border-gray-600 bg-gray-700 text-gray-200 hover:border-gray-500"
      : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
  );

  return (
    <div
      id="crm-additional-filters"
      role="dialog"
      aria-label="Filtros do CRM"
      className={clsx(
        "absolute left-0 top-full z-[80] mt-2 w-[min(22rem,calc(100vw-2rem))] rounded-2xl border p-4 shadow-xl sm:left-auto sm:right-0",
        isDark
          ? "border-gray-700 bg-gray-800"
          : "border-gray-200 bg-white"
      )}
    >
      <div className="mb-4 flex items-center justify-between">
        <h3 className={clsx(
          "text-sm font-semibold",
          isDark ? "text-gray-100" : "text-gray-800"
        )}>
          Filtros
        </h3>
        <button
          type="button"
          onClick={onClose}
          className={clsx(
            "rounded-lg p-1.5 transition-colors",
            isDark
              ? "text-gray-400 hover:bg-gray-700 hover:text-gray-200"
              : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          )}
          aria-label="Fechar filtros"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <label
            htmlFor="crm-patient-filter"
            className={clsx(
              "mb-1.5 block text-xs font-medium",
              isDark ? "text-gray-300" : "text-gray-600"
            )}
          >
            Já é paciente?
          </label>
          <select
            id="crm-patient-filter"
            className={clsx(
              "h-10 w-full rounded-xl border px-3 text-sm transition-colors focus:border-sminy-primary focus:outline-none focus:ring-2 focus:ring-sminy-primary/30",
              isDark
                ? "border-gray-600 bg-gray-700 text-gray-200"
                : "border-gray-200 bg-white text-gray-700"
            )}
            value={patientFilter}
            onChange={(event) => onPatientFilterChange(event.target.value as PatientFilterValue)}
          >
            <option value="all">Todos</option>
            <option value="yes">Já é paciente</option>
            <option value="no">Não é paciente</option>
          </select>
        </div>

        <div>
          <label
            className={clsx(
              "mb-1.5 block text-xs font-medium",
              isDark ? "text-gray-300" : "text-gray-600"
            )}
          >
            Tags
          </label>
          <button
            type="button"
            className={inputClassName}
            onClick={() => setShowTagOptions((current) => !current)}
            aria-expanded={showTagOptions}
            aria-controls="crm-tag-filter-options"
            aria-label={`Tags: ${selectedTagsLabel}`}
          >
            <span className="min-w-0 truncate">{selectedTagsLabel}</span>
            <ChevronDown
              className={clsx(
                "h-4 w-4 shrink-0 text-gray-400 transition-transform",
                showTagOptions && "rotate-180"
              )}
            />
          </button>

          {showTagOptions && (
            <div
              id="crm-tag-filter-options"
              className={clsx(
                "mt-2 max-h-52 overflow-y-auto rounded-xl border p-1.5",
                isDark
                  ? "border-gray-600 bg-gray-900/40"
                  : "border-gray-200 bg-gray-50"
              )}
            >
              {isTagsLoading ? (
                <div className={clsx(
                  "flex items-center justify-center gap-2 px-3 py-5 text-xs",
                  isDark ? "text-gray-400" : "text-gray-500"
                )}>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Carregando tags...
                </div>
              ) : tagsError ? (
                <div className="px-3 py-4 text-center">
                  <p className={clsx(
                    "text-xs",
                    isDark ? "text-rose-300" : "text-rose-600"
                  )}>
                    {tagsError}
                  </p>
                  <button
                    type="button"
                    className="mt-2 text-xs font-medium text-sminy-primary hover:underline"
                    onClick={onRetryTags}
                  >
                    Tentar novamente
                  </button>
                </div>
              ) : availableTags.length === 0 ? (
                <div className={clsx(
                  "px-3 py-5 text-center text-xs",
                  isDark ? "text-gray-500" : "text-gray-500"
                )}>
                  Nenhuma tag disponível
                </div>
              ) : (
                availableTags.map((tag) => {
                  const isSelected = selectedTagIds.includes(tag.id);

                  return (
                    <button
                      key={tag.id}
                      type="button"
                      role="checkbox"
                      aria-checked={isSelected}
                      onClick={() => toggleTag(tag.id)}
                      className={clsx(
                        "flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm transition-colors",
                        isDark
                          ? "text-gray-200 hover:bg-gray-700"
                          : "text-gray-700 hover:bg-white",
                        isSelected && (
                          isDark
                            ? "bg-sminy-primary/15"
                            : "bg-sminy-primary/10"
                        )
                      )}
                    >
                      <span
                        className={clsx(
                          "grid h-4 w-4 shrink-0 place-items-center rounded border",
                          isSelected
                            ? "border-sminy-primary bg-sminy-primary text-white"
                            : isDark
                              ? "border-gray-500"
                              : "border-gray-300"
                        )}
                      >
                        {isSelected && <Check className="h-3 w-3" />}
                      </span>
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: tag.color }}
                      />
                      <span className="min-w-0 truncate">{tag.name}</span>
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>

      <div className={clsx(
        "mt-4 border-t pt-3",
        isDark ? "border-gray-700" : "border-gray-100"
      )}>
        <button
          type="button"
          onClick={onClear}
          disabled={!hasActiveFilters}
          className={clsx(
            "text-xs font-medium transition-colors",
            hasActiveFilters
              ? "text-sminy-primary hover:underline"
              : "cursor-not-allowed text-gray-400"
          )}
        >
          Limpar filtros
        </button>
      </div>
    </div>
  );
};

// ---------- Tags Modal Component ----------
interface TagsModalProps {
  isOpen: boolean;
  onClose: () => void;
  leadId: number;
  leadName?: string;
  currentTags: number[];
  onTagsUpdated: () => void;
}

const TagsModal: React.FC<TagsModalProps> = ({
  isOpen,
  onClose,
  leadId,
  leadName,
  currentTags,
  onTagsUpdated
}) => {
  const [selectedTags, setSelectedTags] = useState<number[]>(currentTags);
  const [isLoading, setIsLoading] = useState(false);

  // Otimização: sincronizar tags quando a prop currentTags mudar
  useEffect(() => {
    console.log(`🏷️ TagsModal: Sincronizando tags - Current: [${currentTags.join(', ')}] -> Selected: [${selectedTags.join(', ')}]`);
    setSelectedTags(currentTags);
  }, [currentTags]);

  // Otimização: memoizar handler para evitar re-renders desnecessários
  const handleSave = useCallback(async () => {
    setIsLoading(true);
    try {
      console.log(`🏷️ TagsModal: Atualizando tags do lead ${leadId} - Tags: [${selectedTags.join(', ')}]`);
      await TagsService.updateLeadTags(leadId, selectedTags);
      console.log(`✅ TagsModal: Tags atualizadas com sucesso`);
      onTagsUpdated();
      onClose();
    } catch (error) {
      console.error('❌ Erro ao atualizar tags:', error);
    } finally {
      setIsLoading(false);
    }
  }, [leadId, selectedTags, onTagsUpdated, onClose]);

  const handleTagsChange = (tagIds: number[]) => {
    console.log(`🏷️ TagsModal: Tags alteradas - Novas: [${tagIds.join(', ')}]`);
    setSelectedTags(tagIds);
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Gerenciar Tags">
      <div className="space-y-4">
        {/* Informações do lead */}
        <div className="text-sm text-gray-600">
          {leadName ? `Gerenciando tags de: ${leadName}` : `Gerenciando tags do Lead #${leadId}`}
        </div>

        {/* Tag Selector */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Selecione as tags
          </label>
          <TagSelector
            selectedTags={selectedTags}
            onTagsChange={handleTagsChange}
            clinicId={parseInt(localStorage.getItem('clinic_id') || '0', 10)}
            allowCreate={true}
            maxTags={10}
            variant="inline"
          />
        </div>

        {/* Estatísticas */}
        <div className="text-xs text-gray-500">
          {selectedTags.length} tag(s) selecionada(s)
        </div>

        {/* Botões de ação */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoading}
          >
            {isLoading ? 'Salvando...' : 'Confirmar'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// ---------- Button -----------
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  icon?: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  icon,
  className,
  children,
  ...props
}) => {
  const { isDark } = useTheme();
  const baseStyles = "inline-flex items-center justify-center rounded-xl font-medium transition-all focus:outline-none focus:ring-2 focus:ring-sminy-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60";

  const variantStyles = {
    primary: "bg-sminy-primary text-white hover:bg-sminy-primary/90 shadow-sm",
    secondary: isDark
      ? "bg-gray-800 text-gray-200 border border-gray-700 hover:bg-gray-700 focus:ring-offset-[#111827]"
      : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50",
    success: "bg-emerald-600 text-white hover:bg-emerald-700 focus:ring-emerald-500",
    danger: "bg-rose-600 text-white hover:bg-rose-700 focus:ring-rose-500",
    ghost: isDark
      ? "bg-transparent text-gray-300 hover:bg-gray-700/60 hover:text-white focus:ring-offset-[#111827]"
      : "bg-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900"
  };

  const sizeStyles = {
    sm: "text-xs px-2.5 py-1.5 space-x-1.5",
    md: "text-sm px-4 py-2 space-x-2",
    lg: "text-base px-5 py-2.5 space-x-2.5"
  };

  return (
    <button
      className={clsx(
        baseStyles,
        variantStyles[variant],
        sizeStyles[size],
        className
      )}
      {...props}
    >
      {icon && <span className={children ? "mr-2" : ""}>{icon}</span>}
      {children}
    </button>
  );
};

// ---------- Badge -----------
interface BadgeProps {
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'danger';
  children: React.ReactNode;
  className?: string;
}

const Badge: React.FC<BadgeProps> = ({
  variant = 'default',
  children,
  className
}) => {
  const { isDark } = useTheme();
  const variantStyles = {
    default: isDark ? "bg-gray-700 text-gray-300" : "bg-slate-100 text-slate-700",
    primary: "bg-sminy-primary/10 text-sminy-primary",
    success: isDark ? "bg-emerald-900/40 text-emerald-300" : "bg-emerald-100 text-emerald-700",
    warning: isDark ? "bg-amber-900/40 text-amber-300" : "bg-amber-100 text-amber-700",
    danger: isDark ? "bg-rose-900/40 text-rose-300" : "bg-rose-100 text-rose-700"
  };

  return (
    <span className={clsx(
      `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium`,
      variantStyles[variant],
      className
    )}>
      {children}
    </span>
  );
};

// ---------- Card -----------
interface CardProps {
  children: React.ReactNode;
  className?: string;
}

const Card: React.FC<CardProps> = ({ children, className = "" }) => {
  const { isDark } = useTheme();

  return (
    <div className={clsx(
      "rounded-2xl border p-3 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md sm:p-4 min-h-[280px] w-full relative",
      isDark ? "border-gray-700 bg-gray-800" : "border-slate-200 bg-white",
      isDark && "[&_.text-gray-900]:!text-gray-100 [&_.text-gray-800]:!text-gray-100 [&_.text-gray-700]:!text-gray-300 [&_.text-gray-600]:!text-gray-400 [&_.text-gray-500]:!text-gray-400 [&_.border-gray-100]:!border-gray-700",
      className
    )}>
      {children}
    </div>
  );
};

// ---------- Modal -----------
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children }) => {
  const { isDark } = useTheme();

  if (!isOpen) return null;

  // Renderizado via portal no body: ancestrais com transform (ex.: hover:-translate-y-0.5
  // do Card) viram containing block de descendentes `fixed`, prendendo o modal dentro do card.
  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={clsx(
        "rounded-2xl shadow-xl max-w-md w-full mx-auto max-h-[90vh] overflow-y-auto",
        isDark ? "bg-gray-800" : "bg-white",
        isDark && "[&_.text-gray-900]:!text-gray-100 [&_.text-gray-800]:!text-gray-100 [&_.text-gray-700]:!text-gray-300 [&_.text-gray-600]:!text-gray-400 [&_.text-gray-500]:!text-gray-400 [&_label]:!text-gray-300 [&_input]:!border-gray-600 [&_input]:!bg-gray-700 [&_input]:!text-gray-100 [&_select]:!border-gray-600 [&_select]:!bg-gray-700 [&_select]:!text-gray-100 [&_textarea]:!border-gray-600 [&_textarea]:!bg-gray-700 [&_textarea]:!text-gray-100"
      )}>
        <div className={clsx(
          "p-4 border-b flex justify-between items-center sticky top-0 rounded-t-2xl",
          isDark ? "border-gray-700 bg-gray-800" : "border-slate-200 bg-white"
        )}>
          <h3 className={clsx("text-lg font-semibold", isDark ? "text-white" : "text-slate-900")}>{title}</h3>
          <button
            onClick={onClose}
            className={clsx(
              "p-1.5 rounded-xl transition-colors",
              isDark ? "text-gray-400 hover:bg-gray-700 hover:text-gray-200" : "text-slate-500 hover:bg-slate-100 hover:text-slate-700"
            )}
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

const TREATMENT_EMPTY_MESSAGE = 'Nenhum tratamento cadastrado na lista da clínica.';

const buildTreatmentSelectOptions = (
  configuredOptions: string[],
  currentValue?: string
): string[] => {
  const seen = new Set<string>();
  const options: string[] = [];

  const addOption = (value?: string) => {
    const normalizedValue = String(value || '').trim();
    if (!normalizedValue) return;

    const dedupeKey = normalizedValue.toLocaleLowerCase('pt-BR');
    if (seen.has(dedupeKey)) return;

    seen.add(dedupeKey);
    options.push(normalizedValue);
  };

  addOption(currentValue);
  configuredOptions.forEach(addOption);

  return options;
};

interface TreatmentSelectFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  treatmentOptions: string[];
  isLoadingTreatmentOptions: boolean;
  placeholder?: string;
  loadingPlaceholder?: string;
  noOptionsMessage?: string;
}

const TreatmentSelectField: React.FC<TreatmentSelectFieldProps> = ({
  label,
  value,
  onChange,
  treatmentOptions,
  isLoadingTreatmentOptions,
  placeholder = 'Selecione um tratamento',
  loadingPlaceholder = 'Carregando tratamentos...',
  noOptionsMessage = TREATMENT_EMPTY_MESSAGE
}) => {
  const selectedValue = String(value || '').trim();
  const options = buildTreatmentSelectOptions(treatmentOptions, selectedValue);
  const isDisabled = isLoadingTreatmentOptions || options.length === 0;

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <select
        className="w-full p-2 border border-gray-300 rounded-xl focus:ring-sminy-primary focus:border-sminy-primary"
        value={selectedValue}
        disabled={isDisabled}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">
          {isLoadingTreatmentOptions ? loadingPlaceholder : placeholder}
        </option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      {!isLoadingTreatmentOptions && options.length === 0 && (
        <p className="mt-1 text-xs text-amber-600">{noOptionsMessage}</p>
      )}
    </div>
  );
};

// ========== Dropdown Menu ==========
interface DropdownMenuItemProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  icon?: React.ReactNode;
  variant?: 'default' | 'danger';
}

const DropdownMenuItem: React.FC<DropdownMenuItemProps> = ({
  children,
  className,
  icon,
  variant = 'default',
  ...props
}) => {
  const { isDark } = useTheme();
  const variantStyles = {
    default: isDark ? "text-gray-300 hover:bg-gray-700" : "text-slate-700 hover:bg-slate-100",
    danger: isDark ? "text-rose-300 hover:bg-rose-900/30" : "text-rose-600 hover:bg-rose-50"
  };

  return (
    <button
      className={clsx(
        'w-full text-left px-4 py-2 text-sm flex items-center',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent',
        variantStyles[variant],
        className
      )}
      {...props}
    >
      {icon && <span className="mr-2">{icon}</span>}
      {children}
    </button>
  );
};

interface CrmViewModeToggleProps {
  value: CrmViewMode;
  onChange: (mode: CrmViewMode) => void;
}

const CrmViewModeToggle: React.FC<CrmViewModeToggleProps> = ({ value, onChange }) => {
  const { isDark } = useTheme();
  const options: Array<{ value: CrmViewMode; label: string; icon: LucideIcon }> = [
    { value: 'cards', label: 'Cards', icon: LayoutGrid },
    { value: 'list', label: 'Lista', icon: List },
  ];

  return (
    <div
      className={clsx(
        "inline-flex w-full sm:w-auto rounded-xl border p-1 shadow-sm",
        isDark ? "border-gray-700 bg-gray-800" : "border-slate-200 bg-white"
      )}
      role="group"
      aria-label="Modo de visualização do CRM"
    >
      {options.map((option) => {
        const Icon = option.icon;
        const isActive = value === option.value;

        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={clsx(
              'inline-flex min-h-[36px] flex-1 items-center justify-center gap-2 rounded-md px-3 text-sm font-medium transition-colors sm:flex-none',
              isActive
                ? 'bg-sminy-primary text-white shadow-sm'
                : isDark
                  ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            )}
            aria-pressed={isActive}
          >
            <Icon className="h-4 w-4" />
            <span>{option.label}</span>
          </button>
        );
      })}
    </div>
  );
};

// ---------- Input -----------
interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  icon?: React.ReactNode;
  error?: string;
}

const Input: React.FC<InputProps> = ({
  label,
  icon,
  error,
  className,
  ...props
}) => {
  const { isDark } = useTheme();

  return (
    <div className="space-y-1">
      {label && (
        <label className={clsx("block text-sm font-medium", isDark ? "text-gray-300" : "text-slate-700")}>{label}</label>
      )}
      <div className="relative">
        {icon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
            {icon}
          </div>
        )}
        <input
          className={clsx(
            "block w-full rounded-xl border px-3 py-2 text-sm shadow-sm transition-colors focus:border-sminy-primary focus:ring-2 focus:ring-sminy-primary",
            isDark ? "border-gray-600 bg-gray-700 text-gray-100 placeholder:text-gray-400" : "border-slate-300 bg-white text-slate-900 placeholder:text-slate-400",
            icon ? "pl-10" : "pl-3",
            error ? "border-rose-500" : "",
            className
          )}
          {...props}
        />
      </div>
      {error && <p className="text-sm text-rose-600">{error}</p>}
    </div>
  );
};

// Verifica se um lead já tem agendamento
const hasAppointment = (lead: Lead, appointments: AgendamentoResponse[]): boolean => {
  return appointments.some(appointment => appointment.lead_id === lead.id);
};

// Verifica se um agendamento tem comparecimento
const hasAttendance = (appointment: AgendamentoResponse, attendances: Comparecimento[]): boolean => {
  return attendances.some(attendance => attendance.agendamento_id === appointment.id);
};

// Verifica se um agendamento tem falta registrada
const hasNoShow = (appointment: AgendamentoResponse, noShows: NoShowEvent[]): boolean => {
  return noShows.some(noShow => noShow.agendamento_id === appointment.id);
};

// Verifica se um comparecimento resultou em venda
const hasSale = (attendance: Comparecimento, sales: Venda[]): boolean => {
  return sales.some(sale => sale.comparecimento_id === attendance.id);
};

// 2. Agora, vamos criar um componente para a tag de status
// Versão corrigida do componente StatusTag para resolver os erros de TypeScript
// O problema é que o TypeScript espera que o switch retorne null em alguns casos,
// mas estamos retornando elementos JSX em todos os casos

const SimpleStatusTag: React.FC<{
  status: 'agendado' | 'compareceu' | 'faltou' | 'venda' | 'pendente';
  className?: string
}> = ({ status, className }) => {
  const { isDark } = useTheme();

  // Mapa de configurações
  const statusConfig = {
    agendado: {
      bgColor: 'bg-sminy-primary/10',
      textColor: 'text-sminy-primary',
      icon: <Calendar className="w-3 h-3 mr-1" />,
      label: 'Agendado'
    },
    compareceu: {
      bgColor: isDark ? 'bg-emerald-900/40' : 'bg-emerald-100',
      textColor: isDark ? 'text-emerald-300' : 'text-emerald-700',
      icon: <CheckCircle2 className="w-3 h-3 mr-1" />,
      label: 'Compareceu'
    },
    faltou: {
      bgColor: isDark ? 'bg-rose-900/40' : 'bg-rose-100',
      textColor: isDark ? 'text-rose-300' : 'text-rose-700',
      icon: <XCircle className="w-3 h-3 mr-1" />,
      label: 'Faltou'
    },
    venda: {
      bgColor: isDark ? 'bg-violet-900/40' : 'bg-violet-100',
      textColor: isDark ? 'text-violet-300' : 'text-violet-700',
      icon: <DollarSign className="w-3 h-3 mr-1" />,
      label: 'Venda Efetuada'
    },
    pendente: {
      bgColor: isDark ? 'bg-amber-900/40' : 'bg-amber-100',
      textColor: isDark ? 'text-amber-300' : 'text-amber-700',
      icon: <Clock className="w-3 h-3 mr-1" />,
      label: 'Pendente'
    }
  };

  const config = statusConfig[status];

  return (
    <span className={`${config.bgColor} ${config.textColor} rounded-full px-2 py-0.5 text-xs font-medium flex items-center ${className}`}>
      {config.icon}
      {config.label}
    </span>
  );
};

// Componente para mostrar o status de sincronização
const SyncStatusTag: React.FC<{
  status?: string;
  provider: 'google' | 'clinicorp';
}> = ({ status, provider }) => {
  const { isDark } = useTheme();

  // Se não tiver status, não mostra nada
  if (!status) return null;

  // Configurações visuais por provedor
  const providerConfig = {
    google: {
      bgColor: 'bg-sminy-primary/10',
      textColor: 'text-sminy-primary',
      label: 'Google'
    },
    clinicorp: {
      bgColor: isDark ? 'bg-amber-900/40' : 'bg-amber-100',
      textColor: isDark ? 'text-amber-300' : 'text-amber-700',
      label: 'Clinicorp'
    }
  };

  // Configurações por status
  const statusLabel = {
    'SYNCED': 'Sincronizado',
    'FAILED': 'Falhou',
    'PENDING': 'Sincronizando',
    'SYNC_UNKNOWN': 'Confirmação pendente',
    'CANCELLED': 'Cancelado',
    'CANCEL_FAILED': 'Falha ao Cancelar',
    'NOT_APPLICABLE': 'N/A'
  };

  const config = providerConfig[provider];
  const label = statusLabel[status as keyof typeof statusLabel] || status;

  return (
    <span className={`${config.bgColor} ${config.textColor} rounded-full px-2 py-0.5 text-xs font-medium flex items-center mr-1`}>
      {config.label}: {label}
    </span>
  );
};

// =========================
// 1. Função auxiliar para verificar se um lead é novo (últimas 24 horas)
// =========================

const isNewLead = (dateStr?: string): boolean => {
  if (!dateStr) return false;
  try {
    const leadDate = new Date(dateStr);
    const now = new Date();
    const diffTime = now.getTime() - leadDate.getTime();
    const diffHours = diffTime / (1000 * 60 * 60);
    return diffHours <= 24;
  } catch {
    return false;
  }
};


// =========================
//      HELPER FUNCS
// =========================

const getClinicTimezoneForDisplay = (): string | null => {
  const clinicId = localStorage.getItem('clinic_id');
  if (!clinicId) return null;
  return localStorage.getItem(`sminy.clinicTimezone.${clinicId}`);
};

export const formatDateTime = (dateStr?: string) => {
  if (!dateStr) return 'Data não informada';
  try {
    const normalized = dateStr.replace(' ', 'T');
    const hasExplicitOffset = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalized);
    if (!hasExplicitOffset) {
      const [datePart, timePart = '00:00'] = normalized.split('T');
      const [year, month, day] = datePart.split('-');
      const timeComponents = timePart.split(':');
      const hours = timeComponents[0] || '00';
      const minutes = timeComponents[1] || '00';
      if (year && month && day) {
        return `${day}/${month}/${year}, ${hours}:${minutes}`;
      }
    }

    const clinicTimezone = getClinicTimezoneForDisplay();
    if (!clinicTimezone) return 'Fuso horário da clínica não configurado';
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return dateStr;
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: clinicTimezone
    });
  } catch {
    return dateStr;
  }
};

const formatNoShowDateTime = (dateStr?: string) => formatDateTime(dateStr);

export const extractDateTimeInputValues = (dateStr?: string) => {
  if (!dateStr) return null;

  const normalized = dateStr.replace(' ', 'T');
  const hasExplicitOffset = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalized);
  if (hasExplicitOffset) {
    const clinicTimezone = getClinicTimezoneForDisplay();
    if (!clinicTimezone) return null;
    const parsed = new Date(normalized);
    if (Number.isNaN(parsed.getTime())) return null;
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: clinicTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(parsed);
    const value = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((part) => part.type === type)?.value || '';
    return {
      date: `${value('year')}-${value('month')}-${value('day')}`,
      time: `${value('hour')}:${value('minute')}`,
    };
  }
  const datePart = normalized.slice(0, 10);
  const timePart = normalized.slice(11, 16);

  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart) && /^\d{2}:\d{2}$/.test(timePart)) {
    return {
      date: datePart,
      time: timePart,
    };
  }

  const parsedDate = new Date(dateStr);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return {
    date: `${parsedDate.getFullYear()}-${String(parsedDate.getMonth() + 1).padStart(2, '0')}-${String(parsedDate.getDate()).padStart(2, '0')}`,
    time: `${String(parsedDate.getHours()).padStart(2, '0')}:${String(parsedDate.getMinutes()).padStart(2, '0')}`,
  };
};

const formatTimeAgo = (dateStr?: string) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffMinutes = Math.floor(diffTime / (1000 * 60));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    if (diffMinutes < 60) {
      return `há ${diffMinutes} minuto${diffMinutes === 1 ? '' : 's'}`;
    }
    if (diffHours < 24) {
      return `há ${diffHours} hora${diffHours === 1 ? '' : 's'}`;
    }
    return `há ${diffDays} dia${diffDays === 1 ? '' : 's'}`;
  } catch {
    return '';
  }
};

const formatCurrencyBRL = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

function maskCurrencyBRL(value: string): string {
  const numericOnly = value.replace(/\D/g, '');
  if (!numericOnly) return '';
  let intValue = parseInt(numericOnly, 10);
  if (isNaN(intValue)) return '';
  const cents = (intValue % 100).toString().padStart(2, '0');
  intValue = Math.floor(intValue / 100);
  const intString = intValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `R$ ${intString},${cents}`;
}

function parseBRLToNumber(value: string): number {
  const clean = value.replace('R$ ', '').replace(/\./g, '').replace(',', '.');
  return parseFloat(clean) || 0;
}

// ========================
//         CARDS
// ========================

export const CrmContactMenuItems: React.FC<{ contact: CrmChatContact; stage: CrmStage; close: () => void }> = ({ contact, stage, close }) => {
  const actions = useCrmLossActions();
  const phoneActions = useCrmPhoneCorrection();
  const leadId = getCrmPhoneLeadId(contact, stage);
  return <>
    {phoneActions.enabled && <DropdownMenuItem icon={<Phone className="h-4 w-4" />} disabled={!leadId} onClick={() => { if (leadId) phoneActions.edit({ leadId, name: contact.name || contact.nome }); close(); }}>Editar telefone</DropdownMenuItem>}
    {actions.enabled && stage !== 'sales' && <DropdownMenuItem icon={<UserX className="h-4 w-4" />} onClick={() => { close(); actions.mark(contact, stage); }}>Marcar como perdido</DropdownMenuItem>}
    {actions.enabled && <DropdownMenuItem icon={<FileText className="h-4 w-4" />} onClick={() => { close(); actions.history(contact); }}>Histórico de perdas</DropdownMenuItem>}
  </>;
};

interface CrmChatContact {
  id?: number;
  lead_id?: number;
  contact_id?: number;
  name?: string;
  nome?: string;
  phone?: string;
  photo?: string;
  thumbnail_url?: string;
}

type OpenChatHandler<T extends CrmChatContact> = (contact: T) => void;

const getCrmChatContactName = (contact: CrmChatContact): string => (
  contact.name || contact.nome || contact.phone || 'Contato sem nome'
);

// ---------- LEAD CARD -----------
interface LeadCardProps {
  lead: Lead;
  appointments: AgendamentoResponse[]; // Nova prop
  leadTags?: LeadTagsSummary; // Nova prop para tags do lead
  onEdit: (lead: Lead) => void;
  onCreateAppointment: (lead: Lead) => void;
  onDelete: (lead: Lead) => void;
  onOpenChat: OpenChatHandler<Lead>;
  onTagsUpdated: (leadId: number) => void; // Nova prop para atualização de tags
}

const LeadCard: React.FC<LeadCardProps> = ({
  lead,
  appointments, // Nova prop
  leadTags, // Nova prop para tags
  onEdit,
  onCreateAppointment,
  onDelete,
  onOpenChat,
  onTagsUpdated // Nova prop para atualização de tags
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showTagsModal, setShowTagsModal] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const formatLeadDate = (dateStr?: string) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
        hour12: false
      });
    } catch {
      return dateStr;
    }
  };

  // Verificar se o lead já tem agendamento
  const isScheduled = appointments.some(appointment => appointment.lead_id === lead.id);

  // Handler para atualização de tags (com otimização de debounce)
  const handleTagsUpdated = () => {
    console.log(`🔄 LeadCard: Recarregando tags do lead ${lead.id} após atualização`);
    onTagsUpdated(lead.id);
  };

  return (
    <Card>
      {/* Cabeçalho com título e menu */}
      <div className="mb-2 flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <CrmContactAvatar name={lead.name} phone={lead.phone} photo={lead.photo} />
          <div className="min-w-0">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              {lead.data_entrada && (
                <span className="text-xs text-gray-500">
                  {formatLeadDate(lead.data_entrada)}
                </span>
              )}

              {/* Badge de novo lead */}
              {isNewLead(lead.data_entrada) && (
                <Badge variant="success" className="animate-pulse">Novo</Badge>
              )}

              {/* Tag de status - agendamento */}
              {isScheduled && (
                <SimpleStatusTag status="agendado" />
              )}
              <PatientStatusBadge isPatient={lead.is_patient} />
            </div>
            <h3 className="truncate text-base font-medium text-gray-900">{lead.name || 'Nome não informado'}</h3>
          </div>
        </div>

        <div className="relative" ref={menuRef}>
          <Button
            variant="ghost"
            size="sm"
            icon={<MoreVertical className="w-4 h-4" />}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Opções" aria-expanded={menuOpen}
          />

          {menuOpen && (
            <DropdownMenu anchorRef={menuRef} onClose={() => setMenuOpen(false)}>
              <CrmContactMenuItems contact={lead} stage="leads" close={() => setMenuOpen(false)} />
              <DropdownMenuItem
                icon={<MessageCircle className="w-4 h-4" />}
                onClick={() => {
                  setMenuOpen(false);
                  onOpenChat(lead);
                }}
                disabled={!lead.phone}
                title={!lead.phone ? 'Telefone não informado' : undefined}
              >
                Abrir no chat
              </DropdownMenuItem>
              <DropdownMenuItem
                icon={<FileText className="w-4 h-4" />}
                onClick={() => {
                  setMenuOpen(false);
                  onEdit(lead);
                }}
              >
                Editar
              </DropdownMenuItem>
              <DropdownMenuItem
                icon={<Calendar className="w-4 h-4" />}
                onClick={() => {
                  setMenuOpen(false);
                  onCreateAppointment(lead);
                }}
                disabled={isScheduled}
              >
                Criar Agendamento
              </DropdownMenuItem>
              <DropdownMenuItem
                icon={<Hash className="w-4 h-4" />}
                onClick={() => {
                  setMenuOpen(false);
                  setShowTagsModal(true);
                }}
              >
                Gerenciar Tags
              </DropdownMenuItem>
              <DropdownMenuItem
                icon={<X className="w-4 h-4" />}
                variant="danger"
                onClick={() => {
                  setMenuOpen(false);
                  onDelete(lead);
                }}
              >
                Excluir
              </DropdownMenuItem>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Conteúdo principal - limite a altura e use overflow para conteúdo grande */}
      <div className="space-y-3 text-sm overflow-y-auto max-h-[160px] pb-2">
        <div className="flex items-center text-gray-700">
          <Phone className="w-4 h-4 mr-2 text-gray-400 flex-shrink-0" />
          <span className="truncate">{lead.phone || 'Telefone não informado'}</span>
        </div>

        <CrmMediaPreview sourceId={lead.source_id} thumbnailUrl={lead.thumbnail_url} />
      </div>

      {/* Seção de Tags - posicionada acima dos botões */}
      <div className="pb-2">
        <TagsDisplay tags={leadTags} />
      </div>

      {/* Botão no footer - posicionado absolutamente na parte inferior */}
      <div className="absolute bottom-4 left-4 right-4 pt-3 border-t border-gray-100">
        <Button
          variant={isScheduled ? "secondary" : "ghost"}
          size="sm"
          className="w-full justify-center text-sminy-primary"
          icon={<Calendar className="w-4 h-4" />}
          onClick={() => onCreateAppointment(lead)}
          disabled={isScheduled}
        >
          {isScheduled ? 'Já Agendado' : 'Agendar Consulta'}
        </Button>
      </div>

      {/* TagsModal */}
      <TagsModal
        isOpen={showTagsModal}
        onClose={() => setShowTagsModal(false)}
        leadId={lead.id}
        leadName={lead.name}
        currentTags={leadTags?.tags?.map(t => t.id) || []}
        onTagsUpdated={handleTagsUpdated}
      />
    </Card>
  );
};

// ---------- APPOINTMENT CARD -----------
// Atualização do AppointmentCard para incluir as tags de status
interface AppointmentCardProps {
  appointment: AgendamentoResponse;
  attendances: Comparecimento[]; // Nova prop
  noShows: NoShowEvent[]; // Nova prop
  appointmentTags?: LeadTagsSummary; // Nova prop para tags
  onAttendanceClick: (appointment: AgendamentoResponse) => void;
  onRescheduleClick: (appointment: AgendamentoResponse) => void;
  onNoShowClick: (appointment: AgendamentoResponse) => void;
  onEditClick: (appointment: AgendamentoResponse) => void;
  onDeleteClick: (appointment: AgendamentoResponse) => void;
  onOpenChat: OpenChatHandler<AgendamentoResponse>;
  onTagsUpdated: (leadId: number) => void; // Nova prop para atualização de tags
}

const AppointmentCard: React.FC<AppointmentCardProps> = ({
  appointment,
  attendances,
  noShows,
  appointmentTags, // Nova prop para tags
  onAttendanceClick,
  onRescheduleClick,
  onNoShowClick,
  onEditClick,
  onDeleteClick,
  onOpenChat,
  onTagsUpdated // Nova prop para atualização de tags
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showTagsModal, setShowTagsModal] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Verificar status do agendamento
  const hasAttendanceRecord = hasAttendance(appointment, attendances);
  const hasNoShowRecord = hasNoShow(appointment, noShows);

  // Handler para atualização de tags (com otimização de debounce)
  const handleTagsUpdated = () => {
    console.log(`🔄 AppointmentCard: Recarregando tags do lead ${appointment.lead_id} após atualização`);
    onTagsUpdated(appointment.lead_id);
  };



  return (
    <Card>
      {/* Cabeçalho com título e menu */}
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <CrmContactAvatar name={appointment.nome} phone={appointment.phone} photo={appointment.photo} />
          <div className="min-w-0">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-500">
                {formatTimeAgo(appointment.agendamento_realizado_em)}
              </span>

              {/* Status Tags - mostrar apenas se compareceu ou faltou */}
              {hasAttendanceRecord && (
                <SimpleStatusTag status="compareceu" />
              )}
              {hasNoShowRecord && (
                <SimpleStatusTag status="faltou" />
              )}
              <PatientStatusBadge isPatient={appointment.is_patient} />
            </div>
            <h3 className="truncate text-base font-medium text-gray-900">{appointment.nome || 'Nome não informado'}</h3>

            {/* Status de sincronização */}
            <div className="mt-1 flex flex-wrap">
              <SyncStatusTag
                status={appointment.google_sync_status}
                provider="google"
              />
              <SyncStatusTag
                status={appointment.clinicorp_sync_status}
                provider="clinicorp"
              />
            </div>
          </div>
        </div>

        <div className="relative" ref={menuRef}>
          <Button
            variant="ghost"
            size="sm"
            icon={<MoreVertical className="w-4 h-4" />}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Opções" aria-expanded={menuOpen}
          />

          {menuOpen && (
            <DropdownMenu anchorRef={menuRef} onClose={() => setMenuOpen(false)}>
              <CrmContactMenuItems contact={appointment} stage="appointments" close={() => setMenuOpen(false)} />
              <DropdownMenuItem
                icon={<MessageCircle className="w-4 h-4" />}
                onClick={() => {
                  setMenuOpen(false);
                  onOpenChat(appointment);
                }}
                disabled={!appointment.phone}
                title={!appointment.phone ? 'Telefone não informado' : undefined}
              >
                Abrir no chat
              </DropdownMenuItem>
              <DropdownMenuItem
                icon={<FileText className="w-4 h-4" />}
                onClick={() => {
                  setMenuOpen(false);
                  onEditClick(appointment);
                }}
              >
                Editar
              </DropdownMenuItem>
              {!hasNoShowRecord && (
                <DropdownMenuItem
                  icon={<CheckCircle2 className="w-4 h-4" />}
                  onClick={() => {
                    setMenuOpen(false);
                    onAttendanceClick(appointment);
                  }}
                  disabled={hasAttendanceRecord}
                >
                  {hasAttendanceRecord ? "Já Compareceu" : "Registrar Comparecimento"}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                icon={<RefreshCw className="w-4 h-4" />}
                onClick={() => {
                  setMenuOpen(false);
                  onRescheduleClick(appointment);
                }}
              >
                Reagendar
              </DropdownMenuItem>
              {!hasAttendanceRecord && (
                <DropdownMenuItem
                  icon={<XCircle className="w-4 h-4" />}
                  onClick={() => {
                    setMenuOpen(false);
                    onNoShowClick(appointment);
                  }}
                  disabled={hasNoShowRecord}
                >
                  {hasNoShowRecord ? "Já Faltou" : "Registrar Falta"}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                icon={<Hash className="w-4 h-4" />}
                onClick={() => {
                  setMenuOpen(false);
                  setShowTagsModal(true);
                }}
              >
                Gerenciar Tags
              </DropdownMenuItem>
              <hr className="my-1 border-gray-200" />
              <DropdownMenuItem
                icon={<X className="w-4 h-4" />}
                variant="danger"
                onClick={() => {
                  setMenuOpen(false);
                  onDeleteClick(appointment);
                }}
              >
                Excluir
              </DropdownMenuItem>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Conteúdo principal - com altura limitada e scrollável se necessário */}
      <div className="space-y-2 text-sm overflow-y-auto max-h-[160px] pb-2">
        <div className="flex items-center text-gray-700">
          <Calendar className="w-4 h-4 mr-2 text-gray-400 flex-shrink-0" />
          <span className="truncate">
            {appointment.consulta_data
              ? formatDateTime(appointment.consulta_data)
              : 'Data não agendada'}
          </span>
        </div>
        <div className="flex items-center text-gray-700">
          <Phone className="w-4 h-4 mr-2 text-gray-400 flex-shrink-0" />
          <span className="truncate">{appointment.phone || 'Telefone não informado'}</span>
        </div>
        <CrmMediaPreview
          sourceId={appointment.midia || appointment.source_id}
          thumbnailUrl={appointment.thumbnail_url}
        />
        {appointment.interesse && (
          <div className="flex items-center text-gray-700">
            <Info className="w-4 h-4 mr-2 text-gray-400 flex-shrink-0" />
            <span className="truncate">Interesse: {appointment.interesse}</span>
          </div>
        )}
      </div>

      {/* Seção de Tags - posicionada acima dos botões */}
      <div className="pb-2">
        <TagsDisplay tags={appointmentTags} />
      </div>

      {/* Botões na parte inferior - posicionados absolutamente */}
      <div className="absolute bottom-4 left-4 right-4 pt-3 border-t border-gray-100 flex space-x-2">
        {hasNoShowRecord ? (
          // Se já faltou, mostra apenas o botão de falta como inativo
          <Button
            variant="secondary"
            size="sm"
            className="w-full justify-center"
            icon={<XCircle className="w-4 h-4" />}
            disabled={true}
          >
            Faltou
          </Button>
        ) : hasAttendanceRecord ? (
          // Se já compareceu, mostra apenas o botão de comparecimento como inativo
          <Button
            variant="secondary"
            size="sm"
            className="w-full justify-center"
            icon={<CheckCircle2 className="w-4 h-4" />}
            disabled={true}
          >
            Compareceu
          </Button>
        ) : (
          // Se não tem status ainda, mostra ambos os botões ativos
          <>
            <Button
              variant="success"
              size="sm"
              className="flex-1 justify-center"
              icon={<CheckCircle2 className="w-4 h-4" />}
              onClick={() => onAttendanceClick(appointment)}
            >
              Compareceu
            </Button>
            <Button
              variant="danger"
              size="sm"
              className="flex-1 justify-center"
              icon={<XCircle className="w-4 h-4" />}
              onClick={() => onNoShowClick(appointment)}
            >
              Faltou
            </Button>
          </>
        )}
      </div>

      {/* TagsModal */}
      <TagsModal
        isOpen={showTagsModal}
        onClose={() => setShowTagsModal(false)}
        leadId={appointment.lead_id}
        leadName={appointment.nome}
        currentTags={appointmentTags?.tags?.map(t => t.id) || []}
        onTagsUpdated={handleTagsUpdated}
      />
    </Card>
  );
};

// ---------- ATTENDANCE CARD -----------
// Atualização do AttendanceCard para incluir as tags de status
interface AttendanceCardProps {
  attendance: Comparecimento;
  sales: Venda[]; // Nova prop
  attendanceTags?: LeadTagsSummary; // Nova prop para tags
  onSaleClick: (attendance: Comparecimento) => void;
  onEditClick: (attendance: Comparecimento) => void;
  onDeleteClick: (attendance: Comparecimento) => void;
  onOpenChat: OpenChatHandler<Comparecimento>;
  onTagsUpdated: (leadId: number) => void; // Nova prop para atualização de tags
}

const AttendanceCard: React.FC<AttendanceCardProps> = ({
  attendance,
  sales,
  attendanceTags, // Nova prop para tags
  onSaleClick,
  onEditClick,
  onDeleteClick,
  onOpenChat,
  onTagsUpdated // Nova prop para atualização de tags
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showTagsModal, setShowTagsModal] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Verificar se há venda associada a este comparecimento
  const hasSaleRecord = hasSale(attendance, sales);

  // Handler para atualização de tags (com otimização de debounce)
  const handleTagsUpdated = () => {
    console.log(`🔄 AttendanceCard: Recarregando tags do lead ${attendance.lead_id} após atualização`);
    onTagsUpdated(attendance.lead_id);
  };



  return (
    <Card>
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <CrmContactAvatar name={attendance.nome} phone={attendance.phone} photo={attendance.photo} />
          <div className="min-w-0">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-500">
                {formatTimeAgo(attendance.compareceu_em)}
              </span>

              {/* Mostrar apenas tag de venda quando existir */}
              {hasSaleRecord && (
                <SimpleStatusTag status="venda" />
              )}
              <PatientStatusBadge isPatient={attendance.is_patient} />
            </div>
            <h3 className="truncate text-base font-medium text-gray-900">{attendance.nome || 'Nome não informado'}</h3>
          </div>
        </div>

        <div className="relative" ref={menuRef}>
          <Button
            variant="ghost"
            size="sm"
            icon={<MoreVertical className="w-4 h-4" />}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Opções" aria-expanded={menuOpen}
          />

          {menuOpen && (
            <DropdownMenu anchorRef={menuRef} onClose={() => setMenuOpen(false)}>
              <CrmContactMenuItems contact={attendance} stage="attendances" close={() => setMenuOpen(false)} />
              <DropdownMenuItem
                icon={<MessageCircle className="w-4 h-4" />}
                onClick={() => {
                  setMenuOpen(false);
                  onOpenChat(attendance);
                }}
                disabled={!attendance.phone}
                title={!attendance.phone ? 'Telefone não informado' : undefined}
              >
                Abrir no chat
              </DropdownMenuItem>
              <DropdownMenuItem
                icon={<Hash className="w-4 h-4" />}
                onClick={() => {
                  setMenuOpen(false);
                  setShowTagsModal(true);
                }}
              >
                Gerenciar Tags
              </DropdownMenuItem>
              <DropdownMenuItem
                icon={<FileText className="w-4 h-4" />}
                onClick={() => {
                  setMenuOpen(false);
                  onEditClick(attendance);
                }}
              >
                Editar
              </DropdownMenuItem>
              <DropdownMenuItem
                icon={<X className="w-4 h-4" />}
                variant="danger"
                onClick={() => {
                  setMenuOpen(false);
                  onDeleteClick(attendance);
                }}
              >
                Excluir
              </DropdownMenuItem>
            </DropdownMenu>
          )}
        </div>
      </div>

      <div className="space-y-2 text-sm overflow-y-auto max-h-[160px] pb-2">
        <div className="flex items-center text-gray-700">
          <Calendar className="w-4 h-4 mr-2 text-gray-400" />
          <span>{formatDateTime(attendance.compareceu_em)}</span>
        </div>
        <div className="flex items-center text-gray-700">
          <Phone className="w-4 h-4 mr-2 text-gray-400" />
          <span>{attendance.phone || 'Telefone não informado'}</span>
        </div>
        <CrmMediaPreview
          sourceId={attendance.midia || attendance.source_id}
          thumbnailUrl={attendance.thumbnail_url}
        />
        {attendance.tratamento_orcado && (
          <div className="flex items-center text-gray-700">
            <Info className="w-4 h-4 mr-2 text-gray-400" />
            <span>Tratamento Orçado: {attendance.tratamento_orcado}</span>
          </div>
        )}
        {attendance.valor_orcamento && (
          <div className="flex items-center text-gray-700">
            <DollarSign className="w-4 h-4 mr-2 text-gray-400" />
            <span>
              Valor do Orçamento: {formatCurrencyBRL(attendance.valor_orcamento)}
            </span>
          </div>
        )}
      </div>

      {/* Seção de Tags - posicionada acima dos botões */}
      <div className="pb-2">
        <TagsDisplay tags={attendanceTags} />
      </div>

      {/* Botão na parte inferior - posicionado absolutamente */}
      <div className="absolute bottom-4 left-4 right-4 pt-3 border-t border-gray-100">
        <Button
          variant={hasSaleRecord ? "secondary" : "primary"}
          size="md"
          className="w-full justify-center"
          icon={<DollarSign className="w-4 h-4" />}
          onClick={() => onSaleClick(attendance)}
          disabled={hasSaleRecord}
        >
          {hasSaleRecord ? 'Venda Registrada' : 'Registrar Venda'}
        </Button>
      </div>

      {/* Modal de Tags */}
      {showTagsModal && (
        <TagsModal
          isOpen={showTagsModal}
          onClose={() => setShowTagsModal(false)}
          leadId={attendance.lead_id}
          leadName={attendance.nome || `Lead #${attendance.lead_id}`}
          currentTags={attendanceTags?.tags?.map(t => t.id) || []}
          onTagsUpdated={handleTagsUpdated}
        />
      )}
    </Card>
  );
};

// ---------- SALE CARD -----------
interface SaleCardProps {
  sale: Venda;
  saleTags?: LeadTagsSummary; // Nova prop para tags
  onNewSaleClick: (sale: Venda) => void;
  onEditClick: (sale: Venda) => void;
  onDeleteClick: (sale: Venda) => void;
  onOpenChat: OpenChatHandler<Venda>;
  onTagsUpdated: (leadId: number) => void; // Nova prop para atualização de tags
}

export const SaleCard: React.FC<SaleCardProps> = ({
  sale,
  saleTags, // Nova prop para tags
  onNewSaleClick,
  onEditClick,
  onDeleteClick,
  onOpenChat,
  onTagsUpdated // Nova prop para atualização de tags
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showTagsModal, setShowTagsModal] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Handler para atualização de tags (com otimização de debounce)
  const handleTagsUpdated = () => {
    console.log(`🔄 SaleCard: Recarregando tags do lead ${sale.lead_id} após atualização`);
    onTagsUpdated(sale.lead_id);
  };



  return (
    <Card>
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <CrmContactAvatar name={sale.nome} phone={sale.phone} photo={sale.photo} />
          <div className="min-w-0">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-500">{formatTimeAgo(sale.venda_data)}</span>
              <PatientStatusBadge isPatient={sale.is_patient} />
            </div>
            <h3 className="truncate text-base font-medium text-gray-900">{sale.nome || 'Nome não informado'}</h3>
          </div>
        </div>

        <div className="relative" ref={menuRef}>
          <Button
            variant="ghost"
            size="sm"
            icon={<MoreVertical className="w-4 h-4" />}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Opções" aria-expanded={menuOpen}
          />

          {menuOpen && (
            <DropdownMenu anchorRef={menuRef} onClose={() => setMenuOpen(false)}>
              <CrmContactMenuItems contact={sale} stage="sales" close={() => setMenuOpen(false)} />
              <DropdownMenuItem
                icon={<MessageCircle className="w-4 h-4" />}
                onClick={() => {
                  setMenuOpen(false);
                  onOpenChat(sale);
                }}
                disabled={!sale.phone}
                title={!sale.phone ? 'Telefone não informado' : undefined}
              >
                Abrir no chat
              </DropdownMenuItem>
              <DropdownMenuItem
                icon={<PlusCircle className="w-4 h-4" />}
                onClick={() => {
                  setMenuOpen(false);
                  onNewSaleClick(sale);
                }}
              >
                Registrar nova venda
              </DropdownMenuItem>
              <DropdownMenuItem
                icon={<Hash className="w-4 h-4" />}
                onClick={() => {
                  setMenuOpen(false);
                  setShowTagsModal(true);
                }}
              >
                Gerenciar Tags
              </DropdownMenuItem>
              <DropdownMenuItem
                icon={<FileText className="w-4 h-4" />}
                onClick={() => {
                  setMenuOpen(false);
                  onEditClick(sale);
                }}
              >
                Editar
              </DropdownMenuItem>
              <DropdownMenuItem
                icon={<X className="w-4 h-4" />}
                variant="danger"
                onClick={() => {
                  setMenuOpen(false);
                  onDeleteClick(sale);
                }}
              >
                Excluir
              </DropdownMenuItem>
            </DropdownMenu>
          )}
        </div>
      </div>

      <div className="space-y-2 text-sm">
        <div className="flex items-center text-gray-700">
          <Calendar className="w-4 h-4 mr-2 text-gray-400" />
          <span>{formatDateTime(sale.venda_data)}</span>
        </div>
        <div className="flex items-center text-gray-700">
          <Phone className="w-4 h-4 mr-2 text-gray-400" />
          <span>{sale.phone || 'Telefone não informado'}</span>
        </div>
        <CrmMediaPreview
          sourceId={sale.midia || sale.source_id}
          thumbnailUrl={sale.thumbnail_url}
        />
        {sale.tratamento_fechado && (
          <div className="flex items-center text-gray-700">
            <Info className="w-4 h-4 mr-2 text-gray-400" />
            <span>Tratamento: {sale.tratamento_fechado}</span>
          </div>
        )}
        {sale.valor_faturado !== null && (
          <div className="flex items-center text-gray-700">
            <DollarSign className="w-4 h-4 mr-2 text-gray-400" />
            <span>
              Valor Faturado: {formatCurrencyBRL(sale.valor_faturado as number)}
            </span>
          </div>
        )}
        {sale.valor_pago !== null && (
          <div className="flex items-center text-gray-700">
            <DollarSign className="w-4 h-4 mr-2 text-gray-400" />
            <span>
              Valor Pago: {formatCurrencyBRL(sale.valor_pago as number)}
            </span>
          </div>
        )}
      </div>

      {/* Seção de Tags */}
      <TagsDisplay tags={saleTags} />

      {/* Modal de Tags */}
      {showTagsModal && (
        <TagsModal
          isOpen={showTagsModal}
          onClose={() => setShowTagsModal(false)}
          leadId={sale.lead_id}
          leadName={sale.nome || `Lead #${sale.lead_id}`}
          currentTags={saleTags?.tags?.map(t => t.id) || []}
          onTagsUpdated={handleTagsUpdated}
        />
      )}
    </Card>
  );
};

// ---------- NO-SHOW CARD -----------
interface NoShowEvent {
  id: number;
  client_id: number;
  clinic_id: number;
  lead_id: number;
  agendamento_id: number;
  marcado_em: string;
  observacao?: string;
  nome?: string;
  phone?: string;
  photo?: string;
  is_patient?: boolean;
  midia?: string;
  source_id?: string;
  thumbnail_url?: string;
  media_editable?: boolean;
  data_agendada?: string;
}

interface NoShowCardProps {
  noShow: NoShowEvent;
  noShowTags?: LeadTagsSummary; // Nova prop para tags
  onRescheduleClick: (noShow: NoShowEvent) => void;
  onEditClick: (noShow: NoShowEvent) => void;
  onDeleteClick: (noShow: NoShowEvent) => void;
  onOpenChat: OpenChatHandler<NoShowEvent>;
  onTagsUpdated: (leadId: number) => void; // Nova prop para atualização de tags
}

const NoShowCard: React.FC<NoShowCardProps> = ({
  noShow,
  noShowTags, // Nova prop para tags
  onRescheduleClick,
  onEditClick,
  onDeleteClick,
  onOpenChat,
  onTagsUpdated // Nova prop para atualização de tags
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showTagsModal, setShowTagsModal] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Handler para atualização de tags (com otimização de debounce)
  const handleTagsUpdated = () => {
    console.log(`🔄 NoShowCard: Recarregando tags do lead ${noShow.lead_id} após atualização`);
    onTagsUpdated(noShow.lead_id);
  };



  return (
    <Card>
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <CrmContactAvatar name={noShow.nome} phone={noShow.phone} photo={noShow.photo} />
          <div className="min-w-0">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-500">
                Marcado em: {formatNoShowDateTime(noShow.marcado_em)}
              </span>
              <PatientStatusBadge isPatient={noShow.is_patient} />
            </div>
            <h3 className="truncate text-base font-medium text-gray-900">{noShow.nome || 'Nome não informado'}</h3>
          </div>
        </div>

        <div className="relative" ref={menuRef}>
          <Button
            variant="ghost"
            size="sm"
            icon={<MoreVertical className="w-4 h-4" />}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Opções" aria-expanded={menuOpen}
          />

          {menuOpen && (
            <DropdownMenu anchorRef={menuRef} onClose={() => setMenuOpen(false)}>
              <CrmContactMenuItems contact={noShow} stage="noShows" close={() => setMenuOpen(false)} />
              <DropdownMenuItem
                icon={<MessageCircle className="w-4 h-4" />}
                onClick={() => {
                  setMenuOpen(false);
                  onOpenChat(noShow);
                }}
                disabled={!noShow.phone}
                title={!noShow.phone ? 'Telefone não informado' : undefined}
              >
                Abrir no chat
              </DropdownMenuItem>
              <DropdownMenuItem
                icon={<Hash className="w-4 h-4" />}
                onClick={() => {
                  setMenuOpen(false);
                  setShowTagsModal(true);
                }}
              >
                Gerenciar Tags
              </DropdownMenuItem>
              <DropdownMenuItem
                icon={<RefreshCw className="w-4 h-4" />}
                onClick={() => {
                  setMenuOpen(false);
                  onRescheduleClick(noShow);
                }}
              >
                Reagendar
              </DropdownMenuItem>
              <DropdownMenuItem
                icon={<FileText className="w-4 h-4" />}
                onClick={() => {
                  setMenuOpen(false);
                  onEditClick(noShow);
                }}
              >
                Editar Falta
              </DropdownMenuItem>
              <DropdownMenuItem
                icon={<X className="w-4 h-4" />}
                variant="danger"
                onClick={() => {
                  setMenuOpen(false);
                  onDeleteClick(noShow);
                }}
              >
                Excluir Falta
              </DropdownMenuItem>
            </DropdownMenu>
          )}
        </div>
      </div>

      <div className="space-y-2 text-sm">
        {noShow.data_agendada && (
          <div className="flex items-center text-gray-700">
            <Calendar className="w-4 h-4 mr-2 text-gray-400" />
            <span>Data Agendada: {formatDateTime(noShow.data_agendada)}</span>
          </div>
        )}
        {noShow.phone && (
          <div className="flex items-center text-gray-700">
            <Phone className="w-4 h-4 mr-2 text-gray-400" />
            <span>Telefone: {noShow.phone}</span>
          </div>
        )}
        <CrmMediaPreview
          sourceId={noShow.midia || noShow.source_id}
          thumbnailUrl={noShow.thumbnail_url}
        />
        {noShow.observacao && (
          <div className="flex items-start text-gray-700">
            <Info className="w-4 h-4 mr-2 mt-0.5 text-gray-400 flex-shrink-0" />
            <span>Observação: {noShow.observacao}</span>
          </div>
        )}
      </div>

      {/* Seção de Tags - posicionada no final */}
      <TagsDisplay tags={noShowTags} />

      {/* Modal de Tags */}
      {showTagsModal && (
        <TagsModal
          isOpen={showTagsModal}
          onClose={() => setShowTagsModal(false)}
          leadId={noShow.lead_id}
          leadName={noShow.nome || `Lead #${noShow.lead_id}`}
          currentTags={noShowTags?.manual_tags?.map(t => t.id) || []}
          onTagsUpdated={handleTagsUpdated}
        />
      )}
    </Card>
  );
};

// ---------- LIST VIEW -----------
interface CrmListColumn {
  label: string;
  className?: string;
  align?: 'left' | 'right';
}

const CrmListTable: React.FC<{
  columns: CrmListColumn[];
  children: React.ReactNode;
}> = ({ columns, children }) => {
  const { isDark } = useTheme();

  return (
    <div className={clsx(
      "hidden lg:block overflow-x-auto rounded-2xl border shadow-sm",
      isDark ? "border-gray-700 bg-gray-800" : "border-slate-200 bg-white",
      isDark && "[&_tr:hover]:!bg-gray-700/40 [&_.text-gray-900]:!text-gray-100 [&_.text-gray-800]:!text-gray-100 [&_.text-gray-700]:!text-gray-300 [&_.text-gray-600]:!text-gray-400 [&_.text-gray-500]:!text-gray-400"
    )}>
      <table className={clsx("w-full table-fixed divide-y", isDark ? "divide-gray-700" : "divide-slate-100")}>
        <thead className={isDark ? "bg-gray-900/50" : "bg-slate-50"}>
          <tr>
            {columns.map((column) => (
              <th
                key={column.label}
                scope="col"
                className={clsx(
                  'px-4 py-3 text-xs font-semibold uppercase tracking-wide',
                  isDark ? 'text-gray-400' : 'text-slate-500',
                  column.align === 'right' ? 'text-right' : 'text-left',
                  column.className
                )}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className={clsx("divide-y", isDark ? "divide-gray-700 bg-gray-800" : "divide-slate-100 bg-white")}>
          {children}
        </tbody>
      </table>
    </div>
  );
};

const CrmMobileListStack: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="space-y-3 lg:hidden">
    {children}
  </div>
);

const CrmEmptyState: React.FC<{
  icon: LucideIcon;
  title: string;
  children?: React.ReactNode;
}> = ({ icon: Icon, title, children }) => {
  const { isDark } = useTheme();

  return (
    <div className="col-span-full flex flex-col items-center justify-center py-12 text-center">
      <div className={clsx(
        "w-16 h-16 rounded-2xl flex items-center justify-center mb-4",
        isDark ? "bg-gray-700 text-gray-400" : "bg-slate-100 text-slate-500"
      )}>
        <Icon className="w-8 h-8" />
      </div>
      <h3 className={clsx("text-lg font-semibold mb-2", isDark ? "text-gray-200" : "text-slate-800")}>{title}</h3>
      {children}
    </div>
  );
};

const CrmListMetaLine: React.FC<{
  icon: React.ReactNode;
  children: React.ReactNode;
}> = ({ icon, children }) => {
  const { isDark } = useTheme();

  return (
    <div className={clsx("flex min-w-0 items-center gap-2 text-sm", isDark ? "text-gray-300" : "text-slate-600")}>
      <span className="flex-shrink-0 text-gray-400">{icon}</span>
      <span className="min-w-0 truncate">{children}</span>
    </div>
  );
};

const CrmMobileListItem: React.FC<{
  header?: React.ReactNode;
  title: React.ReactNode;
  children: React.ReactNode;
  actions: React.ReactNode;
}> = ({ header, title, children, actions }) => {
  const { isDark } = useTheme();

  return (
    <div className={clsx(
      "rounded-2xl border p-3 shadow-sm",
      isDark ? "border-gray-700 bg-gray-800" : "border-slate-200 bg-white",
      isDark && "[&_.text-gray-900]:!text-gray-100 [&_.text-gray-800]:!text-gray-100 [&_.text-gray-700]:!text-gray-300 [&_.text-gray-600]:!text-gray-400 [&_.text-gray-500]:!text-gray-400"
    )}>
      <div className="mb-3">
        <div className="min-w-0">
          {header && <div className="mb-1 flex flex-wrap items-center gap-2">{header}</div>}
          <div className={clsx("min-w-0 text-base font-medium", isDark ? "text-white" : "text-slate-900")}>{title}</div>
        </div>
      </div>
      <div className="space-y-2">{children}</div>
      <div className={clsx("mt-3 border-t pt-3", isDark ? "border-gray-700" : "border-slate-100")}>{actions}</div>
    </div>
  );
};

const CrmMediaPreview: React.FC<{
  sourceId?: string | null;
  thumbnailUrl?: string | null;
}> = ({ sourceId, thumbnailUrl }) => {
  const { isDark } = useTheme();
  const [imageFailed, setImageFailed] = useState(false);
  const source = sourceId?.trim();
  const thumbnail = thumbnailUrl?.trim();

  if (!source) return null;

  const showThumbnail = Boolean(thumbnail && !imageFailed);

  return (
    <div className="flex min-w-0 items-center gap-2">
      <div className={clsx(
        "h-9 w-9 flex-shrink-0 overflow-hidden rounded-xl border",
        isDark ? "border-gray-700 bg-gray-700" : "border-slate-200 bg-slate-100"
      )}>
        {showThumbnail ? (
          <img
            src={thumbnail}
            alt={source}
            className="h-full w-full object-cover"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <div className={clsx(
            "flex h-full w-full items-center justify-center",
            isDark ? "bg-sminy-primary/15 text-sminy-primary" : "bg-sminy-primary/10 text-sminy-primary"
          )}>
            <Target className="h-4 w-4" />
          </div>
        )}
      </div>
      <span className={clsx("min-w-0 truncate text-sm", isDark ? "text-gray-300" : "text-slate-700")}>
        {source}
      </span>
    </div>
  );
};

export const CrmMediaField: React.FC<{
  value: string;
  protectedSource?: string | null;
  mediaEditable?: boolean;
  onChange: (value: string) => void;
}> = ({ value, protectedSource, mediaEditable, onChange }) => {
  const isProtected = mediaEditable === false || isProtectedCrmMediaSource(protectedSource);
  const helpTextId = isProtected ? 'crm-media-protected-help' : undefined;

  return (
    <div>
      <label htmlFor="crm-media-input" className="mb-1 block text-sm font-medium text-gray-700">Mídia</label>
      {isProtected ? (
        <input
          id="crm-media-input"
          type="text"
          className="w-full rounded-xl border border-gray-300 bg-gray-100 p-2 text-gray-600 cursor-not-allowed"
          value={value}
          disabled
          aria-describedby={helpTextId}
        />
      ) : (
        <select
          id="crm-media-input"
          className="w-full rounded-xl border border-gray-300 p-2 text-gray-700 focus:border-sminy-primary focus:ring-sminy-primary"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          aria-describedby={helpTextId}
        >
          <option value="">Selecione uma mídia</option>
          {CRM_SOURCE_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
          {value && !CRM_SOURCE_OPTIONS.includes(value) && (
            <option value={value}>{value}</option>
          )}
        </select>
      )}
      {isProtected && (
        <p id={helpTextId} className="mt-1 text-xs text-gray-500">
          Mídia de lead originado por anúncio: não pode ser editada.
        </p>
      )}
    </div>
  );
};

const CRM_LIST_ICON_ACTION_CLASS = "h-9 w-9 !p-0";

const CrmListActionsGroup: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isDark } = useTheme();

  return (
    <div className={clsx(
      "inline-flex max-w-full items-center justify-end gap-1.5 rounded-xl border p-1",
      isDark ? "border-gray-700 bg-gray-900/35" : "border-slate-200 bg-slate-50/80"
    )}>
      {children}
    </div>
  );
};

const CrmRowActionsMenu: React.FC<{
  children: (close: () => void) => React.ReactNode;
}> = ({ children }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const close = () => setMenuOpen(false);

  return (
    <div className="relative inline-flex" ref={menuRef}>
      <Button
        variant="ghost"
        size="sm"
        className={CRM_LIST_ICON_ACTION_CLASS}
        icon={<MoreVertical className="h-4 w-4" />}
        onClick={() => setMenuOpen((prev) => !prev)}
        title="Mais ações"
        aria-label="Opções" aria-expanded={menuOpen}
      />

      {menuOpen && (
        <DropdownMenu anchorRef={menuRef} onClose={() => setMenuOpen(false)}>
          {children(close)}
        </DropdownMenu>
      )}
    </div>
  );
};

const LeadListActions: React.FC<{
  lead: Lead;
  isScheduled: boolean;
  onEdit: (lead: Lead) => void;
  onCreateAppointment: (lead: Lead) => void;
  onDelete: (lead: Lead) => void;
  onOpenChat: OpenChatHandler<Lead>;
  onManageTags: () => void;
}> = ({
  lead,
  isScheduled,
  onEdit,
  onCreateAppointment,
  onDelete,
  onOpenChat,
  onManageTags,
}) => {
  return (
    <CrmListActionsGroup>
      <Button
        variant="ghost"
        size="sm"
        className={CRM_LIST_ICON_ACTION_CLASS}
        icon={<MessageCircle className="h-4 w-4" />}
        onClick={() => onOpenChat(lead)}
        disabled={!lead.phone}
        title={!lead.phone ? 'Telefone não informado' : 'Abrir no chat'}
        aria-label="Abrir no chat"
      />
      <Button
        variant={isScheduled ? 'secondary' : 'ghost'}
        size="sm"
        className={clsx(CRM_LIST_ICON_ACTION_CLASS, !isScheduled && "text-sminy-primary hover:bg-sminy-primary/10 hover:text-sminy-primary")}
        icon={<Calendar className="h-4 w-4" />}
        onClick={() => onCreateAppointment(lead)}
        disabled={isScheduled}
        title={isScheduled ? 'Lead já agendado' : 'Agendar consulta'}
        aria-label={isScheduled ? 'Lead já agendado' : 'Agendar consulta'}
      />
      <CrmRowActionsMenu>
        {(close) => (
          <>
          <CrmContactMenuItems contact={lead} stage="leads" close={close} />
            <DropdownMenuItem
              icon={<FileText className="h-4 w-4" />}
              onClick={() => {
                close();
                onEdit(lead);
              }}
            >
              Editar
            </DropdownMenuItem>
            <DropdownMenuItem
              icon={<Hash className="h-4 w-4" />}
              onClick={() => {
                close();
                onManageTags();
              }}
            >
              Gerenciar Tags
            </DropdownMenuItem>
            <DropdownMenuItem
              icon={<X className="h-4 w-4" />}
              variant="danger"
              onClick={() => {
                close();
                onDelete(lead);
              }}
            >
              Excluir
            </DropdownMenuItem>
          </>
        )}
      </CrmRowActionsMenu>
    </CrmListActionsGroup>
  );
};

const LeadListRow: React.FC<LeadCardProps & { layout: 'desktop' | 'mobile' }> = ({
  lead,
  appointments,
  leadTags,
  onEdit,
  onCreateAppointment,
  onDelete,
  onOpenChat,
  onTagsUpdated,
  layout,
}) => {
  const [showTagsModal, setShowTagsModal] = useState(false);
  const isScheduled = hasAppointment(lead, appointments);
  const isLeadNew = isNewLead(lead.data_entrada);
  const leadDate = lead.data_entrada ? formatNoShowDateTime(lead.data_entrada) : 'Data não informada';

  const handleTagsUpdated = () => {
    onTagsUpdated(lead.id);
  };

  const title = (
    <div className="flex min-w-0 items-center gap-3">
      <CrmContactAvatar name={lead.name} phone={lead.phone} photo={lead.photo} size="sm" />
      <div className="min-w-0">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="min-w-0 truncate">{lead.name || 'Nome não informado'}</span>
          {isLeadNew && <Badge variant="success" className="animate-pulse">Novo</Badge>}
          {isScheduled && <SimpleStatusTag status="agendado" />}
          <PatientStatusBadge isPatient={lead.is_patient} compact />
        </div>
        <div className="mt-0.5 truncate text-xs font-normal text-gray-500">{leadDate}</div>
      </div>
    </div>
  );

  const actions = (
    <LeadListActions
      lead={lead}
      isScheduled={isScheduled}
      onEdit={onEdit}
      onCreateAppointment={onCreateAppointment}
      onDelete={onDelete}
      onOpenChat={onOpenChat}
      onManageTags={() => setShowTagsModal(true)}
    />
  );

  const modal = (
    <TagsModal
      isOpen={showTagsModal}
      onClose={() => setShowTagsModal(false)}
      leadId={lead.id}
      leadName={lead.name}
      currentTags={leadTags?.tags?.map(t => t.id) || []}
      onTagsUpdated={handleTagsUpdated}
    />
  );

  if (layout === 'mobile') {
    return (
      <>
        <CrmMobileListItem title={title} actions={actions}>
          <CrmListMetaLine icon={<Phone className="h-4 w-4" />}>{lead.phone || 'Telefone não informado'}</CrmListMetaLine>
          <CrmMediaPreview sourceId={lead.source_id} thumbnailUrl={lead.thumbnail_url} />
          <TagsDisplay tags={leadTags} />
        </CrmMobileListItem>
        {modal}
      </>
    );
  }

  return (
    <>
      <tr className="hover:bg-gray-50">
        <td className="min-w-0 px-4 py-3 align-top">
          <div className="min-w-0 space-y-1 overflow-hidden">
            <div className="min-w-0 text-sm font-medium text-gray-900">{title}</div>
          </div>
        </td>
        <td className="min-w-0 px-4 py-3 align-top text-sm text-gray-700">
          <div className="truncate">{lead.phone || 'Telefone não informado'}</div>
        </td>
        <td className="min-w-0 px-4 py-3 align-top text-sm text-gray-700">
          <CrmMediaPreview sourceId={lead.source_id} thumbnailUrl={lead.thumbnail_url} />
        </td>
        <td className="min-w-0 px-4 py-3 align-top">
          <TagsDisplay tags={leadTags} compact />
        </td>
        <td className="px-4 py-3 align-top text-right">
          {actions}
          {modal}
        </td>
      </tr>
    </>
  );
};

interface LeadsListViewProps {
  leads: Lead[];
  appointments: AgendamentoResponse[];
  tagsCache: Record<number, LeadTagsSummary>;
  onEdit: (lead: Lead) => void;
  onCreateAppointment: (lead: Lead) => void;
  onDelete: (lead: Lead) => void;
  onOpenChat: OpenChatHandler<Lead>;
  onTagsUpdated: (leadId: number) => void;
}

const LeadsListView: React.FC<LeadsListViewProps> = ({
  leads,
  appointments,
  tagsCache,
  onEdit,
  onCreateAppointment,
  onDelete,
  onOpenChat,
  onTagsUpdated,
}) => {
  const columns: CrmListColumn[] = [
    { label: 'Lead', className: 'w-[30%]' },
    { label: 'Telefone', className: 'w-[18%]' },
    { label: 'Mídia', className: 'w-[15%]' },
    { label: 'Tags', className: 'w-[20%]' },
    { label: 'Ações', className: 'w-[17%]', align: 'right' },
  ];

  return (
    <>
      <CrmListTable columns={columns}>
        {leads.map((lead) => (
          <LeadListRow
            key={lead.id}
            layout="desktop"
            lead={lead}
            appointments={appointments}
            leadTags={tagsCache[lead.id]}
            onEdit={onEdit}
            onCreateAppointment={onCreateAppointment}
            onDelete={onDelete}
            onOpenChat={onOpenChat}
            onTagsUpdated={onTagsUpdated}
          />
        ))}
      </CrmListTable>
      <CrmMobileListStack>
        {leads.map((lead) => (
          <LeadListRow
            key={lead.id}
            layout="mobile"
            lead={lead}
            appointments={appointments}
            leadTags={tagsCache[lead.id]}
            onEdit={onEdit}
            onCreateAppointment={onCreateAppointment}
            onDelete={onDelete}
            onOpenChat={onOpenChat}
            onTagsUpdated={onTagsUpdated}
          />
        ))}
      </CrmMobileListStack>
    </>
  );
};

const AppointmentListActions: React.FC<{
  appointment: AgendamentoResponse;
  hasAttendanceRecord: boolean;
  hasNoShowRecord: boolean;
  onAttendanceClick: (appointment: AgendamentoResponse) => void;
  onRescheduleClick: (appointment: AgendamentoResponse) => void;
  onNoShowClick: (appointment: AgendamentoResponse) => void;
  onEditClick: (appointment: AgendamentoResponse) => void;
  onDeleteClick: (appointment: AgendamentoResponse) => void;
  onOpenChat: OpenChatHandler<AgendamentoResponse>;
  onManageTags: () => void;
}> = ({
  appointment,
  hasAttendanceRecord,
  hasNoShowRecord,
  onAttendanceClick,
  onRescheduleClick,
  onNoShowClick,
  onEditClick,
  onDeleteClick,
  onOpenChat,
  onManageTags,
}) => (
  <CrmListActionsGroup>
    <Button
      variant="ghost"
      size="sm"
      className={CRM_LIST_ICON_ACTION_CLASS}
      icon={<MessageCircle className="h-4 w-4" />}
      onClick={() => onOpenChat(appointment)}
      disabled={!appointment.phone}
      title={!appointment.phone ? 'Telefone não informado' : 'Abrir no chat'}
      aria-label="Abrir no chat"
    />
    {hasNoShowRecord ? (
      <Button
        variant="secondary"
        size="sm"
        className={CRM_LIST_ICON_ACTION_CLASS}
        icon={<XCircle className="h-4 w-4" />}
        disabled
        title="Falta já registrada"
        aria-label="Falta já registrada"
      />
    ) : hasAttendanceRecord ? (
      <Button
        variant="secondary"
        size="sm"
        className={CRM_LIST_ICON_ACTION_CLASS}
        icon={<CheckCircle2 className="h-4 w-4" />}
        disabled
        title="Comparecimento já registrado"
        aria-label="Comparecimento já registrado"
      />
    ) : (
      <>
        <Button
          variant="success"
          size="sm"
          className={CRM_LIST_ICON_ACTION_CLASS}
          icon={<CheckCircle2 className="h-4 w-4" />}
          onClick={() => onAttendanceClick(appointment)}
          title="Marcar comparecimento"
          aria-label="Marcar comparecimento"
        />
        <Button
          variant="danger"
          size="sm"
          className={CRM_LIST_ICON_ACTION_CLASS}
          icon={<XCircle className="h-4 w-4" />}
          onClick={() => onNoShowClick(appointment)}
          title="Marcar falta"
          aria-label="Marcar falta"
        />
      </>
    )}
    <CrmRowActionsMenu>
      {(close) => (
        <>
          <CrmContactMenuItems contact={appointment} stage="appointments" close={close} />
          <DropdownMenuItem
            icon={<RefreshCw className="h-4 w-4" />}
            onClick={() => {
              close();
              onRescheduleClick(appointment);
            }}
          >
            Reagendar
          </DropdownMenuItem>
          <DropdownMenuItem
            icon={<FileText className="h-4 w-4" />}
            onClick={() => {
              close();
              onEditClick(appointment);
            }}
          >
            Editar
          </DropdownMenuItem>
          <DropdownMenuItem
            icon={<Hash className="h-4 w-4" />}
            onClick={() => {
              close();
              onManageTags();
            }}
          >
            Gerenciar Tags
          </DropdownMenuItem>
          <DropdownMenuItem
            icon={<X className="h-4 w-4" />}
            variant="danger"
            onClick={() => {
              close();
              onDeleteClick(appointment);
            }}
          >
            Excluir
          </DropdownMenuItem>
        </>
      )}
    </CrmRowActionsMenu>
  </CrmListActionsGroup>
);

const AppointmentListRow: React.FC<AppointmentCardProps & { layout: 'desktop' | 'mobile' }> = ({
  appointment,
  attendances,
  noShows,
  appointmentTags,
  onAttendanceClick,
  onRescheduleClick,
  onNoShowClick,
  onEditClick,
  onDeleteClick,
  onOpenChat,
  onTagsUpdated,
  layout,
}) => {
  const [showTagsModal, setShowTagsModal] = useState(false);
  const hasAttendanceRecord = hasAttendance(appointment, attendances);
  const hasNoShowRecord = hasNoShow(appointment, noShows);
  const consultationDate = appointment.consulta_data
    ? formatDateTime(appointment.consulta_data)
    : 'Data não agendada';

  const handleTagsUpdated = () => {
    onTagsUpdated(appointment.lead_id);
  };

  const statusTags = (
    <>
      {hasAttendanceRecord && <SimpleStatusTag status="compareceu" />}
      {hasNoShowRecord && <SimpleStatusTag status="faltou" />}
      <PatientStatusBadge isPatient={appointment.is_patient} compact />
    </>
  );

  const syncTags = (
    <>
      <SyncStatusTag status={appointment.google_sync_status} provider="google" />
      <SyncStatusTag status={appointment.clinicorp_sync_status} provider="clinicorp" />
    </>
  );

  const title = (
    <div className="flex min-w-0 items-center gap-3">
      <CrmContactAvatar name={appointment.nome} phone={appointment.phone} photo={appointment.photo} size="sm" />
      <div className="min-w-0">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="min-w-0 truncate">{appointment.nome || 'Nome não informado'}</span>
          {statusTags}
        </div>
        <div className="mt-1 flex min-w-0 flex-wrap items-center gap-1 text-xs font-normal text-gray-500">
          <span>{formatTimeAgo(appointment.agendamento_realizado_em)}</span>
          {syncTags}
        </div>
      </div>
    </div>
  );

  const actions = (
    <AppointmentListActions
      appointment={appointment}
      hasAttendanceRecord={hasAttendanceRecord}
      hasNoShowRecord={hasNoShowRecord}
      onAttendanceClick={onAttendanceClick}
      onRescheduleClick={onRescheduleClick}
      onNoShowClick={onNoShowClick}
      onEditClick={onEditClick}
      onDeleteClick={onDeleteClick}
      onOpenChat={onOpenChat}
      onManageTags={() => setShowTagsModal(true)}
    />
  );

  const modal = (
    <TagsModal
      isOpen={showTagsModal}
      onClose={() => setShowTagsModal(false)}
      leadId={appointment.lead_id}
      leadName={appointment.nome}
      currentTags={appointmentTags?.tags?.map(t => t.id) || []}
      onTagsUpdated={handleTagsUpdated}
    />
  );

  if (layout === 'mobile') {
    return (
      <>
        <CrmMobileListItem title={title} actions={actions}>
          <CrmListMetaLine icon={<Calendar className="h-4 w-4" />}>{consultationDate}</CrmListMetaLine>
          <CrmListMetaLine icon={<Phone className="h-4 w-4" />}>{appointment.phone || 'Telefone não informado'}</CrmListMetaLine>
          {appointment.midia && (
            <CrmListMetaLine icon={<Target className="h-4 w-4" />}>Mídia: {appointment.midia}</CrmListMetaLine>
          )}
          {appointment.interesse && (
            <CrmListMetaLine icon={<Info className="h-4 w-4" />}>Interesse: {appointment.interesse}</CrmListMetaLine>
          )}
          <TagsDisplay tags={appointmentTags} />
        </CrmMobileListItem>
        {modal}
      </>
    );
  }

  return (
    <>
      <tr className="hover:bg-gray-50">
        <td className="px-4 py-3 align-top">
          <div className="space-y-1">
            <div className="text-sm font-medium text-gray-900">{title}</div>
          </div>
        </td>
        <td className="px-4 py-3 align-top text-sm text-gray-700">
          <div className="truncate">{consultationDate}</div>
        </td>
        <td className="px-4 py-3 align-top text-sm text-gray-700">
          <div className="truncate">{appointment.phone || 'Telefone não informado'}</div>
          {appointment.midia && <div className="truncate text-xs text-gray-500">Mídia: {appointment.midia}</div>}
          {appointment.interesse && <div className="truncate text-xs text-gray-500">Interesse: {appointment.interesse}</div>}
        </td>
        <td className="px-4 py-3 align-top">
          <TagsDisplay tags={appointmentTags} />
        </td>
        <td className="px-4 py-3 align-top text-right">
          {actions}
          {modal}
        </td>
      </tr>
    </>
  );
};

interface AppointmentsListViewProps {
  appointments: AgendamentoResponse[];
  attendances: Comparecimento[];
  noShows: NoShowEvent[];
  tagsCache: Record<number, LeadTagsSummary>;
  onAttendanceClick: (appointment: AgendamentoResponse) => void;
  onRescheduleClick: (appointment: AgendamentoResponse) => void;
  onNoShowClick: (appointment: AgendamentoResponse) => void;
  onEditClick: (appointment: AgendamentoResponse) => void;
  onDeleteClick: (appointment: AgendamentoResponse) => void;
  onOpenChat: OpenChatHandler<AgendamentoResponse>;
  onTagsUpdated: (leadId: number) => void;
}

const AppointmentsListView: React.FC<AppointmentsListViewProps> = ({
  appointments,
  attendances,
  noShows,
  tagsCache,
  onAttendanceClick,
  onRescheduleClick,
  onNoShowClick,
  onEditClick,
  onDeleteClick,
  onOpenChat,
  onTagsUpdated,
}) => {
  const columns: CrmListColumn[] = [
    { label: 'Paciente', className: 'w-[28%]' },
    { label: 'Consulta', className: 'w-[18%]' },
    { label: 'Contato/Origem', className: 'w-[18%]' },
    { label: 'Tags', className: 'w-[16%]' },
    { label: 'Ações', className: 'w-[20%]', align: 'right' },
  ];

  return (
    <>
      <CrmListTable columns={columns}>
        {appointments.map((appointment) => (
          <AppointmentListRow
            key={appointment.id}
            layout="desktop"
            appointment={appointment}
            attendances={attendances}
            noShows={noShows}
            appointmentTags={tagsCache[appointment.lead_id]}
            onAttendanceClick={onAttendanceClick}
            onRescheduleClick={onRescheduleClick}
            onNoShowClick={onNoShowClick}
            onEditClick={onEditClick}
            onDeleteClick={onDeleteClick}
            onOpenChat={onOpenChat}
            onTagsUpdated={onTagsUpdated}
          />
        ))}
      </CrmListTable>
      <CrmMobileListStack>
        {appointments.map((appointment) => (
          <AppointmentListRow
            key={appointment.id}
            layout="mobile"
            appointment={appointment}
            attendances={attendances}
            noShows={noShows}
            appointmentTags={tagsCache[appointment.lead_id]}
            onAttendanceClick={onAttendanceClick}
            onRescheduleClick={onRescheduleClick}
            onNoShowClick={onNoShowClick}
            onEditClick={onEditClick}
            onDeleteClick={onDeleteClick}
            onOpenChat={onOpenChat}
            onTagsUpdated={onTagsUpdated}
          />
        ))}
      </CrmMobileListStack>
    </>
  );
};

const NoShowListActions: React.FC<{
  noShow: NoShowEvent;
  onRescheduleClick: (noShow: NoShowEvent) => void;
  onEditClick: (noShow: NoShowEvent) => void;
  onDeleteClick: (noShow: NoShowEvent) => void;
  onOpenChat: OpenChatHandler<NoShowEvent>;
  onManageTags: () => void;
}> = ({ noShow, onRescheduleClick, onEditClick, onDeleteClick, onOpenChat, onManageTags }) => (
  <CrmListActionsGroup>
    <Button
      variant="ghost"
      size="sm"
      className={CRM_LIST_ICON_ACTION_CLASS}
      icon={<MessageCircle className="h-4 w-4" />}
      onClick={() => onOpenChat(noShow)}
      disabled={!noShow.phone}
      title={!noShow.phone ? 'Telefone não informado' : 'Abrir no chat'}
      aria-label="Abrir no chat"
    />
    <CrmRowActionsMenu>
      {(close) => (
        <>
          <CrmContactMenuItems contact={noShow} stage="noShows" close={close} />
          <DropdownMenuItem
            icon={<RefreshCw className="h-4 w-4" />}
            onClick={() => {
              close();
              onRescheduleClick(noShow);
            }}
          >
            Reagendar
          </DropdownMenuItem>
          <DropdownMenuItem
            icon={<FileText className="h-4 w-4" />}
            onClick={() => {
              close();
              onEditClick(noShow);
            }}
          >
            Editar Falta
          </DropdownMenuItem>
          <DropdownMenuItem
            icon={<Hash className="h-4 w-4" />}
            onClick={() => {
              close();
              onManageTags();
            }}
          >
            Gerenciar Tags
          </DropdownMenuItem>
          <DropdownMenuItem
            icon={<X className="h-4 w-4" />}
            variant="danger"
            onClick={() => {
              close();
              onDeleteClick(noShow);
            }}
          >
            Excluir Falta
          </DropdownMenuItem>
        </>
      )}
    </CrmRowActionsMenu>
  </CrmListActionsGroup>
);

const NoShowListRow: React.FC<NoShowCardProps & { layout: 'desktop' | 'mobile' }> = ({
  noShow,
  noShowTags,
  onRescheduleClick,
  onEditClick,
  onDeleteClick,
  onOpenChat,
  onTagsUpdated,
  layout,
}) => {
  const [showTagsModal, setShowTagsModal] = useState(false);

  const handleTagsUpdated = () => {
    onTagsUpdated(noShow.lead_id);
  };

  const title = (
    <div className="flex min-w-0 items-center gap-3">
      <CrmContactAvatar name={noShow.nome} phone={noShow.phone} photo={noShow.photo} size="sm" />
      <div className="min-w-0">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="min-w-0 truncate">{noShow.nome || 'Nome não informado'}</span>
          <PatientStatusBadge isPatient={noShow.is_patient} compact />
        </div>
        <div className="mt-0.5 truncate text-xs font-normal text-gray-500">{noShow.phone || 'Telefone não informado'}</div>
      </div>
    </div>
  );

  const actions = (
    <NoShowListActions
      noShow={noShow}
      onRescheduleClick={onRescheduleClick}
      onEditClick={onEditClick}
      onDeleteClick={onDeleteClick}
      onOpenChat={onOpenChat}
      onManageTags={() => setShowTagsModal(true)}
    />
  );

  const modal = (
    <TagsModal
      isOpen={showTagsModal}
      onClose={() => setShowTagsModal(false)}
      leadId={noShow.lead_id}
      leadName={noShow.nome || `Lead #${noShow.lead_id}`}
      currentTags={noShowTags?.manual_tags?.map(t => t.id) || []}
      onTagsUpdated={handleTagsUpdated}
    />
  );

  if (layout === 'mobile') {
    return (
      <>
        <CrmMobileListItem title={title} actions={actions}>
          <CrmListMetaLine icon={<Clock className="h-4 w-4" />}>Marcado em: {formatNoShowDateTime(noShow.marcado_em)}</CrmListMetaLine>
          {noShow.data_agendada && (
            <CrmListMetaLine icon={<Calendar className="h-4 w-4" />}>Data Agendada: {formatDateTime(noShow.data_agendada)}</CrmListMetaLine>
          )}
          {noShow.observacao && (
            <CrmListMetaLine icon={<Info className="h-4 w-4" />}>Observação: {noShow.observacao}</CrmListMetaLine>
          )}
          <TagsDisplay tags={noShowTags} />
        </CrmMobileListItem>
        {modal}
      </>
    );
  }

  return (
    <>
      <tr className="hover:bg-gray-50">
        <td className="px-4 py-3 align-top">
          <div className="space-y-1">
            <div className="text-sm font-medium text-gray-900">{title}</div>
          </div>
        </td>
        <td className="px-4 py-3 align-top text-sm text-gray-700">
          <div className="truncate">{formatNoShowDateTime(noShow.marcado_em)}</div>
        </td>
        <td className="px-4 py-3 align-top text-sm text-gray-700">
          <div className="truncate">{noShow.data_agendada ? formatDateTime(noShow.data_agendada) : 'Data não informada'}</div>
          {noShow.observacao && <div className="truncate text-xs text-gray-500">Obs: {noShow.observacao}</div>}
        </td>
        <td className="px-4 py-3 align-top">
          <TagsDisplay tags={noShowTags} />
        </td>
        <td className="px-4 py-3 align-top text-right">
          {actions}
          {modal}
        </td>
      </tr>
    </>
  );
};

interface NoShowsListViewProps {
  noShows: NoShowEvent[];
  tagsCache: Record<number, LeadTagsSummary>;
  onRescheduleClick: (noShow: NoShowEvent) => void;
  onEditClick: (noShow: NoShowEvent) => void;
  onDeleteClick: (noShow: NoShowEvent) => void;
  onOpenChat: OpenChatHandler<NoShowEvent>;
  onTagsUpdated: (leadId: number) => void;
}

const NoShowsListView: React.FC<NoShowsListViewProps> = ({
  noShows,
  tagsCache,
  onRescheduleClick,
  onEditClick,
  onDeleteClick,
  onOpenChat,
  onTagsUpdated,
}) => {
  const columns: CrmListColumn[] = [
    { label: 'Paciente', className: 'w-[28%]' },
    { label: 'Falta', className: 'w-[18%]' },
    { label: 'Agendamento', className: 'w-[22%]' },
    { label: 'Tags', className: 'w-[18%]' },
    { label: 'Ações', className: 'w-[14%]', align: 'right' },
  ];

  return (
    <>
      <CrmListTable columns={columns}>
        {noShows.map((noShow) => (
          <NoShowListRow
            key={noShow.id}
            layout="desktop"
            noShow={noShow}
            noShowTags={tagsCache[noShow.lead_id]}
            onRescheduleClick={onRescheduleClick}
            onEditClick={onEditClick}
            onDeleteClick={onDeleteClick}
            onOpenChat={onOpenChat}
            onTagsUpdated={onTagsUpdated}
          />
        ))}
      </CrmListTable>
      <CrmMobileListStack>
        {noShows.map((noShow) => (
          <NoShowListRow
            key={noShow.id}
            layout="mobile"
            noShow={noShow}
            noShowTags={tagsCache[noShow.lead_id]}
            onRescheduleClick={onRescheduleClick}
            onEditClick={onEditClick}
            onDeleteClick={onDeleteClick}
            onOpenChat={onOpenChat}
            onTagsUpdated={onTagsUpdated}
          />
        ))}
      </CrmMobileListStack>
    </>
  );
};

const AttendanceListActions: React.FC<{
  attendance: Comparecimento;
  hasSaleRecord: boolean;
  onSaleClick: (attendance: Comparecimento) => void;
  onEditClick: (attendance: Comparecimento) => void;
  onDeleteClick: (attendance: Comparecimento) => void;
  onOpenChat: OpenChatHandler<Comparecimento>;
  onManageTags: () => void;
}> = ({
  attendance,
  hasSaleRecord,
  onSaleClick,
  onEditClick,
  onDeleteClick,
  onOpenChat,
  onManageTags,
}) => (
  <CrmListActionsGroup>
    <Button
      variant="ghost"
      size="sm"
      className={CRM_LIST_ICON_ACTION_CLASS}
      icon={<MessageCircle className="h-4 w-4" />}
      onClick={() => onOpenChat(attendance)}
      disabled={!attendance.phone}
      title={!attendance.phone ? 'Telefone não informado' : 'Abrir no chat'}
      aria-label="Abrir no chat"
    />
    <Button
      variant={hasSaleRecord ? 'secondary' : 'success'}
      size="sm"
      className={CRM_LIST_ICON_ACTION_CLASS}
      icon={<DollarSign className="h-4 w-4" />}
      onClick={() => onSaleClick(attendance)}
      disabled={hasSaleRecord}
      title={hasSaleRecord ? 'Venda já registrada' : 'Registrar venda'}
      aria-label={hasSaleRecord ? 'Venda já registrada' : 'Registrar venda'}
    />
    <CrmRowActionsMenu>
      {(close) => (
        <>
          <CrmContactMenuItems contact={attendance} stage="attendances" close={close} />
          <DropdownMenuItem
            icon={<FileText className="h-4 w-4" />}
            onClick={() => {
              close();
              onEditClick(attendance);
            }}
          >
            Editar
          </DropdownMenuItem>
          <DropdownMenuItem
            icon={<Hash className="h-4 w-4" />}
            onClick={() => {
              close();
              onManageTags();
            }}
          >
            Gerenciar Tags
          </DropdownMenuItem>
          <DropdownMenuItem
            icon={<X className="h-4 w-4" />}
            variant="danger"
            onClick={() => {
              close();
              onDeleteClick(attendance);
            }}
          >
            Excluir
          </DropdownMenuItem>
        </>
      )}
    </CrmRowActionsMenu>
  </CrmListActionsGroup>
);

const AttendanceListRow: React.FC<AttendanceCardProps & { layout: 'desktop' | 'mobile' }> = ({
  attendance,
  sales,
  attendanceTags,
  onSaleClick,
  onEditClick,
  onDeleteClick,
  onOpenChat,
  onTagsUpdated,
  layout,
}) => {
  const [showTagsModal, setShowTagsModal] = useState(false);
  const hasSaleRecord = hasSale(attendance, sales);

  const handleTagsUpdated = () => {
    onTagsUpdated(attendance.lead_id);
  };

  const title = (
    <div className="flex min-w-0 items-center gap-3">
      <CrmContactAvatar name={attendance.nome} phone={attendance.phone} photo={attendance.photo} size="sm" />
      <div className="min-w-0">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="min-w-0 truncate">{attendance.nome || 'Nome não informado'}</span>
          {hasSaleRecord && <SimpleStatusTag status="venda" />}
          <PatientStatusBadge isPatient={attendance.is_patient} compact />
        </div>
        <div className="mt-0.5 truncate text-xs font-normal text-gray-500">{attendance.phone || 'Telefone não informado'}</div>
      </div>
    </div>
  );

  const actions = (
    <AttendanceListActions
      attendance={attendance}
      hasSaleRecord={hasSaleRecord}
      onSaleClick={onSaleClick}
      onEditClick={onEditClick}
      onDeleteClick={onDeleteClick}
      onOpenChat={onOpenChat}
      onManageTags={() => setShowTagsModal(true)}
    />
  );

  const modal = (
    <TagsModal
      isOpen={showTagsModal}
      onClose={() => setShowTagsModal(false)}
      leadId={attendance.lead_id}
      leadName={attendance.nome || `Lead #${attendance.lead_id}`}
      currentTags={attendanceTags?.tags?.map(t => t.id) || []}
      onTagsUpdated={handleTagsUpdated}
    />
  );

  if (layout === 'mobile') {
    return (
      <>
        <CrmMobileListItem title={title} actions={actions}>
          <CrmListMetaLine icon={<Calendar className="h-4 w-4" />}>{formatDateTime(attendance.compareceu_em)}</CrmListMetaLine>
          {attendance.tratamento_orcado && (
            <CrmListMetaLine icon={<Info className="h-4 w-4" />}>Tratamento Orçado: {attendance.tratamento_orcado}</CrmListMetaLine>
          )}
          {attendance.valor_orcamento && (
            <CrmListMetaLine icon={<DollarSign className="h-4 w-4" />}>Valor do Orçamento: {formatCurrencyBRL(attendance.valor_orcamento)}</CrmListMetaLine>
          )}
          <TagsDisplay tags={attendanceTags} />
        </CrmMobileListItem>
        {modal}
      </>
    );
  }

  return (
    <>
      <tr className="hover:bg-gray-50">
        <td className="px-4 py-3 align-top">
          <div className="space-y-1">
            <div className="text-sm font-medium text-gray-900">{title}</div>
          </div>
        </td>
        <td className="px-4 py-3 align-top text-sm text-gray-700">
          <div className="truncate">{formatDateTime(attendance.compareceu_em)}</div>
          <div className="text-xs text-gray-500">{formatTimeAgo(attendance.compareceu_em)}</div>
        </td>
        <td className="px-4 py-3 align-top text-sm text-gray-700">
          <div className="truncate">{attendance.tratamento_orcado || 'Tratamento não informado'}</div>
          {attendance.valor_orcamento && (
            <div className="truncate text-xs text-gray-500">{formatCurrencyBRL(attendance.valor_orcamento)}</div>
          )}
        </td>
        <td className="px-4 py-3 align-top">
          <TagsDisplay tags={attendanceTags} />
        </td>
        <td className="px-4 py-3 align-top text-right">
          {actions}
          {modal}
        </td>
      </tr>
    </>
  );
};

interface AttendancesListViewProps {
  attendances: Comparecimento[];
  sales: Venda[];
  tagsCache: Record<number, LeadTagsSummary>;
  onSaleClick: (attendance: Comparecimento) => void;
  onEditClick: (attendance: Comparecimento) => void;
  onDeleteClick: (attendance: Comparecimento) => void;
  onOpenChat: OpenChatHandler<Comparecimento>;
  onTagsUpdated: (leadId: number) => void;
}

const AttendancesListView: React.FC<AttendancesListViewProps> = ({
  attendances,
  sales,
  tagsCache,
  onSaleClick,
  onEditClick,
  onDeleteClick,
  onOpenChat,
  onTagsUpdated,
}) => {
  const columns: CrmListColumn[] = [
    { label: 'Paciente', className: 'w-[28%]' },
    { label: 'Comparecimento', className: 'w-[18%]' },
    { label: 'Orçamento', className: 'w-[20%]' },
    { label: 'Tags', className: 'w-[17%]' },
    { label: 'Ações', className: 'w-[17%]', align: 'right' },
  ];

  return (
    <>
      <CrmListTable columns={columns}>
        {attendances.map((attendance) => (
          <AttendanceListRow
            key={attendance.id}
            layout="desktop"
            attendance={attendance}
            sales={sales}
            attendanceTags={tagsCache[attendance.lead_id]}
            onSaleClick={onSaleClick}
            onEditClick={onEditClick}
            onDeleteClick={onDeleteClick}
            onOpenChat={onOpenChat}
            onTagsUpdated={onTagsUpdated}
          />
        ))}
      </CrmListTable>
      <CrmMobileListStack>
        {attendances.map((attendance) => (
          <AttendanceListRow
            key={attendance.id}
            layout="mobile"
            attendance={attendance}
            sales={sales}
            attendanceTags={tagsCache[attendance.lead_id]}
            onSaleClick={onSaleClick}
            onEditClick={onEditClick}
            onDeleteClick={onDeleteClick}
            onOpenChat={onOpenChat}
            onTagsUpdated={onTagsUpdated}
          />
        ))}
      </CrmMobileListStack>
    </>
  );
};

const SaleListActions: React.FC<{
  sale: Venda;
  onNewSaleClick: (sale: Venda) => void;
  onEditClick: (sale: Venda) => void;
  onDeleteClick: (sale: Venda) => void;
  onOpenChat: OpenChatHandler<Venda>;
  onManageTags: () => void;
}> = ({ sale, onNewSaleClick, onEditClick, onDeleteClick, onOpenChat, onManageTags }) => (
  <CrmListActionsGroup>
    <Button
      variant="ghost"
      size="sm"
      className={CRM_LIST_ICON_ACTION_CLASS}
      icon={<MessageCircle className="h-4 w-4" />}
      onClick={() => onOpenChat(sale)}
      disabled={!sale.phone}
      title={!sale.phone ? 'Telefone não informado' : 'Abrir no chat'}
      aria-label="Abrir no chat"
    />
    <CrmRowActionsMenu>
      {(close) => (
        <>
          <CrmContactMenuItems contact={sale} stage="sales" close={close} />
          <DropdownMenuItem
            icon={<PlusCircle className="h-4 w-4" />}
            onClick={() => {
              close();
              onNewSaleClick(sale);
            }}
          >
            Registrar nova venda
          </DropdownMenuItem>
          <DropdownMenuItem
            icon={<FileText className="h-4 w-4" />}
            onClick={() => {
              close();
              onEditClick(sale);
            }}
          >
            Editar
          </DropdownMenuItem>
          <DropdownMenuItem
            icon={<Hash className="h-4 w-4" />}
            onClick={() => {
              close();
              onManageTags();
            }}
          >
            Gerenciar Tags
          </DropdownMenuItem>
          <DropdownMenuItem
            icon={<X className="h-4 w-4" />}
            variant="danger"
            onClick={() => {
              close();
              onDeleteClick(sale);
            }}
          >
            Excluir
          </DropdownMenuItem>
        </>
      )}
    </CrmRowActionsMenu>
  </CrmListActionsGroup>
);

const SaleListRow: React.FC<SaleCardProps & { layout: 'desktop' | 'mobile' }> = ({
  sale,
  saleTags,
  onNewSaleClick,
  onEditClick,
  onDeleteClick,
  onOpenChat,
  onTagsUpdated,
  layout,
}) => {
  const [showTagsModal, setShowTagsModal] = useState(false);

  const handleTagsUpdated = () => {
    onTagsUpdated(sale.lead_id);
  };

  const title = (
    <div className="flex min-w-0 items-center gap-3">
      <CrmContactAvatar name={sale.nome} phone={sale.phone} photo={sale.photo} size="sm" />
      <div className="min-w-0">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="min-w-0 truncate">{sale.nome || 'Nome não informado'}</span>
          <PatientStatusBadge isPatient={sale.is_patient} compact />
        </div>
        <div className="mt-0.5 truncate text-xs font-normal text-gray-500">{sale.phone || 'Telefone não informado'}</div>
      </div>
    </div>
  );

  const actions = (
    <SaleListActions
      sale={sale}
      onNewSaleClick={onNewSaleClick}
      onEditClick={onEditClick}
      onDeleteClick={onDeleteClick}
      onOpenChat={onOpenChat}
      onManageTags={() => setShowTagsModal(true)}
    />
  );

  const modal = (
    <TagsModal
      isOpen={showTagsModal}
      onClose={() => setShowTagsModal(false)}
      leadId={sale.lead_id}
      leadName={sale.nome || `Lead #${sale.lead_id}`}
      currentTags={saleTags?.tags?.map(t => t.id) || []}
      onTagsUpdated={handleTagsUpdated}
    />
  );

  const valorFaturado = sale.valor_faturado !== null && sale.valor_faturado !== undefined
    ? formatCurrencyBRL(sale.valor_faturado)
    : 'Não informado';
  const valorPago = sale.valor_pago !== null && sale.valor_pago !== undefined
    ? formatCurrencyBRL(sale.valor_pago)
    : 'Não informado';

  if (layout === 'mobile') {
    return (
      <>
        <CrmMobileListItem title={title} actions={actions}>
          <CrmListMetaLine icon={<Calendar className="h-4 w-4" />}>{formatDateTime(sale.venda_data)}</CrmListMetaLine>
          {sale.tratamento_fechado && (
            <CrmListMetaLine icon={<Info className="h-4 w-4" />}>Tratamento: {sale.tratamento_fechado}</CrmListMetaLine>
          )}
          <CrmListMetaLine icon={<DollarSign className="h-4 w-4" />}>Faturado: {valorFaturado}</CrmListMetaLine>
          <CrmListMetaLine icon={<DollarSign className="h-4 w-4" />}>Pago: {valorPago}</CrmListMetaLine>
          <TagsDisplay tags={saleTags} />
        </CrmMobileListItem>
        {modal}
      </>
    );
  }

  return (
    <>
      <tr className="hover:bg-gray-50">
        <td className="px-4 py-3 align-top">
          <div className="space-y-1">
            <div className="text-sm font-medium text-gray-900">{title}</div>
          </div>
        </td>
        <td className="px-4 py-3 align-top text-sm text-gray-700">
          <div className="truncate">{formatDateTime(sale.venda_data)}</div>
          <div className="text-xs text-gray-500">{formatTimeAgo(sale.venda_data)}</div>
        </td>
        <td className="px-4 py-3 align-top text-sm text-gray-700">
          <div className="truncate">{sale.tratamento_fechado || 'Tratamento não informado'}</div>
          <div className="truncate text-xs text-gray-500">Faturado: {valorFaturado}</div>
          <div className="truncate text-xs text-gray-500">Pago: {valorPago}</div>
        </td>
        <td className="px-4 py-3 align-top">
          <TagsDisplay tags={saleTags} />
        </td>
        <td className="px-4 py-3 align-top text-right">
          {actions}
          {modal}
        </td>
      </tr>
    </>
  );
};

interface SalesListViewProps {
  sales: Venda[];
  tagsCache: Record<number, LeadTagsSummary>;
  onNewSaleClick: (sale: Venda) => void;
  onEditClick: (sale: Venda) => void;
  onDeleteClick: (sale: Venda) => void;
  onOpenChat: OpenChatHandler<Venda>;
  onTagsUpdated: (leadId: number) => void;
}

export const SalesListView: React.FC<SalesListViewProps> = ({
  sales,
  tagsCache,
  onNewSaleClick,
  onEditClick,
  onDeleteClick,
  onOpenChat,
  onTagsUpdated,
}) => {
  const columns: CrmListColumn[] = [
    { label: 'Paciente', className: 'w-[28%]' },
    { label: 'Venda', className: 'w-[18%]' },
    { label: 'Valores', className: 'w-[24%]' },
    { label: 'Tags', className: 'w-[18%]' },
    { label: 'Ações', className: 'w-[12%]', align: 'right' },
  ];

  return (
    <>
      <CrmListTable columns={columns}>
        {sales.map((sale) => (
          <SaleListRow
            key={sale.id}
            layout="desktop"
            sale={sale}
            saleTags={tagsCache[sale.lead_id]}
            onNewSaleClick={onNewSaleClick}
            onEditClick={onEditClick}
            onDeleteClick={onDeleteClick}
            onOpenChat={onOpenChat}
            onTagsUpdated={onTagsUpdated}
          />
        ))}
      </CrmListTable>
      <CrmMobileListStack>
        {sales.map((sale) => (
          <SaleListRow
            key={sale.id}
            layout="mobile"
            sale={sale}
            saleTags={tagsCache[sale.lead_id]}
            onNewSaleClick={onNewSaleClick}
            onEditClick={onEditClick}
            onDeleteClick={onDeleteClick}
            onOpenChat={onOpenChat}
            onTagsUpdated={onTagsUpdated}
          />
        ))}
      </CrmMobileListStack>
    </>
  );
};

interface LostItemProps {
  item: CrmLoss & { photo?: string; is_patient?: boolean };
  tags?: LeadTagsSummary;
  onOpenChat: (contact: CrmLoss) => void;
  onTagsUpdated: (leadId: number) => void;
}

const LostActionsMenu: React.FC<LostItemProps & { onManageTags: () => void }> = ({ item, onOpenChat, onManageTags }) => {
  const actions = useCrmLossActions();
  const phoneActions = useCrmPhoneCorrection();
  const leadId = getCrmPhoneLeadId(item, 'lost');
  return <CrmRowActionsMenu>{close => <>
    {phoneActions.enabled && <DropdownMenuItem icon={<Phone className="h-4 w-4" />} disabled={!leadId} onClick={() => { if (leadId) phoneActions.edit({ leadId, name: item.name }); close(); }}>Editar telefone</DropdownMenuItem>}
    <DropdownMenuItem icon={<MessageCircle className="h-4 w-4" />} disabled={!item.phone} onClick={() => { close(); onOpenChat(item); }}>Abrir no chat</DropdownMenuItem>
    <DropdownMenuItem icon={<FileText className="h-4 w-4" />} onClick={() => { close(); actions.history(item); }}>Histórico de perdas</DropdownMenuItem>
    <DropdownMenuItem icon={<Hash className="h-4 w-4" />} onClick={() => { close(); onManageTags(); }}>Gerenciar Tags</DropdownMenuItem>
    <DropdownMenuItem icon={<RefreshCw className="h-4 w-4" />} onClick={() => { close(); actions.reopen(item); }}>Reabrir contato</DropdownMenuItem>
  </>}</CrmRowActionsMenu>;
};

const LostItem: React.FC<LostItemProps & { layout: 'card' | 'desktop' | 'mobile' }> = ({ item, tags, onOpenChat, onTagsUpdated, layout }) => {
  const actions = useCrmLossActions();
  const [showTagsModal, setShowTagsModal] = useState(false);
  const date = formatDateTime(item.lost_at);
  const title = <div className="flex min-w-0 items-center gap-3">
    <CrmContactAvatar name={item.name} phone={item.phone} photo={item.photo} size={layout === 'card' ? undefined : 'sm'} />
    <div className="min-w-0">
      {layout === 'card' && <div className="mb-1 text-xs text-gray-500">{date}</div>}
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <span className="min-w-0 truncate">{item.name || 'Nome não informado'}</span>
        <PatientStatusBadge isPatient={item.is_patient} compact={layout !== 'card'} />
      </div>
      {layout !== 'card' && <div className="mt-0.5 truncate text-xs font-normal text-gray-500">{item.phone || 'Telefone não informado'}</div>}
    </div>
  </div>;
  const menu = <LostActionsMenu item={item} onOpenChat={onOpenChat} onTagsUpdated={onTagsUpdated} onManageTags={() => setShowTagsModal(true)} />;
  const listActions = <CrmListActionsGroup>
    <Button variant="ghost" size="sm" className={CRM_LIST_ICON_ACTION_CLASS} icon={<MessageCircle className="h-4 w-4" />} disabled={!item.phone} title="Abrir no chat" aria-label="Abrir no chat" onClick={() => onOpenChat(item)} />
    <Button variant="ghost" size="sm" className={clsx(CRM_LIST_ICON_ACTION_CLASS, 'text-sminy-primary hover:bg-sminy-primary/10 hover:text-sminy-primary')} icon={<RefreshCw className="h-4 w-4" />} title="Reabrir contato" aria-label="Reabrir contato" onClick={() => actions.reopen(item)} />
    {menu}
  </CrmListActionsGroup>;
  const modal = <TagsModal isOpen={showTagsModal} onClose={() => setShowTagsModal(false)} leadId={item.lead_id} leadName={item.name} currentTags={tags?.tags?.map(tag => tag.id) || []} onTagsUpdated={() => onTagsUpdated(item.lead_id)} />;
  const metadata = <>
    <CrmListMetaLine icon={<Target className="h-4 w-4" />}>Origem: {CRM_STAGE_LABELS[item.from_stage]}</CrmListMetaLine>
    <CrmListMetaLine icon={<User className="h-4 w-4" />}>{item.actor_name}</CrmListMetaLine>
    <CrmListMetaLine icon={<UserX className="h-4 w-4" />}>{item.reason}</CrmListMetaLine>
    {item.observation && <p className="break-words whitespace-pre-wrap text-sm text-gray-500">{item.observation}</p>}
    <CrmMediaPreview sourceId={item.source_id} thumbnailUrl={item.thumbnail_url} />
  </>;

  if (layout === 'card') return <Card>
    <div className="mb-3 flex items-start justify-between gap-3">
      <div className="min-w-0 text-base font-medium text-gray-900">{title}</div>
      {menu}
    </div>
    <div className="space-y-3 text-sm overflow-y-auto max-h-[160px] pb-2">
      <CrmListMetaLine icon={<Phone className="h-4 w-4" />}>{item.phone || 'Telefone não informado'}</CrmListMetaLine>
      {metadata}
    </div>
    <div className="pb-14"><TagsDisplay tags={tags} /></div>
    <div className="absolute bottom-4 left-4 right-4 pt-3 border-t border-gray-100">
      <Button variant="ghost" size="sm" className="w-full justify-center text-sminy-primary" icon={<RefreshCw className="h-4 w-4" />} onClick={() => actions.reopen(item)}>Reabrir contato</Button>
    </div>
    {modal}
  </Card>;

  if (layout === 'mobile') return <>
    <CrmMobileListItem title={title} actions={listActions}>
      <CrmListMetaLine icon={<Clock className="h-4 w-4" />}>{date}</CrmListMetaLine>
      {metadata}
      <TagsDisplay tags={tags} />
    </CrmMobileListItem>
    {modal}
  </>;

  return <tr className="hover:bg-gray-50">
    <td className="min-w-0 px-4 py-3 align-top"><div className="min-w-0 text-sm font-medium text-gray-900">{title}</div></td>
    <td className="min-w-0 px-4 py-3 align-top text-sm text-gray-700"><div>{date}</div><div className="mt-1 text-xs text-gray-500">{CRM_STAGE_LABELS[item.from_stage]}</div></td>
    <td className="min-w-0 px-4 py-3 align-top text-sm text-gray-700"><div className="truncate" title={item.actor_name}>{item.actor_name}</div></td>
    <td className="min-w-0 px-4 py-3 align-top text-sm text-gray-700"><div>{item.reason}</div>{item.observation && <div className="mt-1 truncate text-xs text-gray-500" title={item.observation}>{item.observation}</div>}</td>
    <td className="min-w-0 px-4 py-3 align-top"><CrmMediaPreview sourceId={item.source_id} thumbnailUrl={item.thumbnail_url} /></td>
    <td className="min-w-0 px-4 py-3 align-top"><TagsDisplay tags={tags} compact /></td>
    <td className="px-4 py-3 align-top text-right">{listActions}{modal}</td>
  </tr>;
};

export const CrmLostView: React.FC<{ items: CrmLoss[]; mode: 'cards' | 'list'; tagsCache: Record<number, LeadTagsSummary>; onOpenChat: (contact: CrmLoss) => void; onTagsUpdated: (leadId: number) => void }> = ({ items, mode, tagsCache, onOpenChat, onTagsUpdated }) => {
  if (!items.length) return <CrmEmptyState icon={UserX} title="Nenhum contato perdido encontrado" />;
  const renderItem = (item: CrmLoss, layout: 'card' | 'desktop' | 'mobile') => <LostItem key={item.id} item={item} layout={layout} tags={tagsCache[item.lead_id]} onOpenChat={onOpenChat} onTagsUpdated={onTagsUpdated} />;
  if (mode === 'cards') return <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">{items.map(item => renderItem(item, 'card'))}</div>;
  const columns: CrmListColumn[] = [
    { label: 'Contato', className: 'w-[21%]' },
    { label: 'Perda / origem', className: 'w-[16%]' },
    { label: 'Responsável', className: 'w-[13%]' },
    { label: 'Motivo', className: 'w-[14%]' },
    { label: 'Mídia', className: 'w-[11%]' },
    { label: 'Tags', className: 'w-[12%]' },
    // 3 botões de 36px + gaps/borda/padding do grupo + padding da célula.
    { label: 'Ações', className: 'w-[162px]', align: 'right' },
  ];
  return <>
    <CrmListTable columns={columns}>{items.map(item => renderItem(item, 'desktop'))}</CrmListTable>
    <CrmMobileListStack>{items.map(item => renderItem(item, 'mobile'))}</CrmMobileListStack>
  </>;
};

// ========================
//   FORMS DE EDIÇÃO
// ========================

// ---------- AppointmentEditForm -----------
interface AppointmentEditFormProps {
  appointment: AgendamentoResponse;
  treatmentOptions: string[];
  isLoadingTreatmentOptions: boolean;
  error: string | null;
  onClearError: () => void;
  onClose: () => void;
  onSave: (id: number, data: AgendamentoUpdate) => void;
}

const AppointmentEditForm: React.FC<AppointmentEditFormProps> = ({
  appointment,
  treatmentOptions,
  isLoadingTreatmentOptions,
  error,
  onClearError,
  onClose,
  onSave
}) => {
  const [nome, setNome] = useState(appointment.nome || '');
  const [phone, setPhone] = useState(appointment.phone || '');
  const [interesse, setInteresse] = useState(appointment.interesse || '');
  const [midia, setMidia] = useState(appointment.midia || appointment.source_id || '');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');

  useEffect(() => {
    if (appointment.consulta_data) {
      const inputValues = extractDateTimeInputValues(appointment.consulta_data);
      if (inputValues) {
        setDate(inputValues.date);
        setTime(inputValues.time);
      }
    }
  }, [appointment.consulta_data]);

  const handleSubmit = () => {
    const payload: AgendamentoUpdate = {
      nome,
      phone,
      interesse
    };
    if (appointment.media_editable !== false && !isProtectedCrmMediaSource(appointment.source_id || appointment.midia)) {
      payload.midia = midia;
    }
    if (date && time) {
      const isoLocalString = `${date}T${time}:00`;
      payload.consulta_data = isoLocalString;
    }
    onSave(appointment.id, payload);
  };

  return (
    <div className="space-y-4">
      {error && (
        <div
          role="alert"
          className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm leading-relaxed text-red-700"
        >
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Data</label>
          <input
            type="date"
            className="w-full rounded-xl border border-gray-300 p-2 focus:border-sminy-primary focus:ring-sminy-primary"
            value={date}
            onChange={(event) => {
              onClearError();
              setDate(event.target.value);
            }}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Hora</label>
          <input
            type="time"
            className="w-full rounded-xl border border-gray-300 p-2 focus:border-sminy-primary focus:ring-sminy-primary"
            value={time}
            onChange={(event) => {
              onClearError();
              setTime(event.target.value);
            }}
          />
        </div>
      </div>

      <CrmMediaField
        value={midia}
        protectedSource={appointment.source_id || appointment.midia}
        mediaEditable={appointment.media_editable}
        onChange={(value) => {
          onClearError();
          setMidia(value);
        }}
      />

      <TreatmentSelectField
        label="Interesse"
        value={interesse}
        onChange={(value) => {
          onClearError();
          setInteresse(value);
        }}
        treatmentOptions={treatmentOptions}
        isLoadingTreatmentOptions={isLoadingTreatmentOptions}
      />
      <div className="grid grid-cols-2 gap-4 pt-2">
        <Button variant="primary" onClick={handleSubmit}>
          Salvar
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Cancelar
        </Button>
      </div>
    </div>
  );
};

// ---------- ComparecimentoEditForm -----------
interface ComparecimentoEditFormProps {
  attendance: Comparecimento;
  treatmentOptions: string[];
  isLoadingTreatmentOptions: boolean;
  onClose: () => void;
  onSave: (id: number, data: ComparecimentoUpdate) => void;
}

const ComparecimentoEditForm: React.FC<ComparecimentoEditFormProps> = ({
  attendance,
  treatmentOptions,
  isLoadingTreatmentOptions,
  onClose,
  onSave
}) => {
  const [tratamento, setTratamento] = useState(attendance.tratamento_orcado || '');
  const [midia, setMidia] = useState(attendance.midia || attendance.source_id || '');
  const [valorOrcamento, setValorOrcamento] = useState(() =>
    attendance.valor_orcamento
      ? maskCurrencyBRL(attendance.valor_orcamento.toString())
      : ''
  );

  const handleSubmit = () => {
    const valorOrcNumber = valorOrcamento
      ? parseBRLToNumber(valorOrcamento)
      : undefined;
    const payload: ComparecimentoUpdate = {
      tratamento_orcado: tratamento,
      valor_orcamento: valorOrcNumber
    };
    if (attendance.media_editable !== false && !isProtectedCrmMediaSource(attendance.source_id || attendance.midia)) {
      payload.midia = midia;
    }
    onSave(attendance.id, payload);
  };

  return (
    <div className="space-y-4">
      <CrmMediaField
        value={midia}
        protectedSource={attendance.source_id || attendance.midia}
        mediaEditable={attendance.media_editable}
        onChange={setMidia}
      />
      <TreatmentSelectField
        label="Tratamento"
        value={tratamento}
        onChange={setTratamento}
        treatmentOptions={treatmentOptions}
        isLoadingTreatmentOptions={isLoadingTreatmentOptions}
      />
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Valor Orçamento</label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <DollarSign className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="w-full pl-10 p-2 border border-gray-300 rounded-xl focus:ring-sminy-primary focus:border-sminy-primary"
            value={valorOrcamento}
            onChange={(e) => setValorOrcamento(maskCurrencyBRL(e.target.value))}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 pt-2">
        <Button variant="primary" onClick={handleSubmit}>
          Salvar
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Cancelar
        </Button>
      </div>
    </div>
  );
};

// ---------- VendaEditForm -----------
interface VendaEditFormProps {
  sale: Venda;
  treatmentOptions: string[];
  isLoadingTreatmentOptions: boolean;
  onClose: () => void;
  onSave: (id: number, data: VendaUpdate) => void;
}

const VendaEditForm: React.FC<VendaEditFormProps> = ({
  sale,
  treatmentOptions,
  isLoadingTreatmentOptions,
  onClose,
  onSave
}) => {
  const [tratamentoFechado, setTratamentoFechado] = useState(
    sale.tratamento_fechado || ''
  );
  const [midia, setMidia] = useState(sale.midia || sale.source_id || '');
  const [valorFaturado, setValorFaturado] = useState(() =>
    sale.valor_faturado ? maskCurrencyBRL(sale.valor_faturado.toString()) : ''
  );
  const [valorPago, setValorPago] = useState(() =>
    sale.valor_pago ? maskCurrencyBRL(sale.valor_pago.toString()) : ''
  );

  const handleSubmit = () => {
    const faturadoNum = valorFaturado ? parseBRLToNumber(valorFaturado) : undefined;
    const pagoNum = valorPago ? parseBRLToNumber(valorPago) : undefined;
    const payload: VendaUpdate = {
      tratamento_fechado: tratamentoFechado,
      valor_faturado: faturadoNum,
      valor_pago: pagoNum
    };
    if (sale.media_editable !== false && !isProtectedCrmMediaSource(sale.source_id || sale.midia)) {
      payload.midia = midia;
    }
    onSave(sale.id, payload);
  };

  return (
    <div className="space-y-4">
      <CrmMediaField
        value={midia}
        protectedSource={sale.source_id || sale.midia}
        mediaEditable={sale.media_editable}
        onChange={setMidia}
      />
      <TreatmentSelectField
        label="Tratamento Fechado"
        value={tratamentoFechado}
        onChange={setTratamentoFechado}
        treatmentOptions={treatmentOptions}
        isLoadingTreatmentOptions={isLoadingTreatmentOptions}
      />
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Valor Faturado</label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <DollarSign className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="w-full pl-10 p-2 border border-gray-300 rounded-xl focus:ring-sminy-primary focus:border-sminy-primary"
            value={valorFaturado}
            onChange={(e) => setValorFaturado(maskCurrencyBRL(e.target.value))}
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Valor Pago</label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <DollarSign className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="w-full pl-10 p-2 border border-gray-300 rounded-xl focus:ring-sminy-primary focus:border-sminy-primary"
            value={valorPago}
            onChange={(e) => setValorPago(maskCurrencyBRL(e.target.value))}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 pt-2">
        <Button variant="primary" onClick={handleSubmit}>
          Salvar
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Cancelar
        </Button>
      </div>
    </div>
  );
};

// ---------- NoShowEditForm -----------
interface NoShowEditFormProps {
  noShow: NoShowEvent;
  onClose: () => void;
  onSave: (id: number, data: NoShowUpdatePayload) => void;
}

const NoShowEditForm: React.FC<NoShowEditFormProps> = ({
  noShow,
  onClose,
  onSave
}) => {
  const [observacao, setObservacao] = useState(noShow.observacao || '');
  const [midia, setMidia] = useState(noShow.midia || noShow.source_id || '');

  const handleSubmit = () => {
    const payload: NoShowUpdatePayload = { observacao };
    if (noShow.media_editable !== false && !isProtectedCrmMediaSource(noShow.source_id || noShow.midia)) {
      payload.midia = midia;
    }
    onSave(noShow.id, payload);
  };

  return (
    <div className="space-y-4">
      <CrmMediaField
        value={midia}
        protectedSource={noShow.source_id || noShow.midia}
        mediaEditable={noShow.media_editable}
        onChange={setMidia}
      />
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Observação</label>
        <textarea
          className="w-full p-2 border border-gray-300 rounded-xl focus:ring-sminy-primary focus:border-sminy-primary h-32"
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
        />
      </div>
      <div className="grid grid-cols-2 gap-4 pt-2">
        <Button variant="primary" onClick={handleSubmit}>
          Salvar
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Cancelar
        </Button>
      </div>
    </div>
  );
};

// ===============================
//         COMPONENTE PRINCIPAL
// ===============================
const CRMInterfacev4: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();
  const pageClassName = clsx(
    "min-h-screen overflow-x-hidden transition-colors duration-200",
    isDark ? "bg-[#111827] text-gray-200" : "bg-gradient-to-b from-gray-50 to-white text-slate-800"
  );
  const panelClassName = clsx(
    "rounded-2xl border p-3 shadow-sm sm:p-4",
    isDark ? "border-gray-700 bg-gray-800" : "border-slate-200 bg-white"
  );
  const fieldClassName = clsx(
    "w-full rounded-xl border px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary",
    isDark ? "border-gray-600 bg-gray-700 text-gray-100 placeholder:text-gray-400" : "border-slate-300 bg-white text-slate-900 placeholder:text-slate-400"
  );
  const fieldWithIconClassName = clsx(fieldClassName, "pl-10");
  const labelClassName = clsx("block text-xs font-medium mb-1", isDark ? "text-gray-400" : "text-slate-500");
  const renderNoPhoneNotice = (visible: boolean, details: string) => {
    if (!visible) return null;

    return (
      <div
        role="status"
        className={clsx(
          "flex items-start gap-2 rounded-xl border px-3 py-2 text-sm leading-relaxed",
          isDark
            ? "border-amber-800/70 bg-amber-950/30 text-amber-200"
            : "border-amber-200 bg-amber-50 text-amber-800"
        )}
      >
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{details}</span>
      </div>
    );
  };

  // -------------- Aba ativa --------------
  const [activeTab, setActiveTab] = useState<CrmTabKey>('leads');
  const [viewMode, setViewMode] = useState<CrmViewMode>(() => {
    try {
      const storedMode = localStorage.getItem(CRM_VIEW_MODE_STORAGE_KEY);
      return storedMode === 'list' || storedMode === 'cards' ? storedMode : 'cards';
    } catch {
      return 'cards';
    }
  });

  const handleViewModeChange = useCallback((mode: CrmViewMode) => {
    setViewMode(mode);

    try {
      localStorage.setItem(CRM_VIEW_MODE_STORAGE_KEY, mode);
    } catch {
      // A preferência visual continua ativa na sessão atual mesmo sem persistência.
    }
  }, []);

  // -------------- Estados para navegação mobile --------------
  const [isMobile, setIsMobile] = useState(false);

  const [lossesEnabled, setLossesEnabled] = useState(false);
  const [lost, setLost] = useState<CrmLoss[]>([]);
  useEffect(() => {
    if (!lossesEnabled && activeTab === 'lost') setActiveTab('leads');
  }, [lossesEnabled, activeTab]);
  const [lossFeedback, setLossFeedback] = useState('');

  // Lista de tabs para navegação sequencial
  const tabsList: Array<{
    key: CrmTabKey;
    label: string;
    icon: LucideIcon;
    shortLabel: string;
  }> = [
    { key: 'leads', label: 'Leads', icon: Target, shortLabel: 'Leads' },
    { key: 'appointments', label: 'Agendamentos', icon: CalendarDays, shortLabel: 'Agenda' },
    { key: 'noShows', label: 'No-Shows', icon: XCircle, shortLabel: 'Faltas' },
    { key: 'attendances', label: 'Comparecimentos', icon: CheckCircle2, shortLabel: 'Presença' },
    { key: 'sales', label: 'Vendas', icon: DollarSign, shortLabel: 'Vendas' },
    ...(lossesEnabled ? [{ key: 'lost' as const, label: 'Perdidos', icon: UserX, shortLabel: 'Perdidos' }] : [])
  ];

  const currentTabIndex = Math.max(0, tabsList.findIndex(tab => tab.key === activeTab));


  const goToPreviousTab = () => {
    if (currentTabIndex > 0) {
      setActiveTab(tabsList[currentTabIndex - 1].key);
    }
  };

  const goToNextTab = () => {
    if (currentTabIndex < tabsList.length - 1) {
      setActiveTab(tabsList[currentTabIndex + 1].key);
    }
  };

  // -------------- Estados principais --------------
  const [leads, setLeads] = useState<Lead[]>([]);
  const [appointments, setAppointments] = useState<AgendamentoResponse[]>([]);
  const [attendances, setAttendances] = useState<Comparecimento[]>([]);
  const [sales, setSales] = useState<Venda[]>([]);
  const [noShows, setNoShows] = useState<NoShowEvent[]>([]);
  const [tagsCache, setTagsCache] = useState<Record<number, LeadTagsSummary>>({});
  const [visibleCounts, setVisibleCounts] = useState<Record<CrmTabKey, number>>(DEFAULT_VISIBLE_COUNTS);
  const [crmTotals, setCrmTotals] = useState<Record<CrmTabKey, number>>(DEFAULT_CRM_TOTALS);
  const [exhaustedTabs, setExhaustedTabs] = useState<Record<CrmTabKey, boolean>>(DEFAULT_EXHAUSTED_TABS);
  const [isTagsLoading, setIsTagsLoading] = useState(false);
  const [isCrmRefreshing, setIsCrmRefreshing] = useState(false);
  const [loadingMoreTab, setLoadingMoreTab] = useState<CrmTabKey | null>(null);
  const [crmTreatmentOptions, setCrmTreatmentOptions] = useState<string[]>([]);
  const [isLoadingCrmTreatmentOptions, setIsLoadingCrmTreatmentOptions] = useState(true);
  const tagsCacheRef = useRef<Record<number, LeadTagsSummary>>({});
  const loadedTagLeadIdsRef = useRef<Set<number>>(new Set());
  const tagsRequestIdRef = useRef(0);
  const crmRequestIdRef = useRef(0);
  const hasLoadedCrmRef = useRef(false);
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);

  // -------------- Loading / Error --------------
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    tagsCacheRef.current = tagsCache;
  }, [tagsCache]);

  useEffect(() => {
    let isMounted = true;

    const loadCrmTreatmentOptions = async () => {
      setIsLoadingCrmTreatmentOptions(true);

      try {
        const treatments = await getAgentTreatmentTitles();
        if (!isMounted) return;
        setCrmTreatmentOptions(treatments);
      } catch (err) {
        console.warn('[CRM] Não foi possível carregar tratamentos da clínica:', err);
        if (isMounted) {
          setCrmTreatmentOptions([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingCrmTreatmentOptions(false);
        }
      }
    };

    void loadCrmTreatmentOptions();

    return () => {
      isMounted = false;
    };
  }, []);

  // Função para recarregar tags de lead específico (performance optimization)
  const loadLeadTags = useCallback(async (leadId: number) => {
    try {
      console.log(`🔄 loadLeadTags: Recarregando tags do lead ${leadId}`);
      const tags = await TagsService.getLeadTags(leadId);
      loadedTagLeadIdsRef.current.add(leadId);
      setTagsCache(prev => {
        const next = { ...prev, [leadId]: tags };
        tagsCacheRef.current = next;
        return next;
      });
      console.log(`✅ loadLeadTags: Tags do lead ${leadId} recarregadas com sucesso`);
    } catch (error) {
      console.error(`❌ Erro ao recarregar tags do lead ${leadId}:`, error);
    }
  }, []);

  const loadLeadTagsBatch = useCallback(async (leadIds: number[]) => {
    const uniqueLeadIds = Array.from(new Set(
      leadIds.filter((id): id is number => Number.isFinite(id) && id > 0)
    ));

    const missingLeadIds = uniqueLeadIds.filter((id) => (
      !loadedTagLeadIdsRef.current.has(id) && !tagsCacheRef.current[id]
    ));

    if (missingLeadIds.length === 0) {
      return {};
    }

    const tagsMap: Record<number, LeadTagsSummary> = {};

    for (let index = 0; index < missingLeadIds.length; index += CRM_TAGS_BATCH_SIZE) {
      const chunk = missingLeadIds.slice(index, index + CRM_TAGS_BATCH_SIZE);
      const response = await TagsService.getLeadsTagsBatch(chunk);

      Object.entries(response.results || {}).forEach(([leadId, tags]) => {
        const numericLeadId = Number(leadId);
        if (!Number.isFinite(numericLeadId)) return;

        tagsMap[numericLeadId] = tags;
        loadedTagLeadIdsRef.current.add(numericLeadId);
      });

      response.failed_ids?.forEach((leadId) => {
        loadedTagLeadIdsRef.current.add(leadId);
      });
    }

    if (Object.keys(tagsMap).length > 0) {
      setTagsCache(prev => {
        const next = { ...prev, ...tagsMap };
        tagsCacheRef.current = next;
        return next;
      });
    }

    return tagsMap;
  }, []);

  // Otimização: debounce para carregamento de tags (evita múltiplas chamadas simultâneas)
  const debouncedLoadLeadTags = useMemo(
    () => {
      const timeoutMap = new Map<number, number>();

      return (leadId: number) => {
        // Limpar timeout existente para este lead
        const existingTimeout = timeoutMap.get(leadId);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
        }

        // Agendar nova chamada
        const timeout = setTimeout(() => {
          console.log(`🚀 CRM: Carregando tags do lead ${leadId} (debounced)`);
          loadLeadTags(leadId);
          timeoutMap.delete(leadId);
        }, 300); // 300ms de debounce

        timeoutMap.set(leadId, timeout);
      };
    },
    [loadLeadTags]
  );

  // -------------- Filtros --------------
  const [dateRange, setDateRange] = useState<DateRange>({ startDate: null, endDate: null });
  const [filterType, setFilterType] = useState<PresetFilterType>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [selectedSource] = useState<string>('');
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [patientFilter, setPatientFilter] = useState<PatientFilterValue>('all');
  const [availableFilterTags, setAvailableFilterTags] = useState<TagResponse[]>([]);
  const [isFilterTagsLoading, setIsFilterTagsLoading] = useState(false);
  const [filterTagsError, setFilterTagsError] = useState<string | null>(null);
  const [hasLoadedFilterTags, setHasLoadedFilterTags] = useState(false);
  const filterDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const state = location.state as { searchTerm?: string } | null;
    if (state?.searchTerm) {
      setActiveTab('leads');
      setSearchTerm(state.searchTerm);
    }
  }, [location.key, location.state]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [searchTerm]);

  const getCrmAuth = useCallback(() => {
    const clientId = parseInt(localStorage.getItem('client_id') || '0', 10);
    const clinicId = parseInt(localStorage.getItem('clinic_id') || '0', 10);
    const userType = localStorage.getItem('user_type');

    if (!clientId || !clinicId) {
      throw new Error('Informações de autenticação inválidas');
    }

    const effectiveClientId =
      userType === 'user'
        ? parseInt(localStorage.getItem('master_client_id') || '0', 10)
        : clientId;

    if (userType === 'user' && !effectiveClientId) {
      throw new Error('Informações do client master não encontradas');
    }

    return { effectiveClientId, clinicId };
  }, []);

  const crmListParams = useMemo<CrmListParams>(() => ({
    search: debouncedSearchTerm.trim() || undefined,
    startDate: formatCrmDateParam(dateRange.startDate),
    endDate: formatCrmDateParam(dateRange.endDate),
    source: selectedSource || undefined,
    tagIds: selectedTagIds.length > 0 ? selectedTagIds : undefined,
    isPatient: patientFilter === 'all' ? undefined : patientFilter === 'yes',
    currentStageOnly: CRM_CURRENT_STAGE_ONLY,
  }), [
    debouncedSearchTerm,
    dateRange.startDate,
    dateRange.endDate,
    selectedSource,
    selectedTagIds,
    patientFilter,
  ]);

  useEffect(() => {
    setVisibleCounts(DEFAULT_VISIBLE_COUNTS);
    setExhaustedTabs(DEFAULT_EXHAUSTED_TABS);
  }, [searchTerm, dateRange, selectedSource, selectedTagIds, patientFilter]);

  const activeAdditionalFilterCount =
    (selectedTagIds.length > 0 ? 1 : 0) + (patientFilter === 'all' ? 0 : 1);

  const clearAdditionalFilters = () => {
    setSelectedTagIds([]);
    setPatientFilter('all');
  };

  const loadAvailableFilterTags = useCallback(async () => {
    const clinicId = parseInt(localStorage.getItem('clinic_id') || '0', 10);

    if (!clinicId) {
      setFilterTagsError('Não foi possível identificar a clínica.');
      setHasLoadedFilterTags(true);
      return;
    }

    try {
      setIsFilterTagsLoading(true);
      setFilterTagsError(null);
      const tags = await TagsService.getTags(clinicId, {
        is_active: true,
        limit: 1000,
      });
      setAvailableFilterTags(
        [...tags].sort((firstTag, secondTag) => firstTag.name.localeCompare(secondTag.name))
      );
      setHasLoadedFilterTags(true);
    } catch (error) {
      console.error('[CRM] Erro ao carregar tags para o filtro:', error);
      setFilterTagsError('Não foi possível carregar as tags.');
      setHasLoadedFilterTags(true);
    } finally {
      setIsFilterTagsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (showFilterPanel && !hasLoadedFilterTags && !isFilterTagsLoading) {
      void loadAvailableFilterTags();
    }
  }, [
    hasLoadedFilterTags,
    isFilterTagsLoading,
    loadAvailableFilterTags,
    showFilterPanel,
  ]);

  useEffect(() => {
    if (!showFilterPanel) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterDropdownRef.current
        && !filterDropdownRef.current.contains(event.target as Node)
      ) {
        setShowFilterPanel(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowFilterPanel(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showFilterPanel]);

  // Função para detectar se source_id é protegido (não editável)
  const isProtectedSource = (sourceId: string): boolean => {
    return isProtectedCrmMediaSource(sourceId);
  };

  // Mantém função antiga para compatibilidade
  const isMetaAdsId = (sourceId: string): boolean => {
    return isProtectedCrmMediaSource(sourceId);
  };

  // -------------- Modals: Comparecimento --------------
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] =
    useState<AgendamentoResponse | null>(null);
  const [formComparecimento, setFormComparecimento] = useState({
    tratamento: '',
    orcamento: ''
  });
  const [comparecimentoDate, setComparecimentoDate] = useState('');
  const [comparecimentoTime, setComparecimentoTime] = useState('');

  // -------------- Modals: Reagendar --------------
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [isProcessingReschedule, setIsProcessingReschedule] = useState(false);
  const [rescheduleError, setRescheduleError] = useState<string | null>(null);

  // -------------- Modals: Venda --------------
  const [showSaleModal, setShowSaleModal] = useState(false);
  const [selectedAttendance, setSelectedAttendance] = useState<Comparecimento | null>(
    null
  );
  const [formVenda, setFormVenda] = useState({
    tratamento_fechado: '',
    valor_faturado: '',
    valor_pago: ''
  });
  const [vendaDate, setVendaDate] = useState('');
  const [vendaTime, setVendaTime] = useState('');
  const [isSavingSale, setIsSavingSale] = useState(false);
  const [saleFailure, setSaleFailure] = useState<CrmSaleFailure | null>(null);
  const [isPhoneCorrectionOpen, setIsPhoneCorrectionOpen] = useState(false);
  const [phoneCorrectionFeedback, setPhoneCorrectionFeedback] = useState<string | null>(null);
  const saleNeedsFocusReturn = useRef(false);
  useEffect(() => {
    if (showSaleModal && !saleFailure && !isPhoneCorrectionOpen && saleNeedsFocusReturn.current) {
      document.getElementById('crm-sale-submit')?.focus();
      saleNeedsFocusReturn.current = false;
    }
  }, [showSaleModal, saleFailure, isPhoneCorrectionOpen]);

  // -------------- Modals: Lead --------------
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [leadToEdit, setLeadToEdit] = useState<Lead | null>(null);
  const [isSavingLead, setIsSavingLead] = useState(false);
  const [leadSaveError, setLeadSaveError] = useState<ParsedApiError | null>(null);
  const [leadForm, setLeadForm] = useState({
    name: '',
    phone: '',
    source_id: 'Facebook',
    data_entrada: ''
  });

  // -------------- Modals: Agendamento --------------
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [leadForAppointment, setLeadForAppointment] = useState<Lead | null>(null);
  const [newAppointmentForm, setNewAppointmentForm] = useState({
    date: '',
    time: '',
    midia: 'Facebook',
    interesse: ''
  });
  const [appointmentCreationError, setAppointmentCreationError] = useState<string | null>(null);
  const [appointmentOperationError, setAppointmentOperationError] = useState<string | null>(null);
  const [isCreatingAppointment, setIsCreatingAppointment] = useState(false);
  const [deletingAppointmentId, setDeletingAppointmentId] = useState<number | null>(null);

  // -------------- Modals: Editar Agendamento --------------
  const [showAppointmentEditModal, setShowAppointmentEditModal] = useState(false);
  const [appointmentToEdit, setAppointmentToEdit] = useState<AgendamentoResponse | null>(
    null
  );

  // -------------- Modals: NoShow --------------
  const [showNoShowModal, setShowNoShowModal] = useState(false);
  const [noShowObs, setNoShowObs] = useState('');
  const [appointmentNoShow, setAppointmentNoShow] =
    useState<AgendamentoResponse | null>(null);

  // -------------- Modals: Editar NoShow --------------
  const [showNoShowEditModal, setShowNoShowEditModal] = useState(false);
  const [noShowToEdit, setNoShowToEdit] = useState<NoShowEvent | null>(null);

  // -------------- Modals: Editar Comparecimento --------------
  const [showAttendanceEditModal, setShowAttendanceEditModal] = useState(false);
  const [attendanceToEdit, setAttendanceToEdit] = useState<Comparecimento | null>(null);

  // -------------- Modals: Editar Venda --------------
  const [showSaleEditModal, setShowSaleEditModal] = useState(false);
  const [saleToEdit, setSaleToEdit] = useState<Venda | null>(null);

  // -------------- Filtro: Calendario --------------
  const handleDateFilterChange = (newDateRange: DateRange) => {
    setDateRange(newDateRange);
    setFilterType('custom'); // Se você quiser marcar o tipo de filtro como personalizado
  };

  // Adicione um manipulador para o filtro dropdown
  const handlePresetFilterSelect = (presetType: PresetFilterType) => {
    const today = new Date();
    let newRange: DateRange = { startDate: null, endDate: null };

    switch (presetType) {
      case 'today':
        // Define o intervalo como o dia atual (início ao fim do dia)
        newRange = {
          startDate: startOfDay(today),
          endDate: endOfDay(today)
        };
        break;

      case 'tomorrow':  // Novo caso para "Amanhã"
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        newRange = {
          startDate: startOfDay(tomorrow),
          endDate: endOfDay(tomorrow)
        };
        break;

      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        newRange = {
          startDate: startOfDay(yesterday),
          endDate: endOfDay(yesterday)
        };
        break;

      case 'thisWeek':
        // Começa no domingo (0) e termina no sábado (6)
        const startOfWeekDate = new Date(today);
        startOfWeekDate.setDate(today.getDate() - today.getDay()); // Retrocede até o domingo

        const endOfWeekDate = new Date(startOfWeekDate);
        endOfWeekDate.setDate(startOfWeekDate.getDate() + 6); // Avança até o sábado

        newRange = {
          startDate: startOfDay(startOfWeekDate),
          endDate: endOfDay(endOfWeekDate)
        };
        break;

      case 'lastWeek':
        const lastWeekStartDate = new Date(today);
        lastWeekStartDate.setDate(today.getDate() - today.getDay() - 7); // Domingo da semana anterior

        const lastWeekEndDate = new Date(lastWeekStartDate);
        lastWeekEndDate.setDate(lastWeekStartDate.getDate() + 6); // Sábado da semana anterior

        newRange = {
          startDate: startOfDay(lastWeekStartDate),
          endDate: endOfDay(lastWeekEndDate)
        };
        break;

      case 'thisMonth':
        const startOfMonthDate = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonthDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);

        newRange = {
          startDate: startOfDay(startOfMonthDate),
          endDate: endOfDay(endOfMonthDate)
        };
        break;

      case 'lastMonth':
        const startOfLastMonthDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const endOfLastMonthDate = new Date(today.getFullYear(), today.getMonth(), 0);

        newRange = {
          startDate: startOfDay(startOfLastMonthDate),
          endDate: endOfDay(endOfLastMonthDate)
        };
        break;

      case 'all':
      default:
        // Sem filtro de data
        newRange = { startDate: null, endDate: null };
        break;
    }

    // Atualiza o estado e aplica o filtro
    setDateRange(newRange);
    setFilterType(presetType);
  };

  // Função auxiliar para verificar se a data/hora do agendamento é anterior à data de entrada do lead
  const isAppointmentBeforeLeadEntry = (
    appointmentDate: string,
    appointmentTime: string,
    leadEntryDate: string | undefined
  ): boolean => {
    // Se não houver data de entrada do lead, não podemos fazer a verificação
    if (!leadEntryDate) return false;

    // Criar objeto Date para a data/hora do agendamento
    const appointmentDateTime = new Date(`${appointmentDate}T${appointmentTime}:00`);

    // Criar objeto Date para a data de entrada do lead
    // Assumindo que leadEntryDate está no formato "YYYY-MM-DD HH:MM:SS" ou "YYYY-MM-DDThh:mm:ss"
    const leadDateTime = new Date(leadEntryDate.replace(' ', 'T'));

    // Comparar as datas
    return appointmentDateTime < leadDateTime;
  };

  const isDateInRange = useCallback((dateStr?: string): boolean => {
    if (!dateStr || !dateRange.startDate) return true; // Se não houver data ou filtro, retorna verdadeiro

    try {
      const date = new Date(dateStr);
      const start = dateRange.startDate;
      const end = dateRange.endDate || new Date(); // Se não houver data final, usa hoje

      // Normalizar as datas para o início e fim do dia
      const startOfDay = new Date(start);
      startOfDay.setHours(0, 0, 0, 0);

      const endOfDay = new Date(end);
      endOfDay.setHours(23, 59, 59, 999);

      return date >= startOfDay && date <= endOfDay;
    } catch {
      return true; // Se houver erro ao analisar a data, não filtra
    }
  }, [dateRange.startDate, dateRange.endDate]);

  // ==============================
  //       useEffect -> detect mobile
  // ==============================
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // ==============================
  //       useEffect -> load
  // ==============================
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      const startedAt = performance.now();
      const requestId = ++crmRequestIdRef.current;
      const isInitialLoad = !hasLoadedCrmRef.current;

      try {
        if (isInitialLoad) {
          setLoading(true);
        } else {
          setIsCrmRefreshing(true);
        }
        setError(null);

        const { effectiveClientId, clinicId } = getCrmAuth();
        const crmData = await listarCrmInicial(
          effectiveClientId,
          clinicId,
          CRM_VISIBLE_BATCH_SIZE,
          crmListParams
        );

        if (!isMounted || requestId !== crmRequestIdRef.current) return;

        const timezoneKey = `sminy.clinicTimezone.${clinicId}`;
        if (crmData.clinicTimezone) {
          localStorage.setItem(timezoneKey, crmData.clinicTimezone);
        } else {
          localStorage.removeItem(timezoneKey);
        }
        setAppointments(crmData.appointments);
        setLeads(crmData.leads);
        setAttendances(crmData.attendances);
        setSales(crmData.sales);
        setNoShows(crmData.noShows);
        setLost(crmData.lost || []);
        setLossesEnabled(crmData.losses_enabled === true);
        setCrmTotals({ ...DEFAULT_CRM_TOTALS, ...crmData.totals, lost: crmData.totals.lost || 0 });
        setVisibleCounts(DEFAULT_VISIBLE_COUNTS);
        setExhaustedTabs(DEFAULT_EXHAUSTED_TABS);
        setTagsCache({});
        tagsCacheRef.current = {};
        loadedTagLeadIdsRef.current.clear();
        hasLoadedCrmRef.current = true;

        console.log('[CRM] Dados iniciais carregados:', {
          leads: crmData.leads.length,
          appointments: crmData.appointments.length,
          attendances: crmData.attendances.length,
          sales: crmData.sales.length,
          noShows: crmData.noShows.length,
          totals: crmData.totals,
          durationMs: Math.round(performance.now() - startedAt)
        });
      } catch (err) {
        if (!isMounted || requestId !== crmRequestIdRef.current) return;

        setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
        console.error('Erro ao carregar dados:', err);
      } finally {
        if (isMounted && requestId === crmRequestIdRef.current) {
          setLoading(false);
          setIsCrmRefreshing(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [crmListParams, getCrmAuth]);

  const refreshCrmData = useCallback(async () => {
    const startedAt = performance.now();
    const requestId = ++crmRequestIdRef.current;

    try {
      setIsCrmRefreshing(true);
      setError(null);

      const { effectiveClientId, clinicId } = getCrmAuth();
      const crmData = await listarCrmInicial(
        effectiveClientId,
        clinicId,
        CRM_VISIBLE_BATCH_SIZE,
        crmListParams
      );

      if (requestId !== crmRequestIdRef.current) return;

      const timezoneKey = `sminy.clinicTimezone.${clinicId}`;
      if (crmData.clinicTimezone) {
        localStorage.setItem(timezoneKey, crmData.clinicTimezone);
      } else {
        localStorage.removeItem(timezoneKey);
      }
      setAppointments(crmData.appointments);
      setLeads(crmData.leads);
      setAttendances(crmData.attendances);
      setSales(crmData.sales);
      setNoShows(crmData.noShows);
      setLost(crmData.lost || []);
      setLossesEnabled(crmData.losses_enabled === true);
      setCrmTotals({ ...DEFAULT_CRM_TOTALS, ...crmData.totals, lost: crmData.totals.lost || 0 });
      setVisibleCounts(DEFAULT_VISIBLE_COUNTS);
      setExhaustedTabs(DEFAULT_EXHAUSTED_TABS);
      setTagsCache({});
      tagsCacheRef.current = {};
      loadedTagLeadIdsRef.current.clear();

      console.log('[CRM] Dados recarregados:', {
        leads: crmData.leads.length,
        appointments: crmData.appointments.length,
        attendances: crmData.attendances.length,
        sales: crmData.sales.length,
        noShows: crmData.noShows.length,
        totals: crmData.totals,
        durationMs: Math.round(performance.now() - startedAt)
      });
    } catch (err) {
      if (requestId !== crmRequestIdRef.current) return;

      setError(err instanceof Error ? err.message : 'Erro ao recarregar dados');
      console.error('Erro ao recarregar dados:', err);
    } finally {
      if (requestId === crmRequestIdRef.current) {
        setIsCrmRefreshing(false);
      }
    }
  }, [crmListParams, getCrmAuth]);

  // ==============================
  //        Funções de LEAD
  // ==============================
  const handlePhoneCorrected = (updated: Lead) => {
    setPhoneCorrectionFeedback(getPhoneCorrectionFeedback(updated));
    setLeads(rows => rows.map(row => reconcileCrmPhone(row, updated, true)!));
    setAppointments(rows => rows.map(row => reconcileCrmPhone(row, updated)!));
    setAttendances(rows => rows.map(row => reconcileCrmPhone(row, updated)!));
    setNoShows(rows => rows.map(row => reconcileCrmPhone(row, updated)!));
    setSales(rows => rows.map(row => reconcileCrmPhone(row, updated)!));
    setLost(rows => rows.map(row => reconcileCrmPhone(row, updated)!));
    setSelectedAttendance(row => reconcileCrmPhone(row, updated));
    setSelectedAppointment(row => reconcileCrmPhone(row, updated));
    setLeadForAppointment(row => reconcileCrmPhone(row, updated, true));
    setLeadToEdit(row => reconcileCrmPhone(row, updated, true));
    setAppointmentToEdit(row => reconcileCrmPhone(row, updated));
    setAttendanceToEdit(row => reconcileCrmPhone(row, updated));
    setNoShowToEdit(row => reconcileCrmPhone(row, updated));
    setSaleToEdit(row => reconcileCrmPhone(row, updated));
    void refreshCrmData();
  };

  const handleNewLead = () => {
    setLeadToEdit(null);
    setLeadSaveError(null);
    setLeadForm({ name: '', phone: '', source_id: 'Facebook', data_entrada: '' });
    setShowLeadModal(true);
  };

  const handleEditLead = (lead: Lead) => {
    setLeadToEdit(lead);
    setLeadSaveError(null);
    setLeadForm({
      name: lead.name || '',
      phone: lead.phone ? formatBrazilianPhone(lead.phone) : '',
      source_id: lead.source_id || 'Facebook',
      data_entrada: lead.data_entrada
        ? lead.data_entrada.substring(0, 16).replace(' ', 'T')
        : ''
    });
    setShowLeadModal(true);
  };

  const handleLeadPhoneChange = (value: string) => {
    const masked = formatBrazilianPhoneInput(value);
    setLeadSaveError(null);
    setLeadForm((prev) => ({ ...prev, phone: masked }));
  };

  const handleDeleteLead = async (lead: Lead) => {
    try {
      const clientId = parseInt(localStorage.getItem('client_id') || '0', 10);
      const clinicId = parseInt(localStorage.getItem('clinic_id') || '0', 10);
      const userType = localStorage.getItem('user_type');
      const effectiveClientId =
        userType === 'user'
          ? parseInt(localStorage.getItem('master_client_id') || '0', 10)
          : clientId;

      await deletarLead(effectiveClientId, clinicId, lead.id);

      // limpar local
      setLeads((prev) => prev.filter((item) => item.id !== lead.id));
      setAppointments((prev) => prev.filter((appt) => appt.lead_id !== lead.id));
      setAttendances((prev) => prev.filter((comp) => comp.lead_id !== lead.id));
      setSales((prev) => prev.filter((venda) => venda.lead_id !== lead.id));
    } catch (error) {
      console.error('[handleDeleteLead] Erro:', error);
      alert('Não foi possível excluir o lead.');
    }
  };

  const handleSaveLead = async () => {
    if (isSavingLead) return;

    setLeadSaveError(null);
    const hasPhone = Boolean(leadForm.phone && leadForm.phone.trim());
    const phoneValidation = hasPhone
      ? validateBrazilianWhatsAppPhone(leadForm.phone)
      : null;
    if (!leadToEdit && phoneValidation && !phoneValidation.isValid) {
      setLeadSaveError({ message: phoneValidation.error || 'Telefone inválido.' });
      return;
    }

    setIsSavingLead(true);
    try {
      const clientId = parseInt(localStorage.getItem('client_id') || '0', 10);
      const clinicId = parseInt(localStorage.getItem('clinic_id') || '0', 10);
      const userType = localStorage.getItem('user_type');
      const effectiveClientId =
        userType === 'user'
          ? parseInt(localStorage.getItem('master_client_id') || '0', 10)
          : clientId;

      if (leadToEdit) {
        // Edit - inclui source_id apenas se não for um ID do Meta Ads
        const payload: Partial<Lead> = {
          name: leadForm.name
        };
        if (leadForm.data_entrada) {
          payload.data_entrada = leadForm.data_entrada.replace('T', ' ') + ':00';
        }
        // Incluir source_id apenas se não for protegido (Meta Ads)
        if (!isProtectedSource(leadForm.source_id)) {
          payload.source_id = leadForm.source_id;
        }
        const updated = await atualizarLead(
          effectiveClientId,
          clinicId,
          leadToEdit.id,
          payload
        );
        setLeads((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
      } else {
        // Novo
        const payload: Partial<Lead> = {
          client_id: String(effectiveClientId),
          name: leadForm.name,
          source_id: leadForm.source_id
        };
        if (phoneValidation) {
          payload.phone = phoneValidation.normalized;
        }
        if (leadForm.data_entrada) {
          payload.data_entrada = leadForm.data_entrada.replace('T', ' ') + ':00';
        }
        const novo = await criarLead(effectiveClientId, clinicId, payload);
        setLeads((prev) => [...prev, novo]);
      }

      // reset
      setShowLeadModal(false);
      setLeadToEdit(null);
      setLeadSaveError(null);
      setLeadForm({ name: '', phone: '', source_id: 'Facebook', data_entrada: '' });
    } catch (error: unknown) {
      console.error('Erro ao salvar lead:', error);
      setLeadSaveError(parseApiError(error, 'Erro ao salvar lead.'));
    } finally {
      setIsSavingLead(false);
    }
  };

  const handleOpenExistingLeadIdentity = () => {
    if (!leadSaveError) return;

    const phone = leadSaveError.existingPhone || validateBrazilianWhatsAppPhone(leadForm.phone).normalized;
    setShowLeadModal(false);
    setLeadToEdit(null);
    setLeadSaveError(null);

    if (leadSaveError.existingLeadId) {
      setActiveTab('leads');
      setSearchTerm(phone);
      return;
    }

    if (leadSaveError.existingContactId || leadSaveError.existingPatientId) {
      navigate('/contacts', {
        state: {
          searchTerm: phone,
          existingContactId: leadSaveError.existingContactId,
          existingPatientId: leadSaveError.existingPatientId,
        },
      });
    }
  };

  // ==============================
  //   CRIAR AGENDAMENTO (LEAD)
  // ==============================
  const handleOpenAppointmentModal = (lead: Lead) => {
    setLeadForAppointment(lead);
    setAppointmentCreationError(null);
    setNewAppointmentForm({
      date: '',
      time: '',
      midia: lead.source_id || 'Facebook',
      interesse: ''
    });
    setShowAppointmentModal(true);
  };

  const handleCreateAppointment = async () => {
    try {
      if (isCreatingAppointment) return;
      if (!leadForAppointment) return;
      setAppointmentCreationError(null);

      if (isLoadingCrmTreatmentOptions) {
        alert('Aguarde o carregamento dos tratamentos.');
        return;
      }

      if (crmTreatmentOptions.length === 0) {
        alert('Cadastre pelo menos um tratamento na lista da clínica antes de criar o agendamento.');
        return;
      }

      if (!newAppointmentForm.interesse) {
        alert('Selecione o interesse do agendamento.');
        return;
      }

      // Verificação: Se a data/hora do agendamento é anterior à data de entrada do lead
      if (isAppointmentBeforeLeadEntry(
        newAppointmentForm.date,
        newAppointmentForm.time,
        leadForAppointment.data_entrada
      )) {
        // Formatar a data de entrada do lead para exibição
        const leadEntryDate = leadForAppointment.data_entrada
          ? new Date(leadForAppointment.data_entrada.replace(' ', 'T')).toLocaleString('pt-BR')
          : 'não informada';

        alert(`Erro: A data do agendamento não pode ser anterior à data de entrada do lead.\n\nData de entrada do lead: ${leadEntryDate}\n\nPor favor, ajuste a data do agendamento ou corrija a data de entrada do lead.`);
        return; // Interrompe a execução para evitar a criação do agendamento
      }

      const clientId = parseInt(localStorage.getItem('client_id') || '0', 10);
      const clinicId = parseInt(localStorage.getItem('clinic_id') || '0', 10);
      const userType = localStorage.getItem('user_type');
      const effectiveClientId =
        userType === 'user'
          ? parseInt(localStorage.getItem('master_client_id') || '0', 10)
          : clientId;

      // Formato do banco é dd/MM/yyyy HH:mm
      // Convertemos do formato ISO para o formato esperado pelo backend
      const [yyyy, mm, dd] = newAppointmentForm.date.split('-');
      const [HH, MM] = newAppointmentForm.time.split(':');

      // Formata diretamente no formato esperado pelo backend
      const finalDateStr = `${dd}/${mm}/${yyyy} ${HH}:${MM}`;

      const payload: AgendamentoCreate = {
        lead_id: leadForAppointment.id,
        nome: leadForAppointment.name || '',
        phone: leadForAppointment.phone || '',
        consulta_data: finalDateStr,
        midia: newAppointmentForm.midia,
        interesse: newAppointmentForm.interesse
      };

      setIsCreatingAppointment(true);
      const novoAgendamento = await criarAgendamento(
        effectiveClientId,
        clinicId,
        payload
      );

      // Atualiza estado local
      setAppointments((prev) => [...prev, novoAgendamento]);
      void refreshCrmData();

      // Reset
      setShowAppointmentModal(false);
      setLeadForAppointment(null);
      setAppointmentCreationError(null);
      setNewAppointmentForm({ date: '', time: '', midia: 'Facebook', interesse: '' });
      alert(
        leadForAppointment.phone?.trim()
          ? 'Agendamento criado com sucesso!'
          : 'Agendamento criado no CRM. Como o lead não possui telefone, confirmações e follow-ups automáticos não serão enviados.'
      );
    } catch (error: any) {
      console.error('Erro ao criar agendamento.');
      const parsed = parseApiError(error, 'Não foi possível criar o agendamento.');
      const alternatives = parsed.alternatives?.length
        ? ` Horários alternativos: ${parsed.alternatives.join(' ou ')}.`
        : '';
      setAppointmentCreationError(`${parsed.message}${alternatives}`);
    } finally {
      setIsCreatingAppointment(false);
    }
  };

  // ==============================
  //   EDITAR/EXCLUIR AGENDAMENTO
  // ==============================
  const handleEditAppointment = (appointment: AgendamentoResponse) => {
    setAppointmentOperationError(null);
    setAppointmentToEdit(appointment);
    setShowAppointmentEditModal(true);
  };

  const handleSaveEditAppointment = async (id: number, data: AgendamentoUpdate) => {
    try {
      setAppointmentOperationError(null);
      const clientId = parseInt(localStorage.getItem('client_id') || '0', 10);
      const clinicId = parseInt(localStorage.getItem('clinic_id') || '0', 10);
      const userType = localStorage.getItem('user_type');
      const effectiveClientId =
        userType === 'user'
          ? parseInt(localStorage.getItem('master_client_id') || '0', 10)
          : clientId;

      const atualizado = await atualizarAgendamento(
        effectiveClientId,
        clinicId,
        id,
        data
      );
      setAppointments((prev) =>
        prev.map((appt) => (appt.id === atualizado.id ? { ...appt, ...atualizado } : appt))
      );
      void refreshCrmData();
      setShowAppointmentEditModal(false);
      setAppointmentToEdit(null);
    } catch (error) {
      console.error('Erro ao editar agendamento.');
      const parsed = parseApiError(error, 'Não foi possível atualizar o agendamento.');
      const alternatives = parsed.alternatives?.length
        ? ` Horários alternativos: ${parsed.alternatives.join(' ou ')}.`
        : '';
      setAppointmentOperationError(`${parsed.message}${alternatives}`);
    }
  };

  const handleDeleteAppointment = async (appointment: AgendamentoResponse) => {
    if (deletingAppointmentId !== null) return;
    setAppointmentOperationError(null);
    setDeletingAppointmentId(appointment.id);
    try {
      const clientId = parseInt(localStorage.getItem('client_id') || '0', 10);
      const clinicId = parseInt(localStorage.getItem('clinic_id') || '0', 10);
      const userType = localStorage.getItem('user_type');
      const effectiveClientId =
        userType === 'user'
          ? parseInt(localStorage.getItem('master_client_id') || '0', 10)
          : clientId;

      await deletarAgendamento(effectiveClientId, clinicId, appointment.id);

      setAppointments((prev) => prev.filter((a) => a.id !== appointment.id));
      setNoShows((prev) => prev.filter((n) => n.agendamento_id !== appointment.id));

      // remover comps e vendas
      const compsRemoved = attendances
        .filter((c) => c.agendamento_id === appointment.id)
        .map((c) => c.id);
      setAttendances((prev) => prev.filter((c) => c.agendamento_id !== appointment.id));
      setSales((prev) =>
        prev.filter((s) => !compsRemoved.includes(s.comparecimento_id))
      );
      void refreshCrmData();
    } catch (error) {
      console.error('Erro ao excluir agendamento.');
      const parsed = parseApiError(error, 'Não foi possível excluir o agendamento.');
      setAppointmentOperationError(parsed.message);
    } finally {
      setDeletingAppointmentId(null);
    }
  };

  // ==============================
  //   COMPARECIMENTO
  // ==============================
  const handleAttendanceClick = (appointment: AgendamentoResponse) => {
    setSelectedAppointment(appointment);
    // Definir a data atual para o comparecimento
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');

    setComparecimentoDate(`${year}-${month}-${day}`);
    setComparecimentoTime(`${hours}:${minutes}`);
    setShowAttendanceModal(true);
  };

  const handleSalvarComparecimento = async () => {
    if (!selectedAppointment) return;
    try {
      const clientId = parseInt(localStorage.getItem('client_id') || '0', 10);
      const clinicId = parseInt(localStorage.getItem('clinic_id') || '0', 10);
      const userType = localStorage.getItem('user_type');
      const effectiveClientId =
        userType === 'user'
          ? parseInt(localStorage.getItem('master_client_id') || '0', 10)
          : clientId;
      if (!comparecimentoDate || !comparecimentoTime) {
        alert('Data ou Hora do Comparecimento inválida.');
        return;
      }

      // Enviar como ISO sem indicação de fuso
      const compareceuEmString = `${comparecimentoDate}T${comparecimentoTime}:00`;

      const valorOrcamentoNumber = formComparecimento.orcamento
        ? parseBRLToNumber(formComparecimento.orcamento)
        : undefined;

      const dados: ComparecimentoCreate = {
        lead_id: selectedAppointment.lead_id,
        agendamento_id: selectedAppointment.id,
        nome: selectedAppointment.nome,
        phone: selectedAppointment.phone,
        midia: selectedAppointment.midia,
        interesse: selectedAppointment.interesse,
        tratamento_orcado: formComparecimento.tratamento,
        valor_orcamento: valorOrcamentoNumber,
        compareceu_em: compareceuEmString
      };

      const novoComp = await criarComparecimento(clientId, clinicId, dados);
      setAttendances((prev) => [...prev, novoComp]);

      // recarregar noShows do backend, pois o back removeu no-show
      const updatedNoShows = await listarNoShows(effectiveClientId, clinicId);
      setNoShows(updatedNoShows);
      void refreshCrmData();

      // reset
      setShowAttendanceModal(false);
      setSelectedAppointment(null);
      setFormComparecimento({ tratamento: '', orcamento: '' });
      setComparecimentoDate('');
      setComparecimentoTime('');
    } catch (error) {
      console.error('Erro ao registrar comparecimento:', error);
      alert('Erro ao registrar comparecimento.');
    }
  };

  const handleEditAttendance = (attendance: Comparecimento) => {
    setAttendanceToEdit(attendance);
    setShowAttendanceEditModal(true);
  };

  const handleDeleteAttendance = async (attendance: Comparecimento) => {
    try {
      const clientId = parseInt(localStorage.getItem('client_id') || '0', 10);
      const clinicId = parseInt(localStorage.getItem('clinic_id') || '0', 10);
      await deletarComparecimento(clientId, clinicId, attendance.id);

      setAttendances((prev) => prev.filter((c) => c.id !== attendance.id));
      setSales((prev) => prev.filter((s) => s.comparecimento_id !== attendance.id));
      void refreshCrmData();
    } catch (error) {
      console.error('Erro ao excluir comparecimento:', error);
      alert('Erro ao excluir comparecimento.');
    }
  };

  // ==============================
  //      REAGENDAR & NO-SHOW
  // ==============================
  const handleRescheduleClick = (appointment: AgendamentoResponse) => {
    setSelectedAppointment(appointment);
    setRescheduleError(null);

    // Se já existe uma data, extrair para os campos
    if (appointment.consulta_data) {
      const inputValues = extractDateTimeInputValues(appointment.consulta_data);
      if (inputValues) {
        console.log('[CRM] Abrindo modal de reagendamento com data original', {
          appointmentId: appointment.id,
          originalValue: appointment.consulta_data,
          parsedDate: inputValues.date,
          parsedTime: inputValues.time,
        });
        setRescheduleDate(inputValues.date);
        setRescheduleTime(inputValues.time);
      }
    } else {
      // Caso contrário, sugerir a data atual
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');

      setRescheduleDate(`${year}-${month}-${day}`);
      setRescheduleTime(`${hours}:${minutes}`);
    }

    setShowRescheduleModal(true);
  };

  const handleConfirmReschedule = async () => {
    if (!selectedAppointment || isProcessingReschedule) return;

    setIsProcessingReschedule(true); // Bloqueia múltiplos cliques

    try {
      const clientId = parseInt(localStorage.getItem('client_id') || '0', 10);
      const clinicId = parseInt(localStorage.getItem('clinic_id') || '0', 10);
      const userType = localStorage.getItem('user_type');
      const effectiveClientId =
        userType === 'user'
          ? parseInt(localStorage.getItem('master_client_id') || '0', 10)
          : clientId;
      const hadNoShow =
        selectedAppointment.status === 'NO_SHOW' ||
        noShows.some((noShow) => noShow.agendamento_id === selectedAppointment.id);

      // Vamos usar o formato ISO simples sem timezone
      const isoString = `${rescheduleDate}T${rescheduleTime}:00`;

      const payload: AgendamentoUpdate = {
        consulta_data: isoString,
        ...(hadNoShow ? { status: 'SCHEDULED' } : {}),
      };

      console.log('[CRM] Confirmando reagendamento', {
        appointmentId: selectedAppointment.id,
        previousValue: selectedAppointment.consulta_data,
        nextValue: isoString,
        clearNoShow: hadNoShow,
      });

      const agAtual = await atualizarAgendamento(
        effectiveClientId,
        clinicId,
        selectedAppointment.id,
        payload
      );
      setAppointments((prev) =>
        prev.map((appt) => (appt.id === agAtual.id ? agAtual : appt))
      );

      // Recarregar noShows do backend
      const updatedNoShows = await listarNoShows(effectiveClientId, clinicId);
      setNoShows(updatedNoShows);
      void refreshCrmData();

      // reset
      setShowRescheduleModal(false);
      setSelectedAppointment(null);
      setRescheduleDate('');
      setRescheduleTime('');
    } catch (error) {
      console.error('Erro ao reagendar.');
      const parsed = parseApiError(error, 'Não foi possível reagendar a consulta.');
      const alternatives = parsed.alternatives?.length
        ? ` Horários alternativos: ${parsed.alternatives.join(' ou ')}.`
        : '';
      setRescheduleError(`${parsed.message}${alternatives}`);
    } finally {
      setIsProcessingReschedule(false); // Libera o botão após processar
    }
  };

  // MARCAR NO-SHOW
  const handleNoShowClick = (appointment: AgendamentoResponse) => {
    setAppointmentNoShow(appointment);
    setNoShowObs('');
    setShowNoShowModal(true);
  };

  const handleConfirmNoShow = async () => {
    if (!appointmentNoShow) return;
    try {
      const clientId = parseInt(localStorage.getItem('client_id') || '0', 10);
      const clinicId = parseInt(localStorage.getItem('clinic_id') || '0', 10);

      // Marca no-show
      const msg = await marcarNoShowAgendamento(clientId, clinicId, appointmentNoShow.id, {
        observacao: noShowObs
      });

      // [AUTOMÁTICO] recarregar noShows para aparecer na listagem imediatamente
      const updatedNoShows = await listarNoShows(clientId, clinicId);
      setNoShows(updatedNoShows);
      void refreshCrmData();

      // reset
      setShowNoShowModal(false);
      setAppointmentNoShow(null);
      setNoShowObs('');

      alert(msg);
    } catch (error) {
      console.error('Erro ao marcar falta (no-show):', error);
      alert('Não foi possível marcar como falta.');
    }
  };

  const handleEditNoShow = (n: NoShowEvent) => {
    setNoShowToEdit(n);
    setShowNoShowEditModal(true);
  };

  const handleRescheduleNoShow = (n: NoShowEvent) => {
    handleRescheduleClick({
      id: n.agendamento_id,
      client_id: n.client_id,
      clinic_id: n.clinic_id,
      lead_id: n.lead_id,
      agendamento_realizado_em: n.marcado_em,
      nome: n.nome,
      phone: n.phone,
      consulta_data: n.data_agendada,
      status: 'NO_SHOW',
    });
  };

  const handleDeleteNoShow = async (n: NoShowEvent) => {
    try {
      const clientId = parseInt(localStorage.getItem('client_id') || '0', 10);
      const clinicId = parseInt(localStorage.getItem('clinic_id') || '0', 10);
      await deletarNoShowEvent(clientId, clinicId, n.id);
      setNoShows((prev) => prev.filter((item) => item.id !== n.id));
      void refreshCrmData();
    } catch (error) {
      console.error('Erro ao excluir no-show:', error);
      alert('Não foi possível excluir esta falta.');
    }
  };

  // ==============================
  //         VENDA
  // ==============================
  const handleSaleClick = (attendance: Comparecimento) => {
    setSaleFailure(null);
    setPhoneCorrectionFeedback(null);
    setSelectedAttendance(attendance);

    // Definir a data atual para a venda
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');

    setVendaDate(`${year}-${month}-${day}`);
    setVendaTime(`${hours}:${minutes}`);

    // Pré-preencher o tratamento com o mesmo do comparecimento
    setFormVenda({
      tratamento_fechado: attendance.tratamento_orcado || '',
      valor_faturado: attendance.valor_orcamento
        ? maskCurrencyBRL(attendance.valor_orcamento.toString())
        : '',
      valor_pago: ''
    });

    setShowSaleModal(true);
  };

  const handleNewSaleClick = async (sale: Venda) => {
    try {
      const { effectiveClientId, clinicId } = getCrmAuth();
      const attendance = await obterComparecimento(
        effectiveClientId,
        clinicId,
        sale.comparecimento_id
      );
      handleSaleClick(attendance);
    } catch (error) {
      console.error('Erro ao preparar nova venda:', error);
      alert(parseApiError(error, 'Não foi possível preparar uma nova venda.').message);
    }
  };

  const handleSalvarVenda = async () => {
    if (!selectedAttendance || isSavingSale) return;
    if (!vendaDate || !vendaTime) {
      saleNeedsFocusReturn.current = true;
      setSaleFailure({ title: 'Confira os dados da venda', message: 'Informe uma data e uma hora válidas.', guidance: 'Volte ao formulário para preencher os campos. Os dados da venda foram mantidos.', canCorrectPhone: false });
      return;
    }

    setSaleFailure(null);
    setIsSavingSale(true);
    try {
      const { effectiveClientId, clinicId } = getCrmAuth();

      // Enviar como ISO sem indicação de fuso
      const vendaEmString = `${vendaDate}T${vendaTime}:00`;

      const valorFaturadoNum = formVenda.valor_faturado
        ? parseBRLToNumber(formVenda.valor_faturado)
        : undefined;
      const valorPagoNum = formVenda.valor_pago
        ? parseBRLToNumber(formVenda.valor_pago)
        : undefined;

      const dados: VendaCreate = {
        lead_id: selectedAttendance.lead_id,
        comparecimento_id: selectedAttendance.id,
        nome: selectedAttendance.nome,
        phone: selectedAttendance.phone,
        tratamento_fechado: formVenda.tratamento_fechado,
        valor_faturado: valorFaturadoNum,
        valor_pago: valorPagoNum,
        venda_data: vendaEmString
      };

      const novaVenda = await criarVenda(effectiveClientId, clinicId, dados);
      setSales((prev) => appendSaleTransaction(prev, novaVenda));
      void refreshCrmData();

      // reset
      setShowSaleModal(false);
      setSelectedAttendance(null);
      setFormVenda({ tratamento_fechado: '', valor_faturado: '', valor_pago: '' });
      setVendaDate('');
      setVendaTime('');
    } catch (error) {
      saleNeedsFocusReturn.current = true;
      setSaleFailure(getCrmSaleFailure(error));
    } finally {
      setIsSavingSale(false);
    }
  };

  const handleEditSale = (sale: Venda) => {
    setSaleToEdit(sale);
    setShowSaleEditModal(true);
  };

  const handleDeleteSale = async (sale: Venda) => {
    try {
      const clientId = parseInt(localStorage.getItem('client_id') || '0', 10);
      const clinicId = parseInt(localStorage.getItem('clinic_id') || '0', 10);
      await deletarVenda(clientId, clinicId, sale.id);
      setSales((prev) => prev.filter((s) => s.id !== sale.id));
      void refreshCrmData();
    } catch (error) {
      console.error('Erro ao excluir venda:', error);
      alert('Erro ao excluir venda.');
    }
  };

  // ==============================
  //         FILTROS
  // ==============================
  const lostLeadIds = useMemo(() => new Set(lost.map(item => item.lead_id)), [lost]);
  const normalizedSearchTerm = useMemo(() => searchTerm.trim().toLowerCase(), [searchTerm]);
  const searchDigits = useMemo(() => searchTerm.replace(/\D/g, ''), [searchTerm]);
  const saleLeadIds = useMemo(() => new Set(sales.map((sale) => sale.lead_id)), [sales]);
  const attendanceLeadIds = useMemo(
    () => new Set(attendances.map((attendance) => attendance.lead_id)),
    [attendances]
  );
  const noShowLeadIds = useMemo(
    () => new Set(noShows.map((noShow) => noShow.lead_id)),
    [noShows]
  );
  const appointmentLeadIds = useMemo(
    () => new Set(
      appointments
        .filter((appointment) => appointment.status !== 'NO_SHOW')
        .map((appointment) => appointment.lead_id)
    ),
    [appointments]
  );

  const filteredLeads = useMemo(() => leads.filter((lead) => {
    const matchesSearch =
      normalizedSearchTerm === '' ||
      lead.name?.toLowerCase().includes(normalizedSearchTerm) ||
      lead.phone?.includes(searchTerm) ||
      (searchDigits.length >= 3 && lead.phone?.replace(/\D/g, '').includes(searchDigits));

    const matchesDate = lead.data_entrada ? isDateInRange(lead.data_entrada) : true;

    const matchesSource =
      selectedSource === '' ||
      lead.source_id === selectedSource;

    const isCurrentStage =
      !saleLeadIds.has(lead.id) &&
      !attendanceLeadIds.has(lead.id) &&
      !noShowLeadIds.has(lead.id) &&
      !appointmentLeadIds.has(lead.id);

    return !lostLeadIds.has(lead.id) && matchesSearch && matchesDate && matchesSource && isCurrentStage;
  }).sort((a, b) => {
    // Ordenar por data de entrada, mais recente primeiro
    const dateA = a.data_entrada ? new Date(a.data_entrada).getTime() : 0;
    const dateB = b.data_entrada ? new Date(b.data_entrada).getTime() : 0;
    return dateB - dateA;
  }), [
    leads,
    lostLeadIds,
    normalizedSearchTerm,
    searchDigits,
    searchTerm,
    isDateInRange,
    selectedSource,
    saleLeadIds,
    attendanceLeadIds,
    noShowLeadIds,
    appointmentLeadIds,
  ]);

  // Filtro de Agendamentos
  const filteredAppointments = useMemo(() => appointments
    .filter((appt) => {
      const matchesSearch =
        normalizedSearchTerm === '' ||
        appt.nome?.toLowerCase().includes(normalizedSearchTerm) ||
        appt.phone?.includes(searchTerm) ||
        (searchDigits.length >= 3 && appt.phone?.replace(/\D/g, '').includes(searchDigits));

      const matchesDate = appt.consulta_data ? isDateInRange(appt.consulta_data) : true;

      const matchesSource =
        selectedSource === '' ||
        appt.midia === selectedSource;

      const isCurrentStage =
        appt.status !== 'NO_SHOW' &&
        !saleLeadIds.has(appt.lead_id) &&
        !attendanceLeadIds.has(appt.lead_id) &&
        !noShowLeadIds.has(appt.lead_id);

      return !lostLeadIds.has(appt.lead_id) && matchesSearch && matchesDate && matchesSource && isCurrentStage;
    })
    .sort((a, b) => {
      // Ordenar por data de agendamento, mais recente primeiro
      const dateA = a.agendamento_realizado_em ? new Date(a.agendamento_realizado_em).getTime() : 0;
      const dateB = b.agendamento_realizado_em ? new Date(b.agendamento_realizado_em).getTime() : 0;
      return dateB - dateA;
    }), [
      appointments,
      lostLeadIds,
    normalizedSearchTerm,
      searchDigits,
      searchTerm,
      isDateInRange,
      selectedSource,
      saleLeadIds,
      attendanceLeadIds,
      noShowLeadIds,
    ]);

  // Filtro de No-Shows (Faltas)
  const filteredNoShows = useMemo(() => noShows
    .filter((n) => {
      const matchesSearch =
        normalizedSearchTerm === '' ||
        (n.observacao && n.observacao.toLowerCase().includes(normalizedSearchTerm)) ||
        (n.nome && n.nome.toLowerCase().includes(normalizedSearchTerm)) ||
        (n.phone && n.phone.includes(searchTerm)) ||
        (searchDigits.length >= 3 && n.phone?.replace(/\D/g, '').includes(searchDigits));

      const matchesDate = n.marcado_em ? isDateInRange(n.marcado_em) : true;

      const isCurrentStage =
        !saleLeadIds.has(n.lead_id) &&
        !attendanceLeadIds.has(n.lead_id);

      return !lostLeadIds.has(n.lead_id) && matchesSearch && matchesDate && isCurrentStage;
    })
    .sort((a, b) => {
      // Ordenar por data da falta, mais recente primeiro
      const dateA = a.marcado_em ? new Date(a.marcado_em).getTime() : 0;
      const dateB = b.marcado_em ? new Date(b.marcado_em).getTime() : 0;
      return dateB - dateA;
    }), [
      noShows,
      lostLeadIds,
    normalizedSearchTerm,
      searchDigits,
      searchTerm,
      isDateInRange,
      saleLeadIds,
      attendanceLeadIds,
    ]);

  // Filtro de Comparecimentos
  const filteredAttendances = useMemo(() => attendances
    .filter((comp) => {
      const matchesSearch =
        normalizedSearchTerm === '' ||
        comp.nome?.toLowerCase().includes(normalizedSearchTerm) ||
        comp.phone?.includes(searchTerm) ||
        (searchDigits.length >= 3 && comp.phone?.replace(/\D/g, '').includes(searchDigits));

      const matchesDate = comp.compareceu_em ? isDateInRange(comp.compareceu_em) : true;

      const matchesSource =
        selectedSource === '' ||
        comp.midia === selectedSource;

      const isCurrentStage = !saleLeadIds.has(comp.lead_id);

      return !lostLeadIds.has(comp.lead_id) && matchesSearch && matchesDate && matchesSource && isCurrentStage;
    })
    .sort((a, b) => {
      // Ordenar por data de comparecimento, mais recente primeiro
      const dateA = a.compareceu_em ? new Date(a.compareceu_em).getTime() : 0;
      const dateB = b.compareceu_em ? new Date(b.compareceu_em).getTime() : 0;
      return dateB - dateA;
    }), [
      attendances,
      lostLeadIds,
    normalizedSearchTerm,
      searchDigits,
      searchTerm,
      isDateInRange,
      selectedSource,
      saleLeadIds,
    ]);

  // Filtro de Vendas
  const filteredSales = useMemo(() => sales
    .filter((venda) => {
      const matchesSearch =
        normalizedSearchTerm === '' ||
        venda.nome?.toLowerCase().includes(normalizedSearchTerm) ||
        venda.phone?.includes(searchTerm) ||
        (searchDigits.length >= 3 && venda.phone?.replace(/\D/g, '').includes(searchDigits));

      const matchesDate = venda.venda_data ? isDateInRange(venda.venda_data) : true;

      // Vendas não têm midia, então não filtramos por isso

      return !lostLeadIds.has(venda.lead_id) && matchesSearch && matchesDate;
    })
    .sort((a, b) => {
      // Ordenar por data da venda, mais recente primeiro
      const dateA = a.venda_data ? new Date(a.venda_data).getTime() : 0;
      const dateB = b.venda_data ? new Date(b.venda_data).getTime() : 0;
      return dateB - dateA;
    }), [sales, lostLeadIds, normalizedSearchTerm, searchDigits, searchTerm, isDateInRange]);

  const visibleLeads = useMemo(
    () => filteredLeads.slice(0, visibleCounts.leads),
    [filteredLeads, visibleCounts.leads]
  );
  const visibleAppointments = useMemo(
    () => filteredAppointments.slice(0, visibleCounts.appointments),
    [filteredAppointments, visibleCounts.appointments]
  );
  const visibleNoShows = useMemo(
    () => filteredNoShows.slice(0, visibleCounts.noShows),
    [filteredNoShows, visibleCounts.noShows]
  );
  const visibleAttendances = useMemo(
    () => filteredAttendances.slice(0, visibleCounts.attendances),
    [filteredAttendances, visibleCounts.attendances]
  );
  const visibleSales = useMemo(
    () => filteredSales.slice(0, visibleCounts.sales),
    [filteredSales, visibleCounts.sales]
  );

  const visibleLost = useMemo(() => lost.slice(0, visibleCounts.lost), [lost, visibleCounts.lost]);

  const activeVisibleLeadIds = useMemo(() => {
    switch (activeTab) {
      case 'leads':
        return visibleLeads.map((lead) => lead.id);
      case 'appointments':
        return visibleAppointments.map((appointment) => appointment.lead_id);
      case 'noShows':
        return visibleNoShows.map((noShow) => noShow.lead_id);
      case 'attendances':
        return visibleAttendances.map((attendance) => attendance.lead_id);
      case 'lost':
        return visibleLost.map(item => item.lead_id);
      case 'sales':
        return visibleSales.map((sale) => sale.lead_id);
      default:
        return [];
    }
  }, [activeTab, visibleLeads, visibleAppointments, visibleNoShows, visibleAttendances, visibleSales, visibleLost]);

  const activeVisibleLeadIdsKey = useMemo(
    () => Array.from(new Set(activeVisibleLeadIds)).join(','),
    [activeVisibleLeadIds]
  );

  const activeVisibleCount = useMemo(() => {
    switch (activeTab) {
      case 'leads':
        return visibleLeads.length;
      case 'appointments':
        return visibleAppointments.length;
      case 'noShows':
        return visibleNoShows.length;
      case 'attendances':
        return visibleAttendances.length;
      case 'lost':
        return visibleLost.length;
      case 'sales':
        return visibleSales.length;
      default:
        return 0;
    }
  }, [activeTab, visibleLeads.length, visibleAppointments.length, visibleNoShows.length, visibleAttendances.length, visibleSales.length, visibleLost.length]);

  useEffect(() => {
    if (loading || !activeVisibleLeadIdsKey) return;

    const requestId = ++tagsRequestIdRef.current;
    setIsTagsLoading(true);

    loadLeadTagsBatch(activeVisibleLeadIds)
      .catch((error) => {
        console.error('[CRM] Erro ao carregar tags visíveis:', error);
      })
      .finally(() => {
        if (requestId === tagsRequestIdRef.current) {
          setIsTagsLoading(false);
        }
      });
  }, [activeVisibleLeadIdsKey, activeVisibleLeadIds, loadLeadTagsBatch, loading]);

  const handleLoadMore = useCallback(async (tab: CrmTabKey) => {
    if (loadingMoreTab || exhaustedTabs[tab]) return;

    const loadedItemsByTab: Record<CrmTabKey, number> = {
      leads: leads.length,
      appointments: appointments.length,
      noShows: noShows.length,
      attendances: attendances.length,
      sales: sales.length,
      lost: lost.length,
    };

    if (loadedItemsByTab[tab] > visibleCounts[tab]) {
      setVisibleCounts(prev => ({
        ...prev,
        [tab]: prev[tab] + CRM_VISIBLE_BATCH_SIZE
      }));
      return;
    }

    const { effectiveClientId, clinicId } = getCrmAuth();
    setLoadingMoreTab(tab);
    let receivedItems = 0;
    let newItems = 0;

    try {
      switch (tab) {
        case 'leads': {
          const response = await listarCrmPagina(
            effectiveClientId,
            clinicId,
            'leads',
            leads.length,
            CRM_VISIBLE_BATCH_SIZE,
            crmListParams
          );
          receivedItems = response.items.length;
          newItems = countNewItemsById(leads, response.items);
          setLeads(prev => mergeById(prev, response.items));
          setCrmTotals(prev => ({ ...prev, leads: response.total }));
          break;
        }
        case 'appointments': {
          const response = await listarCrmPagina(
            effectiveClientId,
            clinicId,
            'appointments',
            appointments.length,
            CRM_VISIBLE_BATCH_SIZE,
            crmListParams
          );
          receivedItems = response.items.length;
          newItems = countNewItemsById(appointments, response.items);
          setAppointments(prev => mergeById(prev, response.items));
          setCrmTotals(prev => ({ ...prev, appointments: response.total }));
          break;
        }
        case 'noShows': {
          const response = await listarCrmPagina(
            effectiveClientId,
            clinicId,
            'noShows',
            noShows.length,
            CRM_VISIBLE_BATCH_SIZE,
            crmListParams
          );
          receivedItems = response.items.length;
          newItems = countNewItemsById(noShows, response.items);
          setNoShows(prev => mergeById(prev, response.items));
          setCrmTotals(prev => ({ ...prev, noShows: response.total }));
          break;
        }
        case 'attendances': {
          const response = await listarCrmPagina(
            effectiveClientId,
            clinicId,
            'attendances',
            attendances.length,
            CRM_VISIBLE_BATCH_SIZE,
            crmListParams
          );
          receivedItems = response.items.length;
          newItems = countNewItemsById(attendances, response.items);
          setAttendances(prev => mergeById(prev, response.items));
          setCrmTotals(prev => ({ ...prev, attendances: response.total }));
          break;
        }
        case 'lost': {
          const response = await listarCrmPagina(effectiveClientId, clinicId, 'lost', lost.length, CRM_VISIBLE_BATCH_SIZE, crmListParams);
          receivedItems = response.items.length;
          newItems = countNewItemsById(lost, response.items);
          setLost(prev => mergeById(prev, response.items));
          setCrmTotals(prev => ({ ...prev, lost: response.total }));
          break;
        }
        case 'sales': {
          const response = await listarCrmPagina(
            effectiveClientId,
            clinicId,
            'sales',
            sales.length,
            CRM_VISIBLE_BATCH_SIZE,
            crmListParams
          );
          receivedItems = response.items.length;
          newItems = countNewItemsById(sales, response.items);
          setSales(prev => mergeById(prev, response.items));
          setCrmTotals(prev => ({ ...prev, sales: response.total }));
          break;
        }
        default:
          break;
      }

      if (receivedItems === 0 || newItems === 0) {
        setExhaustedTabs(prev => ({
          ...prev,
          [tab]: true
        }));
        return;
      }

      setVisibleCounts(prev => ({
        ...prev,
        [tab]: prev[tab] + CRM_VISIBLE_BATCH_SIZE
      }));
    } catch (error) {
      console.error('[CRM] Erro ao carregar mais dados:', error);
      alert('Erro ao carregar mais dados. Por favor, tente novamente.');
    } finally {
      setLoadingMoreTab(null);
    }
  }, [
    appointments.length,
    attendances.length,
    crmListParams,
    exhaustedTabs,
    getCrmAuth,
    leads.length,
    loadingMoreTab,
    lost,
    noShows.length,
    sales.length,
    visibleCounts,
  ]);

  const renderLoadMore = (tab: CrmTabKey, visibleCount: number, totalCount: number) => {
    if (visibleCount >= totalCount || exhaustedTabs[tab]) return null;

    const isLoadingMore = loadingMoreTab === tab;

    return (
      <div
        ref={tab === activeTab ? loadMoreSentinelRef : undefined}
        className="col-span-full flex justify-center py-4"
      >
        {isLoadingMore ? (
          <div className="inline-flex items-center gap-2 text-sm text-gray-500">
            <RefreshCw className="w-4 h-4 animate-spin" />
            Carregando mais...
          </div>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleLoadMore(tab)}
          >
            Carregar mais
          </Button>
        )}
      </div>
    );
  };

  const getTabCount = useCallback((tab: CrmTabKey) => crmTotals[tab] || 0, [crmTotals]);

  const handleOpenChat = useCallback(async (contact: CrmChatContact) => {
    const phone = contact.phone?.trim();

    if (!phone) {
      alert('Este contato não possui telefone cadastrado.');
      return;
    }

    const name = getCrmChatContactName(contact);

    try {
      const response = await getContacts({
        search: phone,
        limit: 50,
        lightweight: true,
        show_archived: true,
      });
      const leadId = contact.lead_id ?? contact.id;
      const normalizedPhone = phone.replace(/\D/g, '');
      const exactPhoneMatches = response.contacts.filter(
        (candidate) => candidate.phone.replace(/\D/g, '') === normalizedPhone,
      );
      const resolvedContact =
        response.contacts.find(
          (candidate) =>
            contact.contact_id != null && candidate.id === contact.contact_id,
        ) ||
        response.contacts.find(
          (candidate) =>
            leadId != null && candidate.lead_id === leadId,
        ) ||
        (exactPhoneMatches.length === 1 ? exactPhoneMatches[0] : undefined);

      if (!resolvedContact) {
        alert(
          'Não foi possível identificar um único contato para esta conversa. Confira os cadastros pelo telefone antes de enviar.',
        );
        return;
      }

      navigate('/chat', {
        state: {
          selectedPhone: resolvedContact.phone,
          selectedContact: {
            id: resolvedContact.id,
            name: resolvedContact.name || name,
            phone: resolvedContact.phone,
            photo: resolvedContact.photo || contact.photo || '',
            possible_duplicate: resolvedContact.possible_duplicate,
            duplicate_contact_id: resolvedContact.duplicate_contact_id,
          },
        },
      });
    } catch (error) {
      const parsed = parseApiError(
        error,
        'Não foi possível localizar o contato desta conversa.',
      );
      alert(parsed.message);
    }
  }, [navigate]);

  useEffect(() => {
    if (loading || isCrmRefreshing || loadingMoreTab) return;

    const sentinel = loadMoreSentinelRef.current;
    const totalCount = getTabCount(activeTab);

    if (!sentinel || activeVisibleCount >= totalCount) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          handleLoadMore(activeTab);
        }
      },
      {
        root: null,
        rootMargin: '700px 0px',
        threshold: 0,
      }
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [
    activeTab,
    activeVisibleCount,
    getTabCount,
    handleLoadMore,
    isCrmRefreshing,
    loading,
    loadingMoreTab,
  ]);

  // Componente de navegação mobile
  const MobileTabNavigation = () => {
    const currentTab = tabsList[currentTabIndex];
    const currentCount = getTabCount(activeTab);

    return (
      <div className={clsx("rounded-2xl shadow-sm border mb-6 overflow-hidden", isDark ? "bg-gray-800 border-gray-700" : "bg-white border-slate-200")}>
        {/* Header da tab atual */}
        <div className="bg-sminy-primary text-white p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <currentTab.icon className="w-6 h-6" />
              <div>
                <h2 className="text-lg font-semibold">{currentTab.label}</h2>
                <p className="text-sm text-white/85">
                  {currentCount} {currentCount === 1 ? 'item' : 'itens'}
                </p>
              </div>
            </div>
            <div className="text-sm font-medium bg-white/20 px-3 py-1 rounded-full">
              {currentTabIndex + 1} / {tabsList.length}
            </div>
          </div>
        </div>

        {/* Navegação */}
        <div className={clsx("flex items-center justify-between p-4", isDark ? "bg-gray-900/40" : "bg-slate-50")}>
          <button
            onClick={goToPreviousTab}
            disabled={currentTabIndex === 0}
            className={clsx(
              "flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all",
              currentTabIndex === 0
                ? isDark ? 'bg-gray-800 text-gray-600 cursor-not-allowed' : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                : isDark ? 'bg-gray-800 text-gray-200 hover:bg-gray-700 hover:text-sminy-primary shadow-sm' : 'bg-white text-slate-700 hover:bg-sminy-primary/5 hover:text-sminy-primary shadow-sm'
            )}
          >
            <ChevronLeft className="w-4 h-4" />
            {currentTabIndex > 0 && <span className="text-sm">{tabsList[currentTabIndex - 1].shortLabel}</span>}
          </button>

          {/* Indicadores de progresso */}
          <div className="flex items-center gap-2">
            {tabsList.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveTab(tabsList[index].key)}
                className={clsx(
                  "w-3 h-3 rounded-full transition-all",
                  index === currentTabIndex
                    ? 'bg-sminy-primary'
                    : index < currentTabIndex
                      ? 'bg-sminy-primary/40'
                      : isDark ? 'bg-gray-600' : 'bg-slate-300'
                )}
              />
            ))}
          </div>

          <button
            onClick={goToNextTab}
            disabled={currentTabIndex === tabsList.length - 1}
            className={clsx(
              "flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all",
              currentTabIndex === tabsList.length - 1
                ? isDark ? 'bg-gray-800 text-gray-600 cursor-not-allowed' : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                : isDark ? 'bg-gray-800 text-gray-200 hover:bg-gray-700 hover:text-sminy-primary shadow-sm' : 'bg-white text-slate-700 hover:bg-sminy-primary/5 hover:text-sminy-primary shadow-sm'
            )}
          >
            {currentTabIndex < tabsList.length - 1 && <span className="text-sm">{tabsList[currentTabIndex + 1].shortLabel}</span>}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  // ==============================
  //       RENDER PRINCIPAL
  // ==============================
  if (loading) {
    return (
      <div className={clsx("min-h-screen flex items-center justify-center", isDark ? "bg-[#111827]" : "bg-gradient-to-b from-gray-50 to-white")}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-sminy-primary/30 border-t-sminy-primary mx-auto mb-4"></div>
          <p className={isDark ? "text-gray-400" : "text-slate-600"}>Carregando dados...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={clsx("min-h-screen flex items-center justify-center p-4", isDark ? "bg-[#111827]" : "bg-gradient-to-b from-gray-50 to-white")}>
        <div className={clsx("p-8 rounded-2xl border shadow-xl max-w-md w-full", isDark ? "border-gray-700 bg-gray-800" : "border-slate-200 bg-white")}>
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-rose-100 text-rose-600 mb-4 mx-auto">
            <X className="w-6 h-6" />
          </div>
          {lossFeedback && <p role="status" className="mb-3 text-sm text-sminy-primary">{lossFeedback} A alteração foi salva, mas a lista não pôde ser atualizada.</p>}
          <h2 className={clsx("text-xl font-semibold text-center mb-2", isDark ? "text-white" : "text-slate-900")}>Erro ao carregar dados</h2>
          <p className={clsx("text-center mb-6", isDark ? "text-gray-400" : "text-slate-600")}>{error}</p>
          <Button
            variant="primary"
            className="w-full"
            onClick={() => window.location.reload()}
          >
            Tentar novamente
          </Button>
        </div>
      </div>
    );
  }

  return (
    <CrmPhoneCorrectionProvider getAuth={getCrmAuth} onSaved={handlePhoneCorrected} onOpenChange={open => { setIsPhoneCorrectionOpen(open); if (open) setPhoneCorrectionFeedback(null); }}>
    <CrmLossProvider enabled={lossesEnabled} getAuth={getCrmAuth} formatDate={formatDateTime} Modal={Modal}
      onChanged={async stage => {
        setLossFeedback(stage === 'lost' ? 'Contato marcado como perdido. Confira o período e os filtros para localizá-lo.' : `Contato reaberto em ${CRM_STAGE_LABELS[stage]}. Confira o período e os filtros para localizá-lo.`);
        setActiveTab(stage);
        await refreshCrmData();
      }}>
    <div className={pageClassName}>
      {/* Header */}
      {/*<header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-xl font-semibold text-sminy-primary">
              DentalCRM
            </h1>
            
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                size="sm" 
                icon={<RefreshCw className="w-4 h-4" />}
                onClick={() => window.location.reload()}
              >
                Atualizar
              </Button>
              
              <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-sminy-primary">
                <User className="w-5 h-5" />
              </div>
            </div>
          </div>
        </div>
      </header>*/}
      <main className="mx-auto w-full max-w-[1600px] px-4 py-4 sm:px-6 sm:py-6 lg:px-8 2xl:px-10">
        {/* Barra de Filtros */}
        <div className={clsx(panelClassName, "mb-6")}>
          <div className={clsx(
            "min-w-0",
            activeTab === 'leads'
              ? "flex flex-wrap items-center gap-3"
              : "flex flex-col justify-between gap-4 xl:flex-row xl:items-start"
          )}>
            <div className={clsx(
              "min-w-0",
              activeTab === 'leads'
                ? "grid min-w-[min(100%,20rem)] flex-[1_1_20rem] grid-cols-1 gap-3 sm:grid-cols-[repeat(auto-fit,minmax(9rem,1fr))]"
                : "flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center xl:flex-1"
            )}>
              {/* Filtro Dropdown para períodos predefinidos */}
              <div className={clsx("min-w-0", activeTab === 'leads' ? "w-full" : "w-full sm:w-48")}>
                <CalendarFilterDropdown
                  onFilterSelect={handlePresetFilterSelect}
                  currentFilter={filterType}
                  labels={{
                    today: 'Hoje',
                    tomorrow: 'Amanhã',
                    yesterday: 'Ontem',
                    thisWeek: 'Esta semana',
                    lastWeek: 'Semana passada',
                    thisMonth: 'Este mês',
                    lastMonth: 'Mês passado',
                    custom: 'Personalizado',
                    all: 'Todas as datas',
                    filterByDate: 'Filtrar por período'
                  }}
                />
              </div>

              {/* Calendário para filtro personalizado */}
              <div className={clsx("min-w-0", activeTab === 'leads' ? "w-full" : "w-full sm:w-64")}>
                <CalendarFilter
                  onFilterChange={handleDateFilterChange}
                  initialDateRange={dateRange}
                  singleDate={false}
                  dateFormat="dd/MM/yyyy"
                  labels={{
                    placeholder: "Selecionar período...",
                    clear: "Limpar",
                    apply: "Aplicar",
                    today: "Hoje",
                    monthLabel: "de"
                  }}
                  className="w-full"
                />
              </div>

              <div className={clsx("relative min-w-0", activeTab === 'leads' ? "w-full" : "w-full sm:w-64")}>
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Buscar por nome ou telefone..."
                  className={fieldWithIconClassName}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div
                ref={filterDropdownRef}
                className={clsx(
                  "relative w-full sm:w-auto",
                  activeTab === 'leads' && "sm:min-w-[7rem]"
                )}
              >
                <Button
                  variant={activeAdditionalFilterCount > 0 ? 'primary' : 'secondary'}
                  size="sm"
                  icon={<Filter className="h-4 w-4" />}
                  onClick={() => setShowFilterPanel((current) => !current)}
                  className="h-10 w-full whitespace-nowrap sm:w-auto"
                  aria-expanded={showFilterPanel}
                  aria-controls="crm-additional-filters"
                >
                  Filtros
                  {activeAdditionalFilterCount > 0 && (
                    <span
                      className="ml-1 inline-flex min-w-5 items-center justify-center rounded-full bg-white/20 px-1.5 py-0.5 text-[10px]"
                      aria-label={`${activeAdditionalFilterCount} filtros adicionais ativos`}
                    >
                      {activeAdditionalFilterCount}
                    </span>
                  )}
                </Button>

                {showFilterPanel && (
                  <CrmAdditionalFiltersPopover
                    availableTags={availableFilterTags}
                    isTagsLoading={isFilterTagsLoading}
                    tagsError={filterTagsError}
                    selectedTagIds={selectedTagIds}
                    patientFilter={patientFilter}
                    onTagIdsChange={setSelectedTagIds}
                    onPatientFilterChange={setPatientFilter}
                    onClear={clearAdditionalFilters}
                    onClose={() => setShowFilterPanel(false)}
                    onRetryTags={loadAvailableFilterTags}
                  />
                )}
              </div>
            </div>

            {(isMobile || activeTab === 'leads') && (
              <div className={clsx(
                activeTab === 'leads'
                  ? "ml-auto grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:max-w-full sm:flex-wrap sm:items-center sm:justify-end"
                  : "flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-end xl:flex-shrink-0"
              )}>
                {isMobile && (
                  <div className={clsx(activeTab === 'leads' ? "col-span-2 sm:col-span-1 sm:w-auto" : "w-full sm:w-auto")}>
                    <CrmViewModeToggle value={viewMode} onChange={handleViewModeChange} />
                  </div>
                )}
                {activeTab === 'leads' && (
                  <>
                    <Button
                      variant="primary"
                      size="sm"
                      icon={<PlusCircle className="w-4 h-4" />}
                      onClick={handleNewLead}
                      className="h-9 w-full whitespace-nowrap sm:w-auto sm:shrink-0 sm:px-3"
                      title="Novo Lead"
                      aria-label="Novo Lead"
                    >
                      Lead
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<Download className="w-4 h-4" />}
                      onClick={async () => {
                        try {
                          const { effectiveClientId, clinicId } = getCrmAuth();
                          const startDate = formatCrmDateParam(dateRange.startDate);
                          const endDate = formatCrmDateParam(dateRange.endDate);
                          await exportLeadsToExcel(
                            effectiveClientId,
                            clinicId,
                            startDate,
                            endDate,
                            lossesEnabled
                          );
                        } catch (error) {
                          console.error('Erro ao exportar:', error);
                          alert('Erro ao exportar relatório. Nenhum arquivo foi gerado. Por favor, tente novamente.');
                        }
                      }}
                      className="h-9 w-full whitespace-nowrap sm:w-auto sm:shrink-0 sm:px-3"
                      title="Exportar Excel"
                      aria-label="Exportar Excel"
                    >
                      Exportar
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>

        </div>

        {/* Navegação Responsiva */}
        {isMobile ? (
          <MobileTabNavigation />
        ) : (
          /* Tabs Desktop */
          <div className={clsx("mb-6 rounded-2xl border shadow-sm", isDark ? "bg-gray-800 border-gray-700" : "bg-white border-slate-200")}>
            <div className="flex min-w-0 flex-col gap-2 p-2 xl:flex-row xl:items-center xl:justify-between">
              <div className="flex min-w-0 flex-1 gap-1 overflow-x-auto scrollbar-hide">
                {tabsList.map((tab) => {
                  const isActive = activeTab === tab.key;
                  const count = getTabCount(tab.key);

                  return (
                    <button
                      key={tab.key}
                      className={clsx(
                        "flex flex-shrink-0 items-center gap-2 whitespace-nowrap rounded-xl px-3 py-2.5 text-sm font-medium transition-colors sm:px-4",
                        isActive
                          ? 'bg-sminy-primary/10 text-sminy-primary shadow-sm'
                          : isDark
                            ? 'text-gray-400 hover:bg-gray-700/40 hover:text-gray-200'
                            : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                      )}
                      onClick={() => setActiveTab(tab.key)}
                    >
                      <tab.icon className="w-4 h-4" />
                      <span>{tab.label}</span>
                      <Badge variant={isActive ? 'primary' : 'default'} className="ml-1">
                        {count}
                      </Badge>
                    </button>
                  );
                })}
              </div>
              <div className={clsx(
                "flex w-full flex-shrink-0 items-center justify-end border-t pt-2 xl:w-auto xl:border-l xl:border-t-0 xl:pl-3 xl:pt-0",
                isDark ? "border-gray-700" : "border-slate-200"
              )}>
                <CrmViewModeToggle value={viewMode} onChange={handleViewModeChange} />
              </div>
            </div>
          </div>
        )}

        {(isTagsLoading || isCrmRefreshing) && (
          <div className={clsx("mb-4 text-xs", isDark ? "text-gray-400" : "text-slate-500")}>
            {isCrmRefreshing ? 'Atualizando dados...' : 'Atualizando tags...'}
          </div>
        )}

        {lossFeedback && <p role="status" className="mb-4 rounded-xl bg-sminy-primary/10 p-3 text-sm text-sminy-primary">{lossFeedback}</p>}
        {phoneCorrectionFeedback && <p role="status" className={clsx('mb-4 rounded-xl border p-3 text-sm', isDark ? 'border-sminy-dark-border bg-sminy-dark-surface text-sminy-dark-text-secondary' : 'border-sminy-border bg-sminy-blue-soft text-sminy-text-secondary')}>{phoneCorrectionFeedback}</p>}
        {activeTab === 'lost' && lossesEnabled && <div>
          <CrmLostView items={visibleLost} mode={viewMode} tagsCache={tagsCache} onOpenChat={handleOpenChat} onTagsUpdated={debouncedLoadLeadTags} />
          {renderLoadMore('lost', visibleLost.length, getTabCount('lost'))}
        </div>}

        {/* LEADS TAB */}
        {activeTab === 'leads' && (
          <div>
            <div className={viewMode === 'cards'
              ? 'grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6'
              : 'space-y-3'
            }>
              {filteredLeads.length === 0 ? (
                <CrmEmptyState icon={Target} title="Nenhum lead encontrado">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2 text-sminy-primary"
                    icon={<PlusCircle className="w-4 h-4" />}
                    onClick={handleNewLead}
                  >
                    Criar novo lead
                  </Button>
                </CrmEmptyState>
              ) : viewMode === 'list' ? (
                <>
                  <LeadsListView
                    leads={visibleLeads}
                    appointments={appointments}
                    tagsCache={tagsCache}
                    onEdit={handleEditLead}
                    onCreateAppointment={handleOpenAppointmentModal}
                    onDelete={handleDeleteLead}
                    onOpenChat={handleOpenChat}
                    onTagsUpdated={debouncedLoadLeadTags}
                  />
                  {renderLoadMore('leads', visibleLeads.length, getTabCount('leads'))}
                </>
              ) : (
                <>
                  {visibleLeads.map((lead) => (
                  <LeadCard
                    key={lead.id}
                    lead={lead}
                    appointments={appointments} // Passando a lista de agendamentos
                    leadTags={tagsCache[lead.id]} // Passando as tags do lead
                    onEdit={handleEditLead}
                    onCreateAppointment={handleOpenAppointmentModal}
                    onDelete={handleDeleteLead}
                    onOpenChat={handleOpenChat}
                    onTagsUpdated={debouncedLoadLeadTags}
                  />
                  ))}
                  {renderLoadMore('leads', visibleLeads.length, getTabCount('leads'))}
                </>
              )}
            </div>
          </div>
        )}

        {/* AGENDAMENTOS TAB */}
        {activeTab === 'appointments' && (
          <div>
            {appointmentOperationError && (
              <div
                role="alert"
                className={clsx(
                  "mb-4 flex items-start justify-between gap-3 rounded-xl border px-3 py-2 text-sm leading-relaxed",
                  isDark ? "border-red-900/60 bg-red-950/40 text-red-200" : "border-red-200 bg-red-50 text-red-700"
                )}
              >
                <span>{appointmentOperationError}</span>
                <button
                  type="button"
                  onClick={() => setAppointmentOperationError(null)}
                  className="rounded p-1 hover:bg-black/5"
                  aria-label="Fechar mensagem de erro"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
            <div className={viewMode === 'cards'
              ? 'grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6'
              : 'space-y-3'
            }>
            {filteredAppointments.length === 0 ? (
              <CrmEmptyState icon={CalendarDays} title="Nenhum agendamento encontrado" />
            ) : viewMode === 'list' ? (
              <>
                <AppointmentsListView
                  appointments={visibleAppointments}
                  attendances={attendances}
                  noShows={noShows}
                  tagsCache={tagsCache}
                  onAttendanceClick={handleAttendanceClick}
                  onRescheduleClick={handleRescheduleClick}
                  onNoShowClick={handleNoShowClick}
                  onEditClick={handleEditAppointment}
                  onDeleteClick={handleDeleteAppointment}
                  onOpenChat={handleOpenChat}
                  onTagsUpdated={debouncedLoadLeadTags}
                />
                {renderLoadMore('appointments', visibleAppointments.length, getTabCount('appointments'))}
              </>
            ) : (
              <>
                {visibleAppointments.map((appt) => (
                  <AppointmentCard
                    key={appt.id}
                    appointment={appt}
                    attendances={attendances} // Adicione esta prop
                    noShows={noShows} // Adicione esta prop
                    appointmentTags={tagsCache[appt.lead_id]} // Passando as tags do agendamento
                    onAttendanceClick={handleAttendanceClick}
                    onRescheduleClick={handleRescheduleClick}
                    onNoShowClick={handleNoShowClick}
                    onEditClick={handleEditAppointment}
                    onDeleteClick={handleDeleteAppointment}
                    onOpenChat={handleOpenChat}
                    onTagsUpdated={debouncedLoadLeadTags}
                  />
                ))}
                {renderLoadMore('appointments', visibleAppointments.length, getTabCount('appointments'))}
              </>
            )}
            </div>
          </div>
        )}

        {/* NO-SHOW TAB (FALTAS) */}
        {activeTab === 'noShows' && (
          <div className={viewMode === 'cards'
            ? 'grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6'
            : 'space-y-3'
          }>
            {filteredNoShows.length === 0 ? (
              <CrmEmptyState icon={XCircle} title="Nenhuma falta encontrada" />
            ) : viewMode === 'list' ? (
              <>
                <NoShowsListView
                  noShows={visibleNoShows}
                  tagsCache={tagsCache}
                  onRescheduleClick={handleRescheduleNoShow}
                  onEditClick={handleEditNoShow}
                  onDeleteClick={handleDeleteNoShow}
                  onOpenChat={handleOpenChat}
                  onTagsUpdated={debouncedLoadLeadTags}
                />
                {renderLoadMore('noShows', visibleNoShows.length, getTabCount('noShows'))}
              </>
            ) : (
              <>
                {visibleNoShows.map((n) => (
                  <NoShowCard
                    key={n.id}
                    noShow={n}
                    noShowTags={tagsCache[n.lead_id]} // Passando as tags do no-show
                    onRescheduleClick={handleRescheduleNoShow}
                    onEditClick={handleEditNoShow}
                    onDeleteClick={handleDeleteNoShow}
                    onOpenChat={handleOpenChat}
                    onTagsUpdated={debouncedLoadLeadTags}
                  />
                ))}
                {renderLoadMore('noShows', visibleNoShows.length, getTabCount('noShows'))}
              </>
            )}
          </div>
        )}

        {/* COMPARECIMENTOS TAB */}
        {activeTab === 'attendances' && (
          <div className={viewMode === 'cards'
            ? 'grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6'
            : 'space-y-3'
          }>
            {filteredAttendances.length === 0 ? (
              <CrmEmptyState icon={Clock} title="Nenhum comparecimento encontrado" />
            ) : viewMode === 'list' ? (
              <>
                <AttendancesListView
                  attendances={visibleAttendances}
                  sales={sales}
                  tagsCache={tagsCache}
                  onSaleClick={handleSaleClick}
                  onEditClick={handleEditAttendance}
                  onDeleteClick={handleDeleteAttendance}
                  onOpenChat={handleOpenChat}
                  onTagsUpdated={debouncedLoadLeadTags}
                />
                {renderLoadMore('attendances', visibleAttendances.length, getTabCount('attendances'))}
              </>
            ) : (
              <>
                {visibleAttendances.map((comp) => (
                  <AttendanceCard
                    key={comp.id}
                    attendance={comp}
                    sales={sales} // Nova prop
                    attendanceTags={tagsCache[comp.lead_id]} // Passando as tags do comparecimento
                    onSaleClick={handleSaleClick}
                    onEditClick={handleEditAttendance}
                    onDeleteClick={handleDeleteAttendance}
                    onOpenChat={handleOpenChat}
                    onTagsUpdated={debouncedLoadLeadTags}
                  />
                ))}
                {renderLoadMore('attendances', visibleAttendances.length, getTabCount('attendances'))}
              </>
            )}
          </div>
        )}

        {/* VENDAS TAB */}
        {activeTab === 'sales' && (
          <div className={viewMode === 'cards'
            ? 'grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6'
            : 'space-y-3'
          }>
            {filteredSales.length === 0 ? (
              <CrmEmptyState icon={DollarSign} title="Nenhuma venda encontrada" />
            ) : viewMode === 'list' ? (
              <>
                <SalesListView
                  sales={visibleSales}
                  tagsCache={tagsCache}
                  onNewSaleClick={handleNewSaleClick}
                  onEditClick={handleEditSale}
                  onDeleteClick={handleDeleteSale}
                  onOpenChat={handleOpenChat}
                  onTagsUpdated={debouncedLoadLeadTags}
                />
                {renderLoadMore('sales', visibleSales.length, getTabCount('sales'))}
              </>
            ) : (
              <>
                {visibleSales.map((sale) => (
                  <SaleCard
                    key={sale.id}
                    sale={sale}
                    saleTags={tagsCache[sale.lead_id]} // Passando as tags da venda
                    onNewSaleClick={handleNewSaleClick}
                    onEditClick={handleEditSale}
                    onDeleteClick={handleDeleteSale}
                    onOpenChat={handleOpenChat}
                    onTagsUpdated={debouncedLoadLeadTags}
                  />
                ))}
                {renderLoadMore('sales', visibleSales.length, getTabCount('sales'))}
              </>
            )}
          </div>
        )}
      </main>

      {/* Modal Comparecimento */}
      <Modal
        isOpen={showAttendanceModal}
        onClose={() => {
          setShowAttendanceModal(false);
          setSelectedAppointment(null);
          setFormComparecimento({ tratamento: '', orcamento: '' });
          setComparecimentoDate('');
          setComparecimentoTime('');
        }}
        title="Registrar Comparecimento"
      >
        <div className="space-y-4">
          {renderNoPhoneNotice(
            !selectedAppointment?.phone?.trim(),
            'O comparecimento será salvo no CRM, mas o pós-consulta automático por WhatsApp não será enviado porque o lead não possui telefone.'
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data do Comparecimento
            </label>
            <input
              type="date"
              className="w-full p-2 border border-gray-300 rounded-xl focus:ring-sminy-primary focus:border-sminy-primary"
              value={comparecimentoDate}
              onChange={(e) => setComparecimentoDate(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Hora do Comparecimento
            </label>
            <input
              type="time"
              className="w-full p-2 border border-gray-300 rounded-xl focus:ring-sminy-primary focus:border-sminy-primary"
              value={comparecimentoTime}
              onChange={(e) => setComparecimentoTime(e.target.value)}
            />
          </div>
          <TreatmentSelectField
            label="Tratamento"
            value={formComparecimento.tratamento}
            onChange={(value) =>
              setFormComparecimento((prev) => ({
                ...prev,
                tratamento: value
              }))
            }
            treatmentOptions={crmTreatmentOptions}
            isLoadingTreatmentOptions={isLoadingCrmTreatmentOptions}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Orçamento</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <DollarSign className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="w-full pl-10 p-2 border border-gray-300 rounded-xl focus:ring-sminy-primary focus:border-sminy-primary"
                placeholder="Valor do orçamento"
                value={formComparecimento.orcamento}
                onChange={(e) =>
                  setFormComparecimento((prev) => ({
                    ...prev,
                    orcamento: maskCurrencyBRL(e.target.value)
                  }))
                }
              />
            </div>
          </div>
          <div className="pt-2">
            <Button
              variant="primary"
              className="w-full"
              onClick={handleSalvarComparecimento}
            >
              Salvar Comparecimento
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Reagendar */}
      <Modal
        isOpen={showRescheduleModal}
        onClose={() => {
          setShowRescheduleModal(false);
          setSelectedAppointment(null);
          setRescheduleDate('');
          setRescheduleTime('');
          setIsProcessingReschedule(false); // Reset flag ao fechar modal
          setRescheduleError(null);
        }}
        title="Reagendar Consulta"
      >
        <div className="space-y-4">
          {renderNoPhoneNotice(
            !selectedAppointment?.phone?.trim(),
            'O reagendamento será salvo no CRM, mas não haverá confirmação automática por WhatsApp nem sincronização com Clinicorp sem telefone.'
          )}
          {rescheduleError && (
            <div
              role="alert"
              className={clsx(
                "rounded-xl border px-3 py-2 text-sm leading-relaxed",
                isDark ? "border-red-900/60 bg-red-950/40 text-red-200" : "border-red-200 bg-red-50 text-red-700"
              )}
            >
              {rescheduleError}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nova Data</label>
            <input
              type="date"
              className="w-full p-2 border border-gray-300 rounded-xl focus:ring-sminy-primary focus:border-sminy-primary"
              value={rescheduleDate}
              onChange={(e) => {
                setRescheduleError(null);
                setRescheduleDate(e.target.value);
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nova Hora</label>
            <input
              type="time"
              className="w-full p-2 border border-gray-300 rounded-xl focus:ring-sminy-primary focus:border-sminy-primary"
              value={rescheduleTime}
              onChange={(e) => {
                setRescheduleError(null);
                setRescheduleTime(e.target.value);
              }}
            />
          </div>
          <div className="pt-2">
            <Button
              variant="primary"
              className="w-full"
              onClick={handleConfirmReschedule}
              disabled={isProcessingReschedule}
            >
              {isProcessingReschedule ? 'Processando...' : 'Confirmar Reagendamento'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Registrar Venda */}
      <Modal
        isOpen={showSaleModal && !saleFailure && !isPhoneCorrectionOpen}
        onClose={() => {
          if (isSavingSale) return;
          setShowSaleModal(false);
          setSelectedAttendance(null);
          setFormVenda({ tratamento_fechado: '', valor_faturado: '', valor_pago: '' });
          setVendaDate('');
          setVendaTime('');
          setSaleFailure(null);
        }}
        title="Registrar Venda"
      >
        <div className="space-y-4">
          {phoneCorrectionFeedback && <p role="status" className={clsx('rounded-xl border p-3 text-sm', isDark ? 'border-sminy-dark-border bg-sminy-dark-surface text-sminy-dark-text-secondary' : 'border-sminy-border bg-sminy-blue-soft text-sminy-text-secondary')}>{phoneCorrectionFeedback}</p>}
          {renderNoPhoneNotice(
            !selectedAttendance?.phone?.trim(),
            'A venda será salva no CRM, mas o pós-venda automático por WhatsApp e a conversão automática para paciente não serão executados sem telefone.'
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data da Venda</label>
            <input
              type="date"
              className="w-full p-2 border border-gray-300 rounded-xl focus:ring-sminy-primary focus:border-sminy-primary"
              value={vendaDate}
              onChange={(e) => setVendaDate(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Hora da Venda</label>
            <input
              type="time"
              className="w-full p-2 border border-gray-300 rounded-xl focus:ring-sminy-primary focus:border-sminy-primary"
              value={vendaTime}
              onChange={(e) => setVendaTime(e.target.value)}
            />
          </div>
          <TreatmentSelectField
            label="Tratamento Fechado"
            value={formVenda.tratamento_fechado}
            onChange={(value) =>
              setFormVenda((prev) => ({
                ...prev,
                tratamento_fechado: value
              }))
            }
            treatmentOptions={crmTreatmentOptions}
            isLoadingTreatmentOptions={isLoadingCrmTreatmentOptions}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Valor Faturado</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <DollarSign className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="w-full pl-10 p-2 border border-gray-300 rounded-xl focus:ring-sminy-primary focus:border-sminy-primary"
                placeholder="Valor faturado"
                value={formVenda.valor_faturado}
                onChange={(e) =>
                  setFormVenda((prev) => ({
                    ...prev,
                    valor_faturado: maskCurrencyBRL(e.target.value)
                  }))
                }
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Valor Pago</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <DollarSign className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="w-full pl-10 p-2 border border-gray-300 rounded-xl focus:ring-sminy-primary focus:border-sminy-primary"
                placeholder="Valor pago"
                value={formVenda.valor_pago}
                onChange={(e) =>
                  setFormVenda((prev) => ({
                    ...prev,
                    valor_pago: maskCurrencyBRL(e.target.value)
                  }))
                }
              />
            </div>
          </div>
          <div className="pt-2">
            <Button
              variant="success"
              className="w-full"
              onClick={handleSalvarVenda}
              id="crm-sale-submit"
              disabled={isSavingSale}
            >
              {isSavingSale ? 'Registrando...' : 'Registrar Venda'}
            </Button>
          </div>
        </div>
      </Modal>

      {showSaleModal && saleFailure && !isPhoneCorrectionOpen && <CrmSaleErrorDialog failure={saleFailure} leadId={selectedAttendance?.lead_id} name={selectedAttendance?.nome} onReturn={() => setSaleFailure(null)} />}

      {/* Modal CRIAR/EDITAR LEAD */}
      <Modal
        isOpen={showLeadModal}
        onClose={() => {
          if (isSavingLead) return;
          setShowLeadModal(false);
          setLeadToEdit(null);
          setLeadSaveError(null);
          setLeadForm({ name: '', phone: '', source_id: 'Facebook', data_entrada: '' });
        }}
        title={leadToEdit ? 'Editar Lead' : 'Novo Lead'}
      >
        <div className="space-y-4">
          <div>
            <label className={labelClassName}>Nome</label>
            <input
              type="text"
              className={fieldClassName}
              value={leadForm.name}
              onChange={(e) =>
                setLeadForm((prev) => ({ ...prev, name: e.target.value }))
              }
            />
          </div>

          {/* Mostra o telefone somente para novos leads */}
          {!leadToEdit && (
            <div>
              <label className={labelClassName}>Telefone</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Phone className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel-national"
                  className={fieldWithIconClassName}
                  placeholder="(XX) 9XXXX-XXXX"
                  value={leadForm.phone}
                  onChange={(e) => handleLeadPhoneChange(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Mostra a mídia somente para novos leads */}
          {!leadToEdit && (
            <div>
              <label className={labelClassName}>Mídia</label>
              <select
                className={fieldClassName}
                value={leadForm.source_id}
                onChange={(e) =>
                  setLeadForm((prev) => ({ ...prev, source_id: e.target.value }))
                }
              >
                {CRM_SOURCE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Se for edição, mostra os campos de telefone e mídia como somente leitura */}
          {leadToEdit && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Phone className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    className="w-full pl-10 p-2 bg-gray-100 border border-gray-300 rounded-xl text-gray-600"
                    value={leadForm.phone}
                    disabled
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Use Editar telefone no menu do cadastro para corrigir o número preservando o histórico.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mídia</label>
                {isProtectedSource(leadForm.source_id) ? (
                  // Fonte protegida - BLOQUEADO
                  <>
                    <input
                      type="text"
                      className="w-full p-2 bg-gray-100 border border-gray-300 rounded-xl text-gray-600"
                      value={leadForm.source_id}
                      disabled
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      🔒 {isMetaAdsId(leadForm.source_id)
                        ? 'ID de anúncio do Meta Ads - não pode ser editado'
                        : 'Fonte protegida - não pode ser editada'}
                    </p>
                  </>
                ) : (
                  // Mídia de texto - EDITÁVEL
                  <>
                    <select
                      className="w-full p-2 border border-gray-300 rounded-xl focus:ring-sminy-primary focus:border-sminy-primary"
                      value={leadForm.source_id}
                      onChange={(e) =>
                        setLeadForm((prev) => ({ ...prev, source_id: e.target.value }))
                      }
                    >
                      {CRM_SOURCE_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                      {leadForm.source_id && !CRM_SOURCE_OPTIONS.includes(leadForm.source_id) && (
                        <option value={leadForm.source_id}>{leadForm.source_id}</option>
                      )}
                    </select>
                  </>
                )}
              </div>
            </>
          )}

          <div>
            <label className={labelClassName}>Data de Entrada</label>
            <input
              type="datetime-local"
              className={fieldClassName}
              value={leadForm.data_entrada}
              onChange={(e) =>
                setLeadForm((prev) => ({ ...prev, data_entrada: e.target.value }))
              }
            />
          </div>
          {leadSaveError && (
            <WhatsAppIdentityErrorAlert
              error={leadSaveError}
              onOpenExisting={handleOpenExistingLeadIdentity}
            />
          )}
          <div className="pt-2">
            <Button
              variant="primary"
              className="w-full"
              onClick={handleSaveLead}
              disabled={isSavingLead}
            >
              {isSavingLead && !leadToEdit
                ? 'Verificando WhatsApp…'
                : isSavingLead
                  ? 'Salvando…'
                  : 'Salvar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal CRIAR AGENDAMENTO */}
      <Modal
        isOpen={showAppointmentModal}
        onClose={() => {
          if (isCreatingAppointment) return;
          setShowAppointmentModal(false);
          setLeadForAppointment(null);
          setAppointmentCreationError(null);
          setNewAppointmentForm({
            date: '',
            time: '',
            midia: 'Facebook',
            interesse: ''
          });
        }}
        title="Criar Agendamento"
      >
        <div className="space-y-4">
          {renderNoPhoneNotice(
            !leadForAppointment?.phone?.trim(),
            'O agendamento será salvo no CRM, mas confirmações e follow-ups por WhatsApp não serão enviados. A sincronização com Clinicorp também não será feita sem telefone.'
          )}
          {appointmentCreationError && (
            <div
              role="alert"
              className={clsx(
                "rounded-xl border px-3 py-2 text-sm leading-relaxed",
                isDark ? "border-red-900/60 bg-red-950/40 text-red-200" : "border-red-200 bg-red-50 text-red-700"
              )}
            >
              {appointmentCreationError}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data</label>
            <input
              type="date"
              className="w-full p-2 border border-gray-300 rounded-xl focus:ring-sminy-primary focus:border-sminy-primary"
              value={newAppointmentForm.date}
              onChange={(e) => {
                setAppointmentCreationError(null);
                setNewAppointmentForm((prev) => ({ ...prev, date: e.target.value }));
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Hora</label>
            <input
              type="time"
              className="w-full p-2 border border-gray-300 rounded-xl focus:ring-sminy-primary focus:border-sminy-primary"
              value={newAppointmentForm.time}
              onChange={(e) => {
                setAppointmentCreationError(null);
                setNewAppointmentForm((prev) => ({ ...prev, time: e.target.value }));
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mídia</label>
            <select
              className="w-full p-2 border border-gray-300 rounded-xl focus:ring-sminy-primary focus:border-sminy-primary"
              value={newAppointmentForm.midia}
              onChange={(e) => {
                setAppointmentCreationError(null);
                setNewAppointmentForm((prev) => ({ ...prev, midia: e.target.value }));
              }}
            >
              <option value="Facebook">Facebook</option>
              <option value="Instagram">Instagram</option>
              <option value="Google">Google</option>
              <option value="Indicação">Indicação</option>
              <option value="Fachada">Fachada</option>
              <option value="Site">Site</option>
              <option value="Orgânico">Orgânico</option>
              <option value="Convênio">Convênio</option>
              <option value="Carteira de Pacientes">Carteira de Pacientes</option>
              <option value="Radio">Radio</option>
              <option value="TV">TV</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Interesse</label>
            <select
              className="w-full p-2 border border-gray-300 rounded-xl focus:ring-sminy-primary focus:border-sminy-primary"
              value={newAppointmentForm.interesse}
              disabled={isLoadingCrmTreatmentOptions || crmTreatmentOptions.length === 0}
              onChange={(e) => {
                setAppointmentCreationError(null);
                setNewAppointmentForm((prev) => ({
                  ...prev,
                  interesse: e.target.value
                }));
              }}
            >
              <option value="">
                {isLoadingCrmTreatmentOptions
                  ? 'Carregando tratamentos...'
                  : 'Selecione um tratamento'}
              </option>
              {crmTreatmentOptions.map((treatment) => (
                <option key={treatment} value={treatment}>
                  {treatment}
                </option>
              ))}
            </select>
            {!isLoadingCrmTreatmentOptions && crmTreatmentOptions.length === 0 && (
              <p className="mt-1 text-xs text-amber-600">
                Nenhum tratamento cadastrado na lista da clínica.
              </p>
            )}
          </div>
          <div className="pt-2">
            <Button
              variant="primary"
              className="w-full"
              onClick={handleCreateAppointment}
              disabled={
                isLoadingCrmTreatmentOptions ||
                crmTreatmentOptions.length === 0 ||
                !newAppointmentForm.interesse ||
                isCreatingAppointment
              }
            >
              {isCreatingAppointment ? 'Sincronizando…' : 'Salvar Agendamento'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal EDITAR AGENDAMENTO */}
      <Modal
        isOpen={showAppointmentEditModal}
        onClose={() => {
          setShowAppointmentEditModal(false);
          setAppointmentToEdit(null);
          setAppointmentOperationError(null);
        }}
        title="Editar Agendamento"
      >
        {appointmentToEdit && (
          <AppointmentEditForm
            appointment={appointmentToEdit}
            treatmentOptions={crmTreatmentOptions}
            isLoadingTreatmentOptions={isLoadingCrmTreatmentOptions}
            error={appointmentOperationError}
            onClearError={() => setAppointmentOperationError(null)}
            onClose={() => {
              setShowAppointmentEditModal(false);
              setAppointmentToEdit(null);
              setAppointmentOperationError(null);
            }}
            onSave={handleSaveEditAppointment}
          />
        )}
      </Modal>

      {/* Modal MARCAR FALTA */}
      <Modal
        isOpen={showNoShowModal}
        onClose={() => {
          setShowNoShowModal(false);
          setAppointmentNoShow(null);
          setNoShowObs('');
        }}
        title="Registrar Falta"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Observação</label>
            <textarea
              className="w-full p-2 border border-gray-300 rounded-xl focus:ring-sminy-primary focus:border-sminy-primary h-32"
              value={noShowObs}
              onChange={(e) => setNoShowObs(e.target.value)}
              placeholder="Descreva o motivo da falta ou informações adicionais..."
            />
          </div>
          <div className="pt-2">
            <Button variant="danger" className="w-full" onClick={handleConfirmNoShow}>
              Confirmar Falta
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal EDITAR NO-SHOW */}
      <Modal
        isOpen={showNoShowEditModal}
        onClose={() => {
          setShowNoShowEditModal(false);
          setNoShowToEdit(null);
        }}
        title="Editar Falta"
      >
        {noShowToEdit && (
          <NoShowEditForm
            noShow={noShowToEdit}
            onClose={() => {
              setShowNoShowEditModal(false);
              setNoShowToEdit(null);
            }}
            onSave={async (id, data) => {
              try {
                const clientId = parseInt(localStorage.getItem('client_id') || '0', 10);
                const clinicId = parseInt(localStorage.getItem('clinic_id') || '0', 10);
                const updated = await atualizarNoShowEvent(
                  clientId,
                  clinicId,
                  id,
                  data
                );
                // atualiza local
                setNoShows((prev) =>
                  prev.map((n) => (n.id === updated.id ? { ...n, ...updated } : n))
                );
                void refreshCrmData();
                setShowNoShowEditModal(false);
                setNoShowToEdit(null);
              } catch (error) {
                console.error('Erro ao atualizar no-show:', error);
                alert('Erro ao atualizar falta.');
              }
            }}
          />
        )}
      </Modal>

      {/* Modal EDITAR COMPARECIMENTO */}
      <Modal
        isOpen={showAttendanceEditModal}
        onClose={() => {
          setShowAttendanceEditModal(false);
          setAttendanceToEdit(null);
        }}
        title="Editar Comparecimento"
      >
        {attendanceToEdit && (
          <ComparecimentoEditForm
            attendance={attendanceToEdit}
            treatmentOptions={crmTreatmentOptions}
            isLoadingTreatmentOptions={isLoadingCrmTreatmentOptions}
            onClose={() => {
              setShowAttendanceEditModal(false);
              setAttendanceToEdit(null);
            }}
            onSave={async (id, data) => {
              try {
                const clientId = parseInt(localStorage.getItem('client_id') || '0', 10);
                const clinicId = parseInt(localStorage.getItem('clinic_id') || '0', 10);
                const updated = await atualizarComparecimento(
                  clientId,
                  clinicId,
                  id,
                  data
                );
                setAttendances((prev) =>
                  prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c))
                );
                void refreshCrmData();
                setShowAttendanceEditModal(false);
                setAttendanceToEdit(null);
              } catch (error) {
                console.error('Erro ao editar comparecimento:', error);
                alert('Erro ao editar comparecimento.');
              }
            }}
          />
        )}
      </Modal>

      {/* Modal EDITAR VENDA */}
      <Modal
        isOpen={showSaleEditModal}
        onClose={() => {
          setShowSaleEditModal(false);
          setSaleToEdit(null);
        }}
        title="Editar Venda"
      >
        {saleToEdit && (
          <VendaEditForm
            sale={saleToEdit}
            treatmentOptions={crmTreatmentOptions}
            isLoadingTreatmentOptions={isLoadingCrmTreatmentOptions}
            onClose={() => {
              setShowSaleEditModal(false);
              setSaleToEdit(null);
            }}
            onSave={async (id, data) => {
              try {
                const clientId = parseInt(localStorage.getItem('client_id') || '0', 10);
                const clinicId = parseInt(localStorage.getItem('clinic_id') || '0', 10);
                const updated = await atualizarVenda(
                  clientId,
                  clinicId,
                  id,
                  data
                );
                setSales((prev) =>
                  prev.map((s) => (s.id === updated.id ? { ...s, ...updated } : s))
                );
                void refreshCrmData();
                setShowSaleEditModal(false);
                setSaleToEdit(null);
              } catch (error) {
                console.error('Erro ao editar venda:', error);
                alert('Erro ao editar venda.');
              }
            }}
          />
        )}
      </Modal>
    </div>
    </CrmLossProvider>
    </CrmPhoneCorrectionProvider>
  );
};

export default CRMInterfacev4;
